import React from 'react';
import {
  buildLayoutMemberNumberMap,
  getClassMemberDisplayNumbers,
  layoutHasFixedMemberNumbers,
} from '../utils/classMemberNumbers';
import { createPortal } from 'react-dom';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent, type DragOverEvent, type DragStartEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRemoteReceiver } from '../hooks/useRemoteReceiver';
import RemoteBacklogNavButton from '../components/topbar/RemoteBacklogNavButton';
import {
  readLastPick,
  remotePickToSlidePick,
  sameBgmPick,
  writeLastPick,
  type RemoteBgmController,
} from '../hooks/useMainPresentationBgm';
import { useProgramSidebarOptional } from '../contexts/ProgramSidebarContext';
import { TutorialProvider } from '../contexts/TutorialContext';
import PaidPassRequiredModal from '../components/common/PaidPassRequiredModal';
import DeleteHoldButton from '../components/common/DeleteHoldButton';
import { PaidPassGateProvider, usePaidPassGate } from '../contexts/PaidPassGateContext';
import PresentationTutorialBootstrap from '../components/tutorial/PresentationTutorialBootstrap';
import TutorialOverlayHost from '../components/tutorial/TutorialOverlayHost';
import ClassLessonAlarmPopupRelay from '../components/classAlarm/ClassLessonAlarmPopupRelay';
import ProgramSlideCanvas, { cloneSlideLayout, type ProgramSlideCanvasHandle } from '../components/program/ProgramSlideCanvas';
import ProgramSlideBgmModal from '../components/program/ProgramSlideBgmModal';
import type { ProgramSlideBgmPick } from '../components/program/ProgramSlideBgmModal';
import ProgramSlideDiceModal from '../components/program/ProgramSlideDiceModal';
import {
  ProgramSlideWebcamDrawRoot,
  ProgramSlideWebcamDrawCanvas,
  ProgramSlideWebcamDrawToolbar,
  type WebcamDrawStroke,
} from '../components/program/ProgramSlideWebcamDrawOverlay';
import {
  canvasToPngBlob,
  defaultWebcamDrawExportFilename,
  downloadPngBlob,
  renderWebcamDrawCompositeAsync,
} from '../utils/programSlideWebcamDrawExport';
import { useWebcamViewTransform } from '../hooks/useWebcamViewTransform';
import { duplicateSlideLayoutWithFreshIds } from '../utils/programSlidePageDuplicate';
import { useSlideLiveCaptionRecognition } from '../hooks/useSlideLiveCaptionRecognition';
import ProgramSlideTimerModal from '../components/program/ProgramSlideTimerModal';
import ProgramSlideTitlePanel from '../components/program/ProgramSlideTitlePanel';
import ProgramSlideCommunicationPanel from '../components/program/ProgramSlideCommunicationPanel';
import ProgramContentSlidePublicQrOverlay from '../components/program/ProgramContentSlidePublicQrOverlay';
import AutoStatusToast from '../components/common/AutoStatusToast';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import { createPresentationPublicSession } from '../services/presentationPublicShareService';
import {
  getPresentationCommunication,
  listPresentationCommunicationQuestions,
  listPresentationCommunicationResponses,
  subscribePresentationCommunicationResponses,
  upsertPresentationCommunication,
  upsertPresentationCommunicationQuestion,
  savePresentationCommunicationResponsesBatch,
  setPresentationCommunicationActiveQuestion,
  deletePresentationCommunicationQuestion,
  playPresentationCommunicationQuestion,
  stopPresentationCommunication,
  type PresentationCommunicationQuestionRow,
  type PresentationCommunicationResponse,
} from '../services/presentationCommunicationService';
import {
  publishPresentationCommunicationMonitorSnapshot,
  requestOpenPresentationCommunicationMonitor,
  subscribePresentationCommunicationMonitor,
  type PresentationCommunicationMonitorSnapshot,
} from '../utils/presentationCommunicationMonitorBridge';
import { getClassMemberGradeLayoutsMap } from '../services/classMembersService';
import {
  CLASS_MEMBERS_CHANGED_EVENT,
  classMembersStorageKey,
  flattenGradeLayout,
  isValidEntryNumber,
  loadClassMemberGradeLayoutFromStorage,
} from '../utils/classMembersStorage';
import type { ClassMemberGroupMeta, ClassMemberRow } from '../utils/classMembersStorage';
import { type CommunicationQuestion } from '../utils/presentationCommunicationAnswers';
import {
  buildCommunicationExportSection,
  downloadCommunicationResponsesExcel,
  savePresentationCommunicationResponsesAsPdf,
  type CommunicationExportDocumentMeta,
  type CommunicationExportSection,
} from '../utils/presentationCommunicationExport';
import { normalizeWeeklyGuideColorHex } from '../constants/weeklyLearningGuide';
import {
  downloadProgramSlideOfflineHtml,
  readLiveSlideCanvasAspect,
} from '../utils/programSlideOfflineHtmlExport';
import {
  discardPendingProgramContentSlideDraftForOwner,
  buildSlideRowsUpdatedAtSignature,
  fetchProgramContentSlidePages,
  fetchProgramContentSlideRemoteSignature,
  getProgramContentSlideCachedSignature,
  findPendingProgramContentSlideDraftForOpen,
  flushPendingProgramContentSlideDraftForOwner,
  layoutFromDbRow,
  materializePendingProgramContentSlideClone,
  persistProgramContentSlideLayoutsForOwner,
  programContentSlidePresenceKey,
  deleteProgramContentSlidePagesForOwner,
  type ProgramSlideLearningTextPatch,
  learningTextStorageWithoutPresentationRef,
  learningTextWithPresentationRef,
} from '../services/programContentSlideService';
import { upsertProgramFixedContentRows } from '../services/programFixedContentService';
import { upsertProgramProgressCells } from '../services/programProgressService';
import { ensureScopedRowWithLearningAtPosition } from '../utils/scopedRecordSave';
import {
  clearPresentationRefBindingAfterEmptySlideSave,
  findExistingPresentationRefOnSourceOwner,
  persistPresentationRefIdToSourceOwner,
  PRESENTATION_REF_CONFLICT_MESSAGE,
} from '../services/programEmptyPresentationBindingService';
import type { ProgramFixedContentRowUpsert, ProgramProgressCellUpsert } from '../types/domain';
import {
  EMPTY_SLIDE_LAYOUT,
  parseSlideLayoutJson,
  slideLayoutHasMeaningfulCanvasContent,
  type ProgramContentSlideOwnerRef,
  type ProgramSlideLayoutV1,
} from '../types/programContentSlideCanvas';
import { lightenRgbHexTowardWhite } from '../utils/hexColorLighten';
import {
  notifyProgramContentSlideLearningUpdated,
  PROGRAM_CONTENT_SLIDE_OWNER_REMAPPED_EVENT,
  readProgramContentSlidePayload,
  updateProgramContentSlidePayloadOwner,
  type ProgramContentSlidePayloadV1,
  isProgramContentSlideOpenedFromMainInSameWindow,
} from '../utils/programContentSlideStorage';
import { parseMemoFieldStorageDeep } from '../utils/programCellDisplayText';
import { confirmAndSaveDirtyProgramWorkspaceBeforeAttachment } from '../utils/programAttachmentCoSaveWorkspace';
import { resolvePresentationRefIdFromLearningText } from '../utils/programContentSlideOwner';
import {
  filterVideoInputDevices,
  getSlideScreenShareMedia,
  getSlideWebcamUserMedia,
  getSlideWebcamUserMediaSmartDefault,
  simplifyVideoInputDeviceLabel,
} from '../utils/programSlideWebcamQuality';
import { notifySlideImageStorageChanged } from '../services/slideImageStorageService';
import { getLayoutViewportSize, PAGE_ZOOM_CHANGED_EVENT } from '../utils/pageZoom';
import {
  collectSlideImageStorageUrlsFromLayouts,
  finalizePendingSlideImagesInLayout,
  releaseRemovedSlideImageUrls,
  revokeAllPendingInLayouts,
} from '../utils/slideImagePendingUpload';
import {
  acquireProgramSlideEditScopeLock,
  releaseProgramSlideEditScopeLock,
  touchProgramSlideEditScopeLockHeartbeat,
  touchProgramSlidePresentationOverlayHeartbeat,
} from '../utils/programSlideEditScopeLock';
import {
  captureWebcamFreezeBaselineFromLayouts,
  mergeWebcamSessionLayouts,
  pruneLayoutsToWebcamFreezeBaseline,
  pruneLayoutToKnownEntityIds,
  pruneLayoutToUnknownEntityIds,
  type WebcamFreezeBaselineByPage,
} from '../utils/programSlideWebcamFreezeEphemeral';
import {
  pauseSlideTimerChimeForTimerPause,
  playTimerOneMinuteChime,
  primeTimerOneMinSoundCache,
  resumeSlideTimerChimeAfterTimerPause,
  stopSlideTimerChimeAudioHard,
  unlockSlideTimerOneMinuteChimeAudio,
} from '../utils/timerOneMinuteSoundCache';
import { resolveSlideBgmAudioSrc, slideBgmTracksForMood, startSlideBgmPlaybackAsync, type SlideBgmPlaybackHandle } from '../utils/slideBgmPlayback';
import {
  getProgramSlideRegisteredPages,
  replaceProgramSlideRegisteredPages,
} from '../services/programSlideRegisteredPagesService';
import {
  getProgramSlideRegisteredLayouts,
  replaceProgramSlideRegisteredLayouts,
} from '../services/programSlideRegisteredLayoutsService';
import {
  loadProgramSlideRegisteredPages,
  PROGRAM_SLIDE_REGISTERED_PAGES_CHANGED_EVENT,
  type ProgramSlideRegisteredPage,
} from '../utils/programSlideRegisteredPages';
import {
  loadProgramSlideRegisteredLayouts,
  PROGRAM_SLIDE_REGISTERED_LAYOUTS_CHANGED_EVENT,
  type ProgramSlideRegisteredLayout,
} from '../utils/programSlideRegisteredLayouts';
import {
  limitProgramSlideRegistryNameDraft,
  normalizeProgramSlideRegistryName,
  PROGRAM_SLIDE_REGISTRY_NAME_MAX_LENGTH,
  programSlideRegistryFallbackName,
  programSlideRegistryMaxCount,
} from '../utils/programSlideRegistryLimits';
import {
  footerSlideTabKey,
  isSlideFooterDotsScrollable,
  scrollFooterSlideTabIntoView,
  slideFooterDotsScrollStyle,
} from '../utils/slideFooterDotsNav';
import { useLessonCommRoom, type LessonCommQuestionSnapshot, type LessonCommRosterEntry } from '../hooks/useLessonCommRoom';
import {
  buildCommResponseRows,
  commRowsToResponses,
  communicationToLessonCommQuestion,
  loadPendingCommBatches,
  queuePendingCommBatch,
  removePendingCommBatch,
  type PendingCommBatch,
} from '../utils/lessonCommResponses';
import { isPersistableUuid as isPersistableCommQuestionId } from '../utils/programContentSlideOwner';
import LessonCommResponsesPanel from '../components/program/LessonCommResponsesPanel';

/** 슬라이드 편제 블록: 주간학습 안내 색보다 흰색 방향으로 이 비율만큼 더 밝게 */
const SLIDE_TRACK_BG_LIGHTEN = 0.8;

/** 개발 모드 전용 로그 — 리모컨 질문 → 질문 페이지 저장/재생/학생 기기 연결 단계 */
function remoteQLog(...args: unknown[]) {
  if (process.env.NODE_ENV !== 'production') console.info('[리모트질문]', ...args);
}

const EMPTY_KNOWN_IDS_FOR_WEBCAM_SPLIT = new Set<string>();

/** 세션 전용 판서 키. DB/저장에 넣지 않음 */
type PresentationDrawPageKey = 'title' | `page-${number}` | 'communication';

function contentDrawPageKey(pageIndex: number): PresentationDrawPageKey {
  return `page-${pageIndex}`;
}

function resolvePresentationDrawPageKey(params: {
  isRegistryMode: boolean;
  isCommunicationView: boolean;
  selectedEditablePageIndex: number | null;
}): PresentationDrawPageKey | null {
  if (params.isRegistryMode) {
    return contentDrawPageKey(params.selectedEditablePageIndex ?? 0);
  }
  if (params.isCommunicationView) return 'communication';
  if (params.selectedEditablePageIndex == null) return 'title';
  return contentDrawPageKey(params.selectedEditablePageIndex);
}

function remapDrawStrokesByPage<T>(
  prev: Record<string, T>,
  removedPageIndex: number
): Record<string, T> {
  const next: Record<string, T> = {};
  for (const [key, value] of Object.entries(prev)) {
    if (key === 'title' || key === 'communication') {
      next[key] = value;
      continue;
    }
    if (!key.startsWith('page-')) continue;
    const idx = Number(key.slice('page-'.length));
    if (!Number.isFinite(idx) || idx === removedPageIndex) continue;
    const mapped = idx > removedPageIndex ? idx - 1 : idx;
    next[contentDrawPageKey(mapped)] = value;
  }
  return next;
}

function slideSortableId(pageIndex: number): string {
  return `editable-slide:${pageIndex}`;
}

function parseSlideSortableId(raw: string): number | null {
  if (!raw.startsWith('editable-slide:')) return null;
  const value = Number(raw.slice('editable-slide:'.length));
  return Number.isFinite(value) && value >= 0 ? value : null;
}

const SortableSlideTab: React.FC<{
  id: string;
  tabKey: string;
  isActive: boolean;
  isDraggingState: boolean;
  label: string;
  onClick: () => void;
  onDoubleClick?: () => void;
}> = ({ id, tabKey, isActive, isDraggingState, label, onClick, onDoubleClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const { role: _sortableRole, tabIndex: sortableTabIndex, ...sortableAttributes } = attributes;
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
  };
  return (
    <button
      ref={setNodeRef}
      style={style}
      type="button"
      role="tab"
      aria-selected={isActive}
      tabIndex={sortableTabIndex}
      className={
        'cp-program-slide-dot' + (isActive ? ' is-active' : '') + (isDragging || isDraggingState ? ' is-dragging' : '')
      }
      onClick={onClick}
      onDoubleClick={() => onDoubleClick?.()}
      title="드래그해서 슬라이드 순서를 변경"
      data-footer-slide-tab={tabKey}
      data-tutorial-slide="tab-page"
      {...sortableAttributes}
      {...listeners}
    >
      <span className="cp-program-slide-dot-label">{label}</span>
    </button>
  );
};

/** 저장값이 비어 있어도 기본 2페이지(빈 추가 슬라이드 1개) */
function normalizeExtraMemoBodies(raw: string[] | undefined): string[] {
  const a = Array.isArray(raw) ? raw.map((s) => String(s ?? '')) : [];
  if (a.length === 0) return [''];
  return a;
}

function buildDefaultEmptySlideLayout(): ProgramSlideLayoutV1 {
  return {
    ...EMPTY_SLIDE_LAYOUT,
    defaultTextColorHex: SLIDE_DEFAULT_TEXT_COLOR_HEX,
    defaultBackgroundColorHex: SLIDE_DEFAULT_BG_COLOR_HEX,
  };
}

/** 학습계획 AI가 넘긴 슬라이드. 저장 전 탭 안에서만 쓴다. */
function aiPreviewLayoutsFromPayload(raw: ProgramSlideLayoutV1[] | undefined): ProgramSlideLayoutV1[] {
  if (!Array.isArray(raw)) return [];
  const out: ProgramSlideLayoutV1[] = [];
  for (const item of raw) {
    const layout = parseSlideLayoutJson(item);
    if (!slideLayoutHasMeaningfulCanvasContent(layout)) continue;
    out.push(layout);
    if (out.length >= 12) break;
  }
  return out;
}

/** DB 저장 없음 포함 — 콘텐츠 1페이지(인덱스 0)는 항상 빈 캔버스로 존재 */
function seedMinimumPresentationContentPages(
  layoutMap: Record<number, ProgramSlideLayoutV1>,
  contentPageCount: number
): { layouts: Record<number, ProgramSlideLayoutV1>; pageCount: number } {
  const pageCount = Math.max(1, contentPageCount);
  const shell = buildDefaultEmptySlideLayout();
  const layouts: Record<number, ProgramSlideLayoutV1> = { ...layoutMap };
  for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
    if (layouts[pageIndex] == null) {
      layouts[pageIndex] = { ...shell };
    }
  }
  return { layouts, pageCount };
}

function defaultPresentationContentPageOrder(pageCount: number): number[] {
  const count = Math.max(1, pageCount);
  return Array.from({ length: count }, (_, pageIndex) => pageIndex);
}

const SLIDE_DEFAULT_TEXT_COLOR_HEX = '#141414';
const SLIDE_DEFAULT_BG_COLOR_HEX = '#ffffff';
/** 본문 캔버스 최소 폭(App.css @media max-width:1280px 과 동일) */
const SLIDE_BODY_CANVAS_MIN_WIDTH = 1280;

function normalizeColorInputHex(raw: string | undefined, fallback: string): string {
  return raw?.trim().match(/^#[0-9a-f]{6}$/i) ? raw.trim().toLowerCase() : fallback;
}

function deriveSlideGlobalDefaultsFromLayouts(
  layoutMap: Record<number, ProgramSlideLayoutV1>
): { textColorHex: string; backgroundColorHex: string } {
  const orderedLayouts = Object.keys(layoutMap)
    .map((idxRaw) => Number(idxRaw))
    .filter((idx) => Number.isFinite(idx))
    .sort((a, b) => a - b)
    .map((idx) => layoutMap[idx] ?? EMPTY_SLIDE_LAYOUT);

  const textFromLayout = orderedLayouts
    .map((layout) => layout.defaultTextColorHex)
    .find((hex) => typeof hex === 'string' && /^#[0-9a-f]{6}$/i.test(hex));
  const bgFromLayout = orderedLayouts
    .map((layout) => layout.defaultBackgroundColorHex)
    .find((hex) => typeof hex === 'string' && /^#[0-9a-f]{6}$/i.test(hex));

  return {
    textColorHex: normalizeColorInputHex(textFromLayout, SLIDE_DEFAULT_TEXT_COLOR_HEX),
    backgroundColorHex: normalizeColorInputHex(bgFromLayout, SLIDE_DEFAULT_BG_COLOR_HEX),
  };
}

function createPresentationRefId(): string {
  const id = globalThis.crypto?.randomUUID?.();
  if (id) return id;
  return '00000000-0000-4000-8000-000000000000'.replace(/[08]/g, (c) => {
    const n = Number(c);
    const r = Math.floor(Math.random() * 16);
    return (n ^ (r & (15 >> (n / 4)))).toString(16);
  });
}

function buildRegistryEditorStateFromPages(
  pages: Array<{ id: string; name: string; layout: ProgramSlideLayoutV1 }>
): {
  names: Record<number, string>;
  ids: Record<number, string>;
  layouts: Record<number, ProgramSlideLayoutV1>;
  order: number[];
  extraPageCount: number;
} {
  const names: Record<number, string> = {};
  const ids: Record<number, string> = {};
  const layouts: Record<number, ProgramSlideLayoutV1> = {};
  pages.forEach((page, idx) => {
    names[idx] = normalizeProgramSlideRegistryName(page.name, programSlideRegistryFallbackName(idx));
    ids[idx] = page.id;
    layouts[idx] = cloneSlideLayout(page.layout);
  });
  return {
    names,
    ids,
    layouts,
    order: pages.map((_, idx) => idx),
    extraPageCount: Math.max(1, pages.length),
  };
}

function splitWorkspaceScope(scopeId: string): { scopeType: 'fixed' | 'track' | 'project' | ''; scopeKey: string } {
  const parts = scopeId.split('|');
  const scopeType = parts[0] === 'fixed' || parts[0] === 'track' || parts[0] === 'project' ? parts[0] : '';
  return { scopeType, scopeKey: parts.slice(5).join('|') };
}

async function createProgramContentOwnerForPresentationRef(
  payload: ProgramContentSlidePayloadV1,
  learningText: string
): Promise<ProgramContentSlideOwnerRef> {
  const ctx = payload.createPresentationRefOnSave;
  if (!ctx) throw new Error('저장할 수 있는 프로그램 행 정보가 없습니다.');
  const sid = payload.workspaceId.trim();
  const scopeId = (payload.scopeId ?? '').trim();
  const gradeId = (ctx.gradeId ?? '').trim();
  if (!sid || !gradeId) throw new Error('저장할 수 있는 프로그램 행 정보가 없습니다.');

  const { scopeType, scopeKey } = splitWorkspaceScope(scopeId);
  const common = {
    workspace_id: sid,
    grade_id: gradeId,
    academic_year: payload.academicYear,
    term_number: payload.termNumber,
    timetable_text: ctx.timetableText ?? '',
    unit_text: ctx.unitText ?? '',
    learning_text: learningText,
    page_text: ctx.pageText ?? '',
    assistant_page_text: ctx.assistantPageText ?? '',
    current_period_text: ctx.currentPeriodText ?? '',
    total_period_text: ctx.totalPeriodText ?? '',
    material_text: ctx.materialText ?? '',
    record_cross_curricular_text: ctx.recordCrossCurricularText ?? '',
    record_memo_text: ctx.recordMemoText ?? '',
    record_evaluation_text: ctx.recordEvaluationText ?? '',
    record_class_text: ctx.recordClassText ?? '',
    bg_color_by_field: {},
  };

  if (ctx.saveMode === 'fixed_content' && scopeType === 'fixed') {
    const row: ProgramFixedContentRowUpsert = {
      ...common,
      order_index: Number.isFinite(ctx.orderIndex) ? Number(ctx.orderIndex) : 0,
      source_annual_cell_key: ctx.sourceAnnualCellKey ?? ctx.annualKey,
      event_name: null,
    };
    const saved = await upsertProgramFixedContentRows([row]);
    const id = saved[0]?.id?.trim();
    if (!id) throw new Error('프로그램 행을 만든 뒤 프레젠테이션을 저장하지 못했습니다.');
    return { kind: 'fixed_row', id };
  }

  if (ctx.saveMode === 'fixed_content' && (scopeType === 'track' || scopeType === 'project') && scopeKey.trim()) {
    /** 일정 위치(몇 번째 수업)의 행을 찾고, 없을 때만 그 자리에 하나 만든다(중복 행 방지) */
    const savedRow = await ensureScopedRowWithLearningAtPosition(
      {
        workspaceId: common.workspace_id,
        academicYear: common.academic_year,
        termNumber: common.term_number,
        gradeId: common.grade_id,
        scopeType,
        scopeKey,
      },
      Number.isFinite(ctx.orderIndex) ? Number(ctx.orderIndex) : 0,
      common
    );
    const id = savedRow.id?.trim();
    if (!id) throw new Error('프로그램 행을 만든 뒤 프레젠테이션을 저장하지 못했습니다.');
    return { kind: 'scoped_row', id };
  }

  const row: ProgramProgressCellUpsert = {
    ...common,
    monday_iso: ctx.mondayIso ?? '',
    weekday_key: (ctx.weekdayKey ?? 'mon') as ProgramProgressCellUpsert['weekday_key'],
    slot_index: Number.isFinite(ctx.slotIndex) ? Number(ctx.slotIndex) : 0,
    record_member_group_id: null,
  };
  const saved = await upsertProgramProgressCells([row]);
  const id = saved[0]?.id?.trim();
  if (!id) throw new Error('프로그램 셀을 만든 뒤 프레젠테이션을 저장하지 못했습니다.');
  return { kind: 'program_cell', id };
}

interface ProgramSlideTopBarProps {
  titleText?: string;
  editMode: boolean;
  onEditModeChange: (next: boolean) => void;
  lockEditModeToggle?: boolean;
  registeredPageButtons?: React.ReactNode;
  showQuestionQrButton?: boolean;
  questionQrLoading?: boolean;
  onOpenQuestionQr?: () => void;
  showSlideBoardButton?: boolean;
  slideBoardDrawOpen?: boolean;
  onSlideBoardButtonClick?: () => void;
  showSlideWebcamButton?: boolean;
  slideWebcamOn?: boolean;
  slideWebcamLoading?: boolean;
  /** 켜져 있지 않을 때만 미리보기 켬. 끄기는 정지 버튼만 사용 */
  onSlideWebcamCameraClick?: () => void;
  slideTimerActive?: boolean;
  onSlideTimerButtonClick?: () => void;
  onSlideBgmButtonClick?: () => void;
  slideBgmTransport?: 'idle' | 'playing' | 'paused';
  onSlideBgmPauseToggle?: () => void;
  onSlideBgmStop?: () => void;
  onSlideDiceButtonClick?: () => void;
  /** 웹캠 켜진 뒤(로딩 아님) 일시정지·입력 전환·끄기 — BGM 재생 블록과 동일하게 필 안에 표시 */
  slideWebcamTools?: React.ReactNode;
  /** Web Speech API 사용 가능 시에만 전달 */
  slideLiveCaptionSupported?: boolean;
  slideLiveCaptionOn?: boolean;
  onSlideLiveCaptionMicClick?: () => void;
  onSlideLiveCaptionStop?: () => void;
  offlineHtmlExporting?: boolean;
  onOfflineHtmlExport?: () => void;
}

async function requestProgramSlideFullscreen(): Promise<void> {
  const el = document.documentElement;
  if (el.requestFullscreen) {
    await el.requestFullscreen();
    return;
  }
  const webkitEl = el as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
  if (webkitEl.webkitRequestFullscreen) {
    await webkitEl.webkitRequestFullscreen();
  }
}

async function exitProgramSlideFullscreen(): Promise<void> {
  if (document.exitFullscreen) {
    await document.exitFullscreen();
    return;
  }
  const webkitDoc = document as Document & { webkitExitFullscreen?: () => Promise<void> | void };
  if (webkitDoc.webkitExitFullscreen) {
    await webkitDoc.webkitExitFullscreen();
  }
}

function isProgramSlideFullscreenActive(): boolean {
  const doc = document as Document & { webkitFullscreenElement?: Element | null };
  return !!(document.fullscreenElement ?? doc.webkitFullscreenElement);
}

const ProgramSlideOfflineHtmlDownloadIcon: React.FC = () => (
  <svg
    className="cp-program-slide-offline-html-btn-icon"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="m7 10 5 5 5-5" />
    <path d="M12 15V3" />
  </svg>
);

const ProgramSlideFullscreenExpandIcon: React.FC = () => (
  <svg
    className="cp-program-slide-fullscreen-btn-icon"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M8 3H3v5" />
    <path d="M16 3h5v5" />
    <path d="M8 21H3v-5" />
    <path d="M16 21h5v-5" />
  </svg>
);

const ProgramSlideFullscreenShrinkIcon: React.FC = () => (
  <svg
    className="cp-program-slide-fullscreen-btn-icon"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M4 14h6v6" />
    <path d="M20 10h-6V4" />
    <path d="M14 10l7-7" />
    <path d="M10 14l-7 7" />
  </svg>
);

/** 본문 `TopNav`와 동일 클래스(높이·색·테두리) */
function ProgramSlideTopBar({
  titleText = '프로그램 내용 슬라이드',
  editMode,
  onEditModeChange,
  lockEditModeToggle = false,
  registeredPageButtons,
  showQuestionQrButton,
  questionQrLoading,
  onOpenQuestionQr,
  showSlideBoardButton,
  slideBoardDrawOpen,
  onSlideBoardButtonClick,
  showSlideWebcamButton,
  slideWebcamOn,
  slideWebcamLoading,
  onSlideWebcamCameraClick,
  slideTimerActive,
  onSlideTimerButtonClick,
  onSlideBgmButtonClick,
  slideBgmTransport = 'idle',
  onSlideBgmPauseToggle,
  onSlideBgmStop,
  onSlideDiceButtonClick,
  slideWebcamTools,
  slideLiveCaptionSupported,
  slideLiveCaptionOn,
  onSlideLiveCaptionMicClick,
  onSlideLiveCaptionStop,
  offlineHtmlExporting,
  onOfflineHtmlExport,
}: ProgramSlideTopBarProps) {
  const [slideFullscreen, setSlideFullscreen] = React.useState(() => isProgramSlideFullscreenActive());

  React.useEffect(() => {
    const sync = () => setSlideFullscreen(isProgramSlideFullscreenActive());
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  const toggleSlideFullscreen = React.useCallback(() => {
    void (async () => {
      try {
        if (isProgramSlideFullscreenActive()) {
          await exitProgramSlideFullscreen();
        } else {
          await requestProgramSlideFullscreen();
        }
      } catch {
        /* 사용자 취소·브라우저 미지원 */
      }
    })();
  }, []);

  return (
    <header className="cp-topnav cp-program-slide-topnav cp-program-slide-app-chrome" role="banner">
      <div className="cp-topnav-left">
        <span className="cp-program-slide-topnav-title">{titleText}</span>
        <button
          type="button"
          className={'cp-theme-toggle cp-program-slide-mode-toggle' + (editMode ? ' is-edit' : ' is-view')}
          onClick={() => onEditModeChange(!editMode)}
          aria-pressed={editMode}
          title={editMode ? '보기모드로 전환' : '편집모드로 전환'}
          disabled={lockEditModeToggle}
        >
          <span className="cp-program-slide-mode-toggle-text cp-program-slide-mode-toggle-text--full">
            {editMode ? '편집모드' : '보기모드'}
          </span>
          <span className="cp-program-slide-mode-toggle-text cp-program-slide-mode-toggle-text--short">
            {editMode ? '편집' : '보기'}
          </span>
        </button>
        {showQuestionQrButton ? (
          <button
            type="button"
            className="cp-theme-toggle cp-program-slide-public-qr-btn cp-program-slide-question-qr-btn"
            onClick={() => onOpenQuestionQr?.()}
            disabled={questionQrLoading}
            title="개별 디바이스 프레젠테이션 연결"
            aria-label="개별 디바이스 프레젠테이션 연결"
          >
            {questionQrLoading ? 'QR…' : '연결'}
          </button>
        ) : null}
        {registeredPageButtons ? (
          <span className="cp-program-slide-topnav-left-divider" aria-hidden />
        ) : null}
        {registeredPageButtons}
      </div>
      {onSlideTimerButtonClick ||
      onSlideBgmButtonClick ||
      onSlideDiceButtonClick ||
      (slideLiveCaptionSupported && onSlideLiveCaptionMicClick) ||
      (showSlideWebcamButton && onSlideWebcamCameraClick) ||
      (showSlideBoardButton && onSlideBoardButtonClick) ? (
        <div className="cp-program-slide-topnav-center">
          {showSlideBoardButton && onSlideBoardButtonClick ? (
            <button
              type="button"
              className={
                'cp-theme-toggle cp-program-slide-board-nav-btn' + (slideBoardDrawOpen ? ' is-on' : '')
              }
              aria-pressed={!!slideBoardDrawOpen}
              aria-label={slideBoardDrawOpen ? '칠판 도구 닫기' : '칠판 도구'}
              title={slideBoardDrawOpen ? '칠판 도구 닫기' : '칠판 — 색상·선택·그리기'}
              onClick={() => onSlideBoardButtonClick()}
            >
              <img
                src={`${process.env.PUBLIC_URL || ''}/blackboard.svg`}
                alt=""
                className="cp-dash-presentation-board-icon"
                aria-hidden
              />
            </button>
          ) : null}
          {showSlideWebcamButton && onSlideWebcamCameraClick ? (
            <div
              className={'cp-program-slide-webcam-nav-block' + (slideWebcamOn ? ' is-webcam-on' : '')}
              role="group"
              aria-label="웹캠 미리보기"
            >
              <button
                type="button"
                data-tutorial-slide="webcam"
                aria-busy={slideWebcamLoading || undefined}
                className={
                  'cp-program-slide-webcam-nav-block-btn cp-program-slide-webcam-nav-block-btn--camera' +
                  (slideWebcamLoading ? ' is-webcam-connecting' : '')
                }
                onClick={() => onSlideWebcamCameraClick()}
                title={
                  slideWebcamLoading
                    ? '웹캠 연결 중'
                    : slideWebcamOn
                      ? '웹캠 미리보기 사용 중 (끄기: 정지 버튼)'
                      : '웹캠 미리보기 켜기'
                }
                aria-pressed={slideWebcamOn ?? false}
                aria-label={
                  slideWebcamLoading
                    ? '웹캠 연결 중'
                    : slideWebcamOn
                      ? '웹캠 미리보기 사용 중, 끄기는 정지 버튼'
                      : '웹캠 미리보기 켜기'
                }
              >
                <span className="cp-program-slide-webcam-btn-icon" aria-hidden>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                    <circle cx="12" cy="13" r="3" />
                  </svg>
                </span>
              </button>
              {slideWebcamTools ? (
                <>
                  <span className="cp-program-slide-bgm-nav-block-gap" aria-hidden />
                  {slideWebcamTools}
                </>
              ) : null}
            </div>
          ) : null}
          {onSlideTimerButtonClick ? (
            <button
              type="button"
              data-tutorial-slide="timer"
              className={
                'cp-theme-toggle cp-program-slide-webcam-btn cp-program-slide-timer-btn' +
                (slideTimerActive ? ' is-on' : '')
              }
              onClick={() => onSlideTimerButtonClick()}
              title={slideTimerActive ? '타이머 중지' : '타이머 설정'}
              aria-pressed={!!slideTimerActive}
              aria-label={slideTimerActive ? '타이머 중지' : '타이머 설정'}
            >
              <span className="cp-program-slide-webcam-btn-icon" aria-hidden>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
              </span>
            </button>
          ) : null}
          {onSlideBgmButtonClick ? (
            <div
              className={
                'cp-program-slide-bgm-nav-block' + (slideBgmTransport !== 'idle' ? ' is-bgm-on' : '')
              }
              role="group"
              aria-label="BGM"
            >
              <button
                type="button"
                data-tutorial-slide="bgm"
                className="cp-program-slide-bgm-nav-block-btn cp-program-slide-bgm-nav-block-btn--note"
                onClick={() => onSlideBgmButtonClick()}
                title="BGM 선택"
                aria-label="BGM 선택"
              >
                <span className="cp-program-slide-webcam-btn-icon" aria-hidden>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M9 18V5l10-2v10" />
                    <circle cx="6" cy="18" r="3" />
                    <circle cx="16" cy="16" r="3" />
                  </svg>
                </span>
              </button>
              {slideBgmTransport !== 'idle' && onSlideBgmPauseToggle && onSlideBgmStop ? (
                <>
                  <span className="cp-program-slide-bgm-nav-block-gap" aria-hidden />
                  <button
                    type="button"
                    className="cp-program-slide-bgm-nav-block-btn cp-program-slide-bgm-nav-block-btn--transport"
                    onClick={() => onSlideBgmPauseToggle()}
                    title={slideBgmTransport === 'paused' ? 'BGM 재생' : 'BGM 일시정지'}
                    aria-label={slideBgmTransport === 'paused' ? 'BGM 재생' : 'BGM 일시정지'}
                    aria-pressed={slideBgmTransport === 'playing'}
                  >
                    <span className="cp-program-slide-webcam-btn-icon cp-program-slide-bgm-nav-block-icon--compact" aria-hidden>
                      {slideBgmTransport === 'paused' ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden
                        >
                          <path d="M8 5v14l11-7L8 5z" />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden
                        >
                          <rect x="7" y="6" width="4" height="12" rx="1" />
                          <rect x="13" y="6" width="4" height="12" rx="1" />
                        </svg>
                      )}
                    </span>
                  </button>
                  <span className="cp-program-slide-bgm-nav-block-gap" aria-hidden />
                  <button
                    type="button"
                    className="cp-program-slide-bgm-nav-block-btn cp-program-slide-bgm-nav-block-btn--transport"
                    data-tutorial-slide="bgm-stop"
                    onClick={() => onSlideBgmStop()}
                    title="BGM 정지"
                    aria-label="BGM 정지"
                  >
                    <span className="cp-program-slide-webcam-btn-icon cp-program-slide-bgm-nav-block-icon--compact" aria-hidden>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden
                      >
                        <rect x="7" y="7" width="10" height="10" rx="1.5" />
                      </svg>
                    </span>
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
          {onSlideDiceButtonClick ? (
            <button
              type="button"
              data-tutorial-slide="dice"
              className="cp-theme-toggle cp-program-slide-webcam-btn cp-program-slide-dice-btn"
              onClick={() => onSlideDiceButtonClick()}
              title="뽑기"
              aria-label="뽑기"
            >
              <span className="cp-program-slide-webcam-btn-icon" aria-hidden>
                <img src="/Dice.svg" alt="" width={18} height={18} />
              </span>
            </button>
          ) : null}
          {slideLiveCaptionSupported && onSlideLiveCaptionMicClick && onSlideLiveCaptionStop ? (
            <div
              className={
                'cp-program-slide-caption-nav-block' + (slideLiveCaptionOn ? ' is-caption-on' : '')
              }
              role="group"
              aria-label="실시간 자막"
            >
              <button
                type="button"
                data-tutorial-slide="caption"
                className="cp-program-slide-caption-nav-block-btn cp-program-slide-caption-nav-block-btn--mic"
                onClick={() => onSlideLiveCaptionMicClick()}
                disabled={!!slideLiveCaptionOn}
                title={slideLiveCaptionOn ? '자막 인식 중 (끄기: 정지)' : '실시간 자막 켜기'}
                aria-pressed={!!slideLiveCaptionOn}
                aria-label={slideLiveCaptionOn ? '자막 인식 중, 끄기는 정지 버튼' : '실시간 자막 켜기'}
              >
                <span className="cp-program-slide-webcam-btn-icon" aria-hidden>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z" />
                    <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
                    <line x1="12" x2="12" y1="19" y2="22" />
                  </svg>
                </span>
              </button>
              {slideLiveCaptionOn ? (
                <>
                  <span className="cp-program-slide-bgm-nav-block-gap" aria-hidden />
                  <button
                    type="button"
                    className="cp-program-slide-caption-nav-block-btn cp-program-slide-caption-nav-block-btn--transport"
                    data-tutorial-slide="caption-stop"
                    onClick={() => onSlideLiveCaptionStop()}
                    title="자막 끄기"
                    aria-label="자막 끄기"
                  >
                    <span className="cp-program-slide-webcam-btn-icon cp-program-slide-bgm-nav-block-icon--compact" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                        <rect x="7" y="7" width="10" height="10" rx="1.5" />
                      </svg>
                    </span>
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="cp-topnav-right">
        <RemoteBacklogNavButton />
        {onOfflineHtmlExport ? (
          <button
            type="button"
            className="cp-theme-toggle cp-program-slide-offline-html-btn"
            data-tutorial-slide="offline-html"
            onClick={() => onOfflineHtmlExport()}
            disabled={!!offlineHtmlExporting}
            title="오프라인 HTML로 저장"
            aria-label="오프라인 HTML로 저장"
            aria-busy={offlineHtmlExporting || undefined}
          >
            <ProgramSlideOfflineHtmlDownloadIcon />
          </button>
        ) : null}
        <button
          type="button"
          className="cp-theme-toggle cp-program-slide-fullscreen-btn"
          onClick={toggleSlideFullscreen}
          title={slideFullscreen ? '전체 화면 해제' : '전체 화면'}
          aria-label={slideFullscreen ? '전체 화면 해제' : '전체 화면'}
          aria-pressed={slideFullscreen}
        >
          {slideFullscreen ? <ProgramSlideFullscreenShrinkIcon /> : <ProgramSlideFullscreenExpandIcon />}
        </button>
        <button
          type="button"
          className="cp-theme-toggle"
          data-tutorial-slide="close"
          onClick={() => {
            if (isProgramContentSlideOpenedFromMainInSameWindow() && window.history.length > 1) {
              window.history.back();
              return;
            }
            window.close();
          }}
          title={isProgramContentSlideOpenedFromMainInSameWindow() ? '메인 프레젠으로 돌아가기' : '탭 닫기'}
        >
          {isProgramContentSlideOpenedFromMainInSameWindow() ? '메인 프레젠으로' : '닫기'}
        </button>
      </div>
    </header>
  );
}

type SlideWebcamCaptureMode = 'camera' | 'screen' | 'screen_camera';

type SlideCaptionWinMode = 'single-center' | 'two-line' | 'tail';

const ProgramContentSlidePage: React.FC = () => (
  <PaidPassGateProvider>
    <ProgramContentSlidePageGate />
  </PaidPassGateProvider>
);

const ProgramContentSlidePageGate: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const registryRaw = (searchParams.get('registry') ?? '').trim().toLowerCase();
  const isExplicitRegistryEntry =
    registryRaw === '1' ||
    registryRaw === 'true' ||
    registryRaw === 'yes' ||
    registryRaw === 'on';
  const { passGateStatus, isFeatureLocked } = usePaidPassGate();
  const isFreePass = isFeatureLocked('content_registry');
  const [paidPassBlockOpen, setPaidPassBlockOpen] = React.useState(false);

  React.useEffect(() => {
    if (!isExplicitRegistryEntry) return;
    if (passGateStatus !== 'ready') return;
    if (!isFreePass) return;
    setPaidPassBlockOpen(true);
  }, [isExplicitRegistryEntry, passGateStatus, isFreePass]);

  if (isExplicitRegistryEntry && (passGateStatus === 'pending' || passGateStatus === 'error')) {
    return (
      <div className="bootstrap-state bootstrap-splash" role="status" aria-busy="true" aria-label="이용권 확인 중">
        <div className="bootstrap-splash-inner">
          <div className="bootstrap-splash-spinner" aria-hidden />
        </div>
      </div>
    );
  }

  if (isExplicitRegistryEntry && isFreePass) {
    return (
      <PaidPassRequiredModal
        open={paidPassBlockOpen}
        onClose={() => {
          setPaidPassBlockOpen(false);
          if (typeof window !== 'undefined' && window.opener) {
            window.close();
            return;
          }
          navigate('/', { replace: true });
        }}
      />
    );
  }

  return <ProgramContentSlidePageInner />;
};

const ProgramContentSlidePageInner: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sidebarCtx = useProgramSidebarOptional();
  const k = searchParams.get('k') ?? '';
  const modeParam = (searchParams.get('mode') ?? '').trim().toLowerCase();
  const registryRaw = (searchParams.get('registry') ?? '').trim().toLowerCase();
  const registryKindRaw = (searchParams.get('registryKind') ?? '').trim().toLowerCase();
  const registryWorkspaceIdParam = (searchParams.get('workspaceId') ?? '').trim();
  const isRegistryMode =
    registryRaw === '1' ||
    registryRaw === 'true' ||
    registryRaw === 'yes' ||
    registryRaw === 'on' ||
    !k.trim();
  const registryKind: 'page' | 'layout' = registryKindRaw === 'layout' ? 'layout' : 'page';
  const isLayoutRegistryMode = isRegistryMode && registryKind === 'layout';
  const registryNoun = isLayoutRegistryMode ? '레이아웃' : '페이지';
  const registryMaxCount = programSlideRegistryMaxCount(isLayoutRegistryMode);
  const openInEditMode = isRegistryMode ? true : modeParam !== 'view';
  const [payload, setPayload] = React.useState<ProgramContentSlidePayloadV1 | null>(null);
  const registryWorkspaceId = React.useMemo(
    () =>
      registryWorkspaceIdParam ||
      (sidebarCtx?.workspaceId ?? '').trim() ||
      (payload?.workspaceId ?? '').trim(),
    [registryWorkspaceIdParam, sidebarCtx?.workspaceId, payload?.workspaceId]
  );
  const [registeredPages, setRegisteredPages] = React.useState<ProgramSlideRegisteredPage[]>([]);
  const [registeredLayouts, setRegisteredLayouts] = React.useState<ProgramSlideRegisteredLayout[]>([]);
  const [registryPageNamesByIndex, setRegistryPageNamesByIndex] = React.useState<Record<number, string>>({});
  const [registryPageIdsByIndex, setRegistryPageIdsByIndex] = React.useState<Record<number, string>>({});
  const [openedRegisteredPage, setOpenedRegisteredPage] = React.useState<ProgramSlideRegisteredPage | null>(null);
  const [layoutPickerPageIndex, setLayoutPickerPageIndex] = React.useState<number | null>(null);
  /** undefined=호버 없음(열 때 적용값), null=해제 미리보기, string=레이아웃 id 미리보기 */
  const [layoutPickerHoverId, setLayoutPickerHoverId] = React.useState<string | null | undefined>(undefined);
  const [layoutPickerOriginalId, setLayoutPickerOriginalId] = React.useState('');
  const layoutPickerRef = React.useRef<HTMLDivElement>(null);
  const [slideSettingsModalOpen, setSlideSettingsModalOpen] = React.useState(false);
  const slideSettingsModalRef = React.useRef<HTMLDivElement>(null);
  const [registryNameModal, setRegistryNameModal] = React.useState<{
    pageIndex: number;
    draft: string;
    fallbackName: string;
  } | null>(null);
  const registryNameModalRef = React.useRef<HTMLDivElement>(null);
  const registryNameInputRef = React.useRef<HTMLInputElement>(null);
  const [slideGlobalTextColorHex, setSlideGlobalTextColorHex] = React.useState(SLIDE_DEFAULT_TEXT_COLOR_HEX);
  const [slideGlobalBackgroundColorHex, setSlideGlobalBackgroundColorHex] = React.useState(SLIDE_DEFAULT_BG_COLOR_HEX);
  const registeredPageRestoreEditModeRef = React.useRef<boolean | null>(null);
  const slideDiceMemberLookup = React.useMemo((): { workspaceId: string; gradeId: string } | null => {
    const p = payload;
    if (!p?.workspaceId?.trim()) return null;
    const gid = (p.contextGradeId ?? p.createPresentationRefOnSave?.gradeId ?? '').trim();
    if (!gid) return null;
    return { workspaceId: p.workspaceId.trim(), gradeId: gid };
  }, [payload]);
  const [extraPageCount, setExtraPageCount] = React.useState(1);
  const [layouts, setLayouts] = React.useState<Record<number, ProgramSlideLayoutV1>>({});
  const layoutsRef = React.useRef(layouts);
  layoutsRef.current = layouts;
  /** 마지막으로 DB에 반영된 클립보드 이미지 Storage URL (저장 시 diff용) */
  const savedClipboardImageUrlsRef = React.useRef<Set<string>>(new Set());
  /** 화면에 불러온(또는 마지막으로 저장한) 슬라이드 페이지 버전 — 저장 직전 다른 기기 저장 여부 비교용 */
  const loadedSlideVersionRef = React.useRef<{ ownerKey: string; signature: string } | null>(null);
  /** 학습계획 미리보기를 연 뒤 저장되면, 늦게 도착한 불러오기가 저장본을 다시 더티로 만들지 않게 한다. */
  const aiPreviewHoldRef = React.useRef(0);
  const [selectedEditablePageIndex, setSelectedEditablePageIndex] = React.useState<number | null>(null);
  const [editMode, setEditMode] = React.useState(openInEditMode);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [savingSlides, setSavingSlides] = React.useState(false);
  const [offlineHtmlExporting, setOfflineHtmlExporting] = React.useState(false);
  const [hasUnsavedSlideDraft, setHasUnsavedSlideDraft] = React.useState(false);
  const [slideSaveMessage, setSlideSaveMessage] = React.useState('');
  const [editableSlideOrder, setEditableSlideOrder] = React.useState<number[]>([]);
  const [draggingSlideId, setDraggingSlideId] = React.useState<string | null>(null);
  const deleteSlideHoldTimerRef = React.useRef<number | null>(null);
  const [, setHoldingSlideDelete] = React.useState(false);
  const [publicQrOverlay, setPublicQrOverlay] = React.useState<{
    publicUrl: string;
    expiresAt: string;
    /** comm: 저장 전 소통방(학생 기기) QR — 주소는 방 정보에서 */
    mode: 'full' | 'question' | 'comm';
  } | null>(null);
  const [publicQrError, setPublicQrError] = React.useState('');
  const [questionQrLoading, setQuestionQrLoading] = React.useState(false);
  const [localOnlyHintDismissed, setLocalOnlyHintDismissed] = React.useState(false);
  const [isCommunicationView, setIsCommunicationView] = React.useState(false);
  const slideBodyScrollRef = React.useRef<HTMLDivElement>(null);
  const slideBodyScrollRoRef = React.useRef<ResizeObserver | null>(null);
  const [slideCanvasFitWidth, setSlideCanvasFitWidth] = React.useState(false);
  const [slideCanvasOverflowsX, setSlideCanvasOverflowsX] = React.useState(false);
  const [slideCanvasFitScale, setSlideCanvasFitScale] = React.useState(1);
  const [communicationPrompt, setCommunicationPrompt] = React.useState('');
  const [communicationAnswer, setCommunicationAnswer] = React.useState('');
  const [communicationQuestion, setCommunicationQuestion] =
    React.useState<CommunicationQuestion | null>(null);
  const [communicationGroups, setCommunicationGroups] = React.useState<ClassMemberGroupMeta[]>([]);
  const [communicationMembersByGroupId, setCommunicationMembersByGroupId] = React.useState<
    Record<string, ClassMemberRow[]>
  >({});
  const [communicationSelectedGroupId, setCommunicationSelectedGroupId] = React.useState<string | null>(
    null
  );
  const [communicationResponses, setCommunicationResponses] = React.useState<
    PresentationCommunicationResponse[]
  >([]);
  /**
   * 질문별 응답 캐시(한 번 불러오면 재사용). 데이터 캐시·버전 비교 로드 패턴:
   * </> 로 이미 조회한 질문에 돌아오면 DB를 다시 조회하지 않고 캐시를 그대로 쓰고,
   * 실시간 구독으로 해당 질문의 응답이 실제로 바뀌었을 때만 그 질문 캐시를 무효화한다.
   */
  const communicationResponsesCacheRef = React.useRef<Map<string, PresentationCommunicationResponse[]>>(
    new Map()
  );
  const [communicationQuestions, setCommunicationQuestions] = React.useState<
    PresentationCommunicationQuestionRow[]
  >([]);
  /** 저장된 질문 index 또는 questions.length(빈 초안) */
  const [communicationQuestionIndex, setCommunicationQuestionIndex] = React.useState(0);
  /**
   * FAB 저장 전에도 <> 로 탐색할 수 있도록, 인덱스별 로컬 초안을 보관한다.
   * 서버 questions보다 우선(같은 index의 미저장 편집 포함).
   */
  const [communicationLocalDrafts, setCommunicationLocalDrafts] = React.useState<
    Record<
      number,
      {
        promptText: string;
        answerText: string;
        question: CommunicationQuestion | null;
        multiResponse: boolean;
      }
    >
  >({});
  const communicationEditorRef = React.useRef({
    index: 0,
    prompt: '',
    answer: '',
    question: null as CommunicationQuestion | null,
    localDrafts: {} as typeof communicationLocalDrafts,
    questions: [] as PresentationCommunicationQuestionRow[],
  });
  communicationEditorRef.current = {
    index: communicationQuestionIndex,
    prompt: communicationPrompt,
    answer: communicationAnswer,
    question: communicationQuestion,
    localDrafts: communicationLocalDrafts,
    questions: communicationQuestions,
  };
  /** 재생 중일 때만 true — 공개(QR) 화면에 현재 질문이 노출됨 */
  const [communicationIsOpen, setCommunicationIsOpen] = React.useState(false);
  /** 재생 버튼에 사용할 단일/복수 응답 초안(재생 전에는 로컬만, 재생 중이면 즉시 서버 반영) */
  const [communicationMultiResponseDraft, setCommunicationMultiResponseDraft] = React.useState(false);
  const [communicationPlayBusy, setCommunicationPlayBusy] = React.useState(false);
  const [communicationDeleteConfirmOpen, setCommunicationDeleteConfirmOpen] = React.useState(false);
  const [communicationDeleting, setCommunicationDeleting] = React.useState(false);
  const [communicationSaving, setCommunicationSaving] = React.useState(false);
  const [communicationSaveError, setCommunicationSaveError] = React.useState<string | null>(null);
  const [communicationLoadError, setCommunicationLoadError] = React.useState('');
  const {
    toastState: presentationToastState,
    showSuccess: showPresentationToastSuccess,
    showError: showPresentationToastError,
  } = useAutoStatusToast(3200);
  const [slideWebcamDesired, setSlideWebcamDesired] = React.useState(false);
  const [slideBoardDrawOpen, setSlideBoardDrawOpen] = React.useState(false);
  const [slideWebcamLoading, setSlideWebcamLoading] = React.useState(false);
  const [slideWebcamError, setSlideWebcamError] = React.useState('');
  const [slideWebcamCaptureMode, setSlideWebcamCaptureMode] = React.useState<SlideWebcamCaptureMode>('camera');
  const [slideWebcamCameraDeviceId, setSlideWebcamCameraDeviceId] = React.useState<string | null>(null);
  const [slideWebcamDeviceList, setSlideWebcamDeviceList] = React.useState<MediaDeviceInfo[]>([]);
  const [slideWebcamSourceMenuOpen, setSlideWebcamSourceMenuOpen] = React.useState(false);
  const slideWebcamSourceMenuRef = React.useRef<HTMLDivElement | null>(null);
  const webcamVideoRef = React.useRef<HTMLVideoElement | null>(null);
  const screenVideoRef = React.useRef<HTMLVideoElement | null>(null);
  const webcamStreamRef = React.useRef<MediaStream | null>(null);
  const screenStreamRef = React.useRef<MediaStream | null>(null);
  const webcamEffectGenRef = React.useRef(0);
  const frozenBlobUrlRef = React.useRef<string | null>(null);
  const webcamFrozenRef = React.useRef(false);
  const freezeGenRef = React.useRef(0);
  const freezePendingRef = React.useRef(false);
  const freezeResolveRef = React.useRef<(() => void) | null>(null);
  const [frozenFrameUrl, setFrozenFrameUrl] = React.useState<string | null>(null);
  const webcamSessionBaselineByPageRef = React.useRef<WebcamFreezeBaselineByPage | null>(null);
  const [webcamSessionBaselineSnapshot, setWebcamSessionBaselineSnapshot] =
    React.useState<WebcamFreezeBaselineByPage | null>(null);
  const [webcamDrawStrokesByPage, setWebcamDrawStrokesByPage] = React.useState<
    Record<string, WebcamDrawStroke[]>
  >({});
  const [boardDrawStrokesByPage, setBoardDrawStrokesByPage] = React.useState<
    Record<string, WebcamDrawStroke[]>
  >({});
  /** 타이틀 페이지 표시용 임시 텍스트. 저장·DB에 넣지 않음 */
  const [sessionTitleOverride, setSessionTitleOverride] = React.useState<string | null>(null);
  const [sessionAchievementOverride, setSessionAchievementOverride] = React.useState<string | null>(null);
  const [sessionLearningMemoOverride, setSessionLearningMemoOverride] = React.useState<string | null>(
    null
  );
  const [sessionEvaluationOverride, setSessionEvaluationOverride] = React.useState<string | null>(null);
  React.useEffect(() => {
    setSessionTitleOverride(null);
    setSessionAchievementOverride(null);
    setSessionLearningMemoOverride(null);
    setSessionEvaluationOverride(null);
    setWebcamDrawStrokesByPage({});
    setBoardDrawStrokesByPage({});
  }, [k]);
  const webcamDrawStrokesByPageRef = React.useRef(webcamDrawStrokesByPage);
  webcamDrawStrokesByPageRef.current = webcamDrawStrokesByPage;
  type SlidePageClipboard = {
    layout: ProgramSlideLayoutV1;
    webcamDrawStrokes: WebcamDrawStroke[];
  };
  const slidePageClipboardRef = React.useRef<SlidePageClipboard | null>(null);
  const slideBlockClipboardActiveRef = React.useRef(false);
  const markSlideBlockClipboardActive = React.useCallback(() => {
    slidePageClipboardRef.current = null;
    slideBlockClipboardActiveRef.current = true;
  }, []);
  const selectedEditablePageIndexRef = React.useRef(selectedEditablePageIndex);
  selectedEditablePageIndexRef.current = selectedEditablePageIndex;
  const footerDotsScrollRef = React.useRef<HTMLDivElement>(null);
  const extraPageCountRef = React.useRef(extraPageCount);
  extraPageCountRef.current = extraPageCount;
  const prevWebcamSessionOpenRef = React.useRef(false);
  /** 웹캠 켠 뒤 슬라이드(타이틀/쪽) 바뀌면 미리보기 자동 종료용 */
  const slideWebcamNavKeyRef = React.useRef<string | null>(null);

  /** 하단 슬라이드 네비 타이머 */
  const [slideTimerModalOpen, setSlideTimerModalOpen] = React.useState(false);
  const [slideBgmModalOpen, setSlideBgmModalOpen] = React.useState(false);
  const [slideDiceModalOpen, setSlideDiceModalOpen] = React.useState(false);
  const [slideBgmTransport, setSlideBgmTransport] = React.useState<'idle' | 'playing' | 'paused'>('idle');
  const slideBgmPlaybackRef = React.useRef<SlideBgmPlaybackHandle | null>(null);
  const [slideTimerDraftSec, setSlideTimerDraftSec] = React.useState(0);
  const [slideTimerActive, setSlideTimerActive] = React.useState(false);
  const [slideTimerPaused, setSlideTimerPaused] = React.useState(false);
  const [slideTimerPausedRemainingMs, setSlideTimerPausedRemainingMs] = React.useState(0);
  const slideTimerPauseRemainingMsRef = React.useRef(0);
  const slideTimerSessionIdRef = React.useRef(0);
  const [slideTimerTotalMs, setSlideTimerTotalMs] = React.useState(0);
  const slideTimerEndsAtRef = React.useRef(0);
  const [slideTimerTick, setSlideTimerTick] = React.useState(0);
  /** 마지막 1분 알림음을 이미 재생한 타이머 세션 id(일시정지 후 재개 시 중복 재생 방지) */
  const timerLastMinuteChimeDoneForSessionRef = React.useRef(0);
  const slideTimerPausedRef = React.useRef(false);
  slideTimerPausedRef.current = slideTimerPaused;
  const slideCanvasHandleRef = React.useRef<ProgramSlideCanvasHandle | null>(null);

  const {
    active: slideLiveCaptionOn,
    text: slideLiveCaptionText,
    error: slideLiveCaptionError,
    start: startSlideLiveCaption,
    stop: stopSlideLiveCaption,
    isSupported: slideLiveCaptionSupported,
  } = useSlideLiveCaptionRecognition();

  const handleSlideLiveCaptionMicClick = React.useCallback(() => {
    if (!slideLiveCaptionSupported) return;
    if (slideLiveCaptionOn) return;
    startSlideLiveCaption();
  }, [slideLiveCaptionSupported, slideLiveCaptionOn, startSlideLiveCaption]);

  const handleSlideLiveCaptionStopClick = React.useCallback(() => {
    stopSlideLiveCaption();
  }, [stopSlideLiveCaption]);

  const slideCaptionInnerRef = React.useRef<HTMLDivElement | null>(null);
  const [slideCaptionWinMode, setSlideCaptionWinMode] = React.useState<SlideCaptionWinMode>('two-line');

  React.useLayoutEffect(() => {
    if (!slideLiveCaptionOn) {
      setSlideCaptionWinMode('two-line');
      return;
    }
    const inner = slideCaptionInnerRef.current;
    const winEl = inner?.parentElement;
    if (!inner || !winEl) return;
    const measure = () => {
      const ch = winEl.clientHeight;
      const sh = inner.scrollHeight;
      const lhRaw = getComputedStyle(inner).lineHeight;
      let linePx = parseFloat(lhRaw);
      if (!Number.isFinite(linePx) || linePx <= 0) {
        linePx = ch / 2;
      }
      const singleLineTol = 6;
      if (sh <= linePx + singleLineTol) {
        setSlideCaptionWinMode('single-center');
      } else if (sh <= ch + 1) {
        setSlideCaptionWinMode('two-line');
      } else {
        setSlideCaptionWinMode('tail');
      }
    };
    measure();
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(winEl);
      ro.observe(inner);
    }
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [slideLiveCaptionOn, slideLiveCaptionText]);

  React.useEffect(() => {
    primeTimerOneMinSoundCache();
  }, []);

  React.useEffect(() => {
    if (!slideTimerActive || slideTimerPaused) return;
    const id = window.setInterval(() => {
      const left = slideTimerEndsAtRef.current - Date.now();
      if (left <= 0) {
        /** 1분 알림음은 `stopSlideTimerChimeAudioHard`로 끊지 않음 — 끝까지 재생(수동 중지·새 타이머 시작 시에만 정지) */
        slideTimerPausedRef.current = false;
        setSlideTimerPaused(false);
        setSlideTimerActive(false);
        setSlideTimerTotalMs(0);
      }
      setSlideTimerTick((n) => n + 1);
    }, 100);
    return () => window.clearInterval(id);
  }, [slideTimerActive, slideTimerPaused]);

  const addSlideTimerDraftSeconds = React.useCallback((delta: number) => {
    setSlideTimerDraftSec((s) => Math.min(Math.max(0, s + delta), 24 * 3600));
  }, []);

  const resetSlideTimerDraft = React.useCallback(() => setSlideTimerDraftSec(0), []);

  const startSlideTimerFromModal = React.useCallback(async () => {
    if (slideTimerDraftSec <= 0) return;
    stopSlideTimerChimeAudioHard();
    await unlockSlideTimerOneMinuteChimeAudio();
    slideTimerSessionIdRef.current += 1;
    timerLastMinuteChimeDoneForSessionRef.current = 0;
    setSlideTimerPaused(false);
    slideTimerPausedRef.current = false;
    const ms = slideTimerDraftSec * 1000;
    setSlideTimerTotalMs(ms);
    slideTimerEndsAtRef.current = Date.now() + ms;
    setSlideTimerActive(true);
    setSlideTimerModalOpen(false);
  }, [slideTimerDraftSec]);

  const handleSlideTimerNavButtonClick = React.useCallback(() => {
    if (slideTimerActive) {
      stopSlideTimerChimeAudioHard();
      setSlideTimerPaused(false);
      slideTimerPausedRef.current = false;
      setSlideTimerActive(false);
      setSlideTimerTotalMs(0);
      return;
    }
    setSlideTimerModalOpen(true);
  }, [slideTimerActive]);

  /* 슬라이드 BGM = 리모컨 음악도 같은 플레이어 사용(한 곡만, 버튼 UI·리모컨 명령이 같은 상태를 봄) */
  const [slideBgmBlocked, setSlideBgmBlocked] = React.useState(false);
  const slideBgmSeqRef = React.useRef(0);
  const slideBgmDuckedRef = React.useRef(false);
  const slideBgmCurrentRef = React.useRef<{ moodId: string; track: number } | null>(null);
  const handleSlideBgmPick = React.useCallback(async (pick: ProgramSlideBgmPick): Promise<boolean> => {
    const seq = ++slideBgmSeqRef.current;
    slideBgmPlaybackRef.current?.stop();
    slideBgmPlaybackRef.current = null;
    const tracks = slideBgmTracksForMood(pick.moodId);
    const track = pick.mode === 'random' ? tracks[Math.floor(Math.random() * tracks.length)] : pick.track;
    writeLastPick(pick.moodId, track);
    const src = resolveSlideBgmAudioSrc(pick.moodId, track);
    const handle = await startSlideBgmPlaybackAsync(src, {
      moodId: pick.moodId,
      track,
    });
    if (seq !== slideBgmSeqRef.current) {
      handle.stop();
      return false;
    }
    slideBgmPlaybackRef.current = handle;
    slideBgmCurrentRef.current = { moodId: pick.moodId, track };
    if (slideBgmDuckedRef.current) handle.setVolume(0.25);
    setSlideBgmTransport('playing');
    setSlideBgmModalOpen(false);
    const ok = await handle.initialPlay;
    if (seq !== slideBgmSeqRef.current) return ok;
    setSlideBgmBlocked(!ok);
    if (!ok) setSlideBgmTransport('paused');
    return ok;
  }, []);

  const handleSlideBgmPauseToggle = React.useCallback(() => {
    const h = slideBgmPlaybackRef.current;
    if (!h) return;
    if (h.isPaused()) {
      h.resume();
      setSlideBgmTransport('playing');
    } else {
      h.pause();
      setSlideBgmTransport('paused');
    }
  }, []);

  const handleSlideBgmStop = React.useCallback(() => {
    slideBgmSeqRef.current += 1;
    slideBgmPlaybackRef.current?.stop();
    slideBgmPlaybackRef.current = null;
    slideBgmCurrentRef.current = null;
    setSlideBgmTransport('idle');
    setSlideBgmBlocked(false);
  }, []);

  /** 리모컨 음악 명령 → 이 페이지 BGM 플레이어 (리모컨·슬라이드 어느 쪽이 튼 곡이든 같은 곡을 제어) */
  const slideRemoteBgm = React.useMemo<RemoteBgmController>(
    () => ({
      bgmBlocked: slideBgmBlocked,
      remotePlay: async (opt) => {
        try {
          const rp = opt?.pick;
          const cur = slideBgmPlaybackRef.current;
          /* 같은 곡이면 재개/유지, 다르면 리모컨이 고른 곡으로 전환 */
          if (rp && !opt?.restart && cur && sameBgmPick(slideBgmCurrentRef.current, rp)) {
            if (!cur.isPaused()) return { ok: true };
            const ok = await cur.resumeAsync();
            setSlideBgmBlocked(!ok);
            setSlideBgmTransport(ok ? 'playing' : 'paused');
            return ok ? { ok: true } : { ok: false, error: 'autoplay_blocked' };
          }
          if (rp) {
            const pick = remotePickToSlidePick(rp);
            if (!pick) return { ok: false, error: 'unknown' };
            return (await handleSlideBgmPick(pick)) ? { ok: true } : { ok: false, error: 'autoplay_blocked' };
          }
          const h = slideBgmPlaybackRef.current;
          if (h) {
            if (!h.isPaused()) return { ok: true };
            const ok = await h.resumeAsync();
            setSlideBgmBlocked(!ok);
            setSlideBgmTransport(ok ? 'playing' : 'paused');
            return ok ? { ok: true } : { ok: false, error: 'autoplay_blocked' };
          }
          const last = readLastPick();
          const ok = await handleSlideBgmPick({ moodId: last.moodId, moodLabel: '', mode: 'track', track: last.track });
          return ok ? { ok: true } : { ok: false, error: 'autoplay_blocked' };
        } catch {
          return { ok: false, error: 'unknown' };
        }
      },
      remotePause: () => {
        const h = slideBgmPlaybackRef.current;
        if (h && !h.isPaused()) h.pause();
        if (h) setSlideBgmTransport('paused');
        setSlideBgmBlocked(false);
        return { ok: true };
      },
      remoteStop: handleSlideBgmStop,
      setBgmDucked: (on) => {
        slideBgmDuckedRef.current = on;
        slideBgmPlaybackRef.current?.setVolume(on ? 0.25 : 1);
      },
      resumeFromGesture: async () => {
        const h = slideBgmPlaybackRef.current;
        if (!h) {
          setSlideBgmBlocked(false);
          return;
        }
        const ok = await h.resumeAsync();
        setSlideBgmBlocked(!ok);
        if (ok) setSlideBgmTransport('playing');
      },
    }),
    [slideBgmBlocked, handleSlideBgmPick, handleSlideBgmStop]
  );

  const toggleSlideTimerHeroPause = React.useCallback(() => {
    if (!slideTimerActive) return;
    if (!slideTimerPausedRef.current) {
      const left = Math.max(0, slideTimerEndsAtRef.current - Date.now());
      slideTimerPauseRemainingMsRef.current = left;
      setSlideTimerPausedRemainingMs(left);
      pauseSlideTimerChimeForTimerPause();
      setSlideTimerPaused(true);
      slideTimerPausedRef.current = true;
      return;
    }
    slideTimerEndsAtRef.current = Date.now() + slideTimerPauseRemainingMsRef.current;
    setSlideTimerPaused(false);
    slideTimerPausedRef.current = false;
    resumeSlideTimerChimeAfterTimerPause();
    setSlideTimerTick((n) => n + 1);
  }, [slideTimerActive]);

  React.useEffect(() => {
    if (!slideTimerModalOpen && !slideBgmModalOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [slideTimerModalOpen, slideBgmModalOpen]);

  React.useEffect(() => {
    return () => {
      slideBgmPlaybackRef.current?.stop();
      slideBgmPlaybackRef.current = null;
    };
  }, []);

  const releaseFrozenFrame = React.useCallback(() => {
    freezeGenRef.current += 1;
    freezePendingRef.current = false;
    if (frozenBlobUrlRef.current) {
      URL.revokeObjectURL(frozenBlobUrlRef.current);
      frozenBlobUrlRef.current = null;
    }
    webcamFrozenRef.current = false;
    setFrozenFrameUrl(null);
  }, []);

  React.useEffect(() => {
    if (slideWebcamDesired) {
      const snap = captureWebcamFreezeBaselineFromLayouts(layoutsRef.current);
      webcamSessionBaselineByPageRef.current = snap;
      setWebcamSessionBaselineSnapshot(snap);
      prevWebcamSessionOpenRef.current = true;
      return;
    }
    if (prevWebcamSessionOpenRef.current) {
      const baseline = webcamSessionBaselineByPageRef.current;
      webcamSessionBaselineByPageRef.current = null;
      setWebcamSessionBaselineSnapshot(null);
      if (baseline) {
        let layoutChanged = false;
        setLayouts((prev) => {
          const next = pruneLayoutsToWebcamFreezeBaseline(prev, baseline);
          layoutChanged = JSON.stringify(prev) !== JSON.stringify(next);
          return next;
        });
        if (layoutChanged) setHasUnsavedSlideDraft(true);
      }
    }
    prevWebcamSessionOpenRef.current = false;
  }, [slideWebcamDesired, setLayouts, setHasUnsavedSlideDraft]);

  React.useEffect(() => {
    if (!slideWebcamDesired) {
      slideWebcamNavKeyRef.current = null;
      return;
    }
    const key = selectedEditablePageIndex == null ? 'title' : `p:${selectedEditablePageIndex}`;
    const prev = slideWebcamNavKeyRef.current;
    if (prev === null) {
      slideWebcamNavKeyRef.current = key;
      return;
    }
    if (prev !== key) {
      setSlideWebcamDesired(false);
    }
  }, [slideWebcamDesired, selectedEditablePageIndex]);

  const handleSlideWebcamNavCameraClick = React.useCallback(() => {
    if (slideWebcamLoading) return;
    setSlideWebcamSourceMenuOpen(false);
    if (slideWebcamDesired) return;
    setSlideBoardDrawOpen(false);
    setBoardDrawStrokesByPage({});
    setSlideWebcamDesired(true);
  }, [slideWebcamLoading, slideWebcamDesired]);

  const runWebcamFreezeCapture = React.useCallback((): boolean => {
    const v = webcamVideoRef.current;
    if (!v || !slideWebcamDesired) return false;
    if (webcamFrozenRef.current || frozenBlobUrlRef.current) {
      freezeResolveRef.current?.();
      freezeResolveRef.current = null;
      return true;
    }
    if (freezePendingRef.current) return false;

    const w = v.videoWidth;
    const h = v.videoHeight;
    if (w <= 0 || h <= 0) return false;

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    try {
      ctx.drawImage(v, 0, 0, w, h);
    } catch {
      return false;
    }

    freezePendingRef.current = true;
    freezeGenRef.current += 1;
    const gen = freezeGenRef.current;

    canvas.toBlob(
      (blob) => {
        freezePendingRef.current = false;
        if (!blob || gen !== freezeGenRef.current || !webcamStreamRef.current) {
          freezeResolveRef.current = null;
          return;
        }
        const url = URL.createObjectURL(blob);
        if (frozenBlobUrlRef.current) URL.revokeObjectURL(frozenBlobUrlRef.current);
        frozenBlobUrlRef.current = url;
        webcamFrozenRef.current = true;
        setFrozenFrameUrl(url);
        void webcamVideoRef.current?.pause();
        freezeResolveRef.current?.();
        freezeResolveRef.current = null;
      },
      'image/jpeg',
      0.94
    );
    return true;
  }, [slideWebcamDesired]);

  const ensureWebcamFrozen = React.useCallback((): Promise<void> => {
    if (webcamFrozenRef.current || frozenBlobUrlRef.current) return Promise.resolve();
    if (freezePendingRef.current) {
      return new Promise((resolve) => {
        const prev = freezeResolveRef.current;
        freezeResolveRef.current = () => {
          prev?.();
          resolve();
        };
      });
    }
    return new Promise((resolve, reject) => {
      freezeResolveRef.current = resolve;
      if (!runWebcamFreezeCapture()) {
        freezeResolveRef.current = null;
        reject(new Error('웹캠 미리보기를 정지할 수 없습니다.'));
      }
    });
  }, [runWebcamFreezeCapture]);

  const toggleWebcamFreeze = React.useCallback(() => {
    if (slideWebcamLoading) return;
    const v = webcamVideoRef.current;
    if (!v || !slideWebcamDesired) return;

    if (webcamFrozenRef.current || frozenBlobUrlRef.current) {
      releaseFrozenFrame();
      void v.play().catch(() => {});
      return;
    }

    runWebcamFreezeCapture();
  }, [slideWebcamDesired, slideWebcamLoading, releaseFrozenFrame, runWebcamFreezeCapture]);

  React.useEffect(() => {
    setEditMode(openInEditMode);
  }, [openInEditMode, k, isRegistryMode]);

  const clearDeleteSlideHold = React.useCallback(() => {
    if (deleteSlideHoldTimerRef.current != null) {
      window.clearTimeout(deleteSlideHoldTimerRef.current);
      deleteSlideHoldTimerRef.current = null;
    }
    setHoldingSlideDelete(false);
  }, []);

  React.useEffect(() => {
    const sid = payload?.workspaceId?.trim();
    const sc = payload?.scopeId?.trim();
    const ak = payload?.annualCellKey?.trim();
    if (!sid || (!sc && !ak)) return;

    let released = false;
    const releaseOnce = () => {
      if (released) return;
      released = true;
      releaseProgramSlideEditScopeLock(sid, sc, ak || undefined);
    };

    acquireProgramSlideEditScopeLock(sid, sc, ak || undefined);
    window.addEventListener('pagehide', releaseOnce);
    window.addEventListener('beforeunload', releaseOnce);
    return () => {
      window.removeEventListener('pagehide', releaseOnce);
      window.removeEventListener('beforeunload', releaseOnce);
      releaseOnce();
    };
  }, [payload?.workspaceId, payload?.scopeId, payload?.annualCellKey]);

  React.useEffect(() => {
    const sid = payload?.workspaceId?.trim();
    const sc = payload?.scopeId?.trim();
    if (!sid) return;
    const tick = () => {
      touchProgramSlidePresentationOverlayHeartbeat(sid);
      if (sc) touchProgramSlideEditScopeLockHeartbeat(sid, sc);
    };
    tick();
    const id = window.setInterval(tick, 20_000);
    return () => window.clearInterval(id);
  }, [payload?.workspaceId, payload?.scopeId]);

  React.useEffect(() => {
    const stopAllPreview = () => {
      const cam = webcamStreamRef.current;
      if (cam) {
        cam.getTracks().forEach((t) => t.stop());
        webcamStreamRef.current = null;
      }
      const scr = screenStreamRef.current;
      if (scr) {
        scr.getTracks().forEach((t) => t.stop());
        screenStreamRef.current = null;
      }
      const v = webcamVideoRef.current;
      if (v) v.srcObject = null;
      const sv = screenVideoRef.current;
      if (sv) sv.srcObject = null;
    };

    if (!slideWebcamDesired) {
      releaseFrozenFrame();
      stopAllPreview();
      setSlideWebcamLoading(false);
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setSlideWebcamError('이 환경에서는 카메라를 사용할 수 없습니다. HTTPS 접속인지 확인해 주세요.');
      setSlideWebcamDesired(false);
      return;
    }

    let cancelled = false;
    let removeScreenEndedListener: (() => void) | undefined;
    const runGen = (webcamEffectGenRef.current += 1);
    const alive = () => !cancelled && runGen === webcamEffectGenRef.current;

    const attachScreenEnded = (stream: MediaStream, onEnd: () => void) => {
      const t = stream.getVideoTracks()[0];
      if (!t) return () => {};
      t.addEventListener('ended', onEnd);
      return () => t.removeEventListener('ended', onEnd);
    };

    setSlideWebcamLoading(true);
    setSlideWebcamError('');

    void (async () => {
      try {
        stopAllPreview();
        if (!alive()) return;

        const mode = slideWebcamCaptureMode;
        const camId = slideWebcamCameraDeviceId?.trim() || null;

        if (mode === 'camera') {
          const stream =
            camId != null && camId !== ''
              ? await getSlideWebcamUserMedia({ deviceId: camId })
              : await getSlideWebcamUserMediaSmartDefault();
          if (!alive()) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }
          webcamStreamRef.current = stream;
          const v = webcamVideoRef.current;
          if (v) {
            v.srcObject = stream;
            void v.play().catch(() => {});
          }
        } else if (mode === 'screen') {
          const stream = await getSlideScreenShareMedia();
          if (!alive()) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }
          screenStreamRef.current = stream;
          removeScreenEndedListener = attachScreenEnded(stream, () => {
            if (cancelled) return;
            setSlideWebcamDesired(false);
          });
          const v = webcamVideoRef.current;
          if (v) {
            v.srcObject = stream;
            void v.play().catch(() => {});
          }
        } else {
          const scr = await getSlideScreenShareMedia();
          if (!alive()) {
            scr.getTracks().forEach((t) => t.stop());
            return;
          }
          screenStreamRef.current = scr;
          removeScreenEndedListener = attachScreenEnded(scr, () => {
            if (cancelled) return;
            setSlideWebcamDesired(false);
          });
          const sv = screenVideoRef.current;
          if (sv) {
            sv.srcObject = scr;
            void sv.play().catch(() => {});
          }

          const camStream =
            camId != null && camId !== ''
              ? await getSlideWebcamUserMedia({ deviceId: camId })
              : await getSlideWebcamUserMediaSmartDefault();
          if (!alive()) {
            scr.getTracks().forEach((t) => t.stop());
            camStream.getTracks().forEach((t) => t.stop());
            return;
          }
          webcamStreamRef.current = camStream;
          const pv = webcamVideoRef.current;
          if (pv) {
            pv.srcObject = camStream;
            void pv.play().catch(() => {});
          }
        }
      } catch (err: unknown) {
        if (!alive()) return;
        stopAllPreview();
        const name = err && typeof err === 'object' && 'name' in err ? String((err as { name?: string }).name) : '';
        if (
          name === 'NotAllowedError' &&
          (slideWebcamCaptureMode === 'screen' || slideWebcamCaptureMode === 'screen_camera')
        ) {
          setSlideWebcamCaptureMode('camera');
          setSlideWebcamError('화면 공유가 취소되었습니다.');
          window.setTimeout(() => setSlideWebcamError(''), 4200);
          return;
        }
        const msg =
          name === 'NotAllowedError'
            ? '카메라 사용이 거부되었습니다.'
            : err instanceof Error
              ? err.message
              : '미디어를 켤 수 없습니다.';
        setSlideWebcamError(msg);
        setSlideWebcamDesired(false);
        window.setTimeout(() => setSlideWebcamError(''), 4200);
      } finally {
        if (!cancelled && runGen === webcamEffectGenRef.current) setSlideWebcamLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      removeScreenEndedListener?.();
      releaseFrozenFrame();
      stopAllPreview();
    };
  }, [slideWebcamDesired, slideWebcamCaptureMode, slideWebcamCameraDeviceId, releaseFrozenFrame]);

  React.useEffect(() => {
    if (!slideWebcamSourceMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      const el = slideWebcamSourceMenuRef.current;
      if (el && !el.contains(e.target as Node)) setSlideWebcamSourceMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [slideWebcamSourceMenuOpen]);

  React.useEffect(() => {
    const p = isRegistryMode ? null : readProgramContentSlidePayload(k);
    setPayload(p);
    setOpenedRegisteredPage(null);
    setSelectedEditablePageIndex(null);
    setLoadError(null);
    setSavingSlides(false);
    setHasUnsavedSlideDraft(false);
    setSlideSaveMessage('');
    setDraggingSlideId(null);
    clearDeleteSlideHold();
    setSlideSettingsModalOpen(false);
    setEditableSlideOrder([]);
    setLayouts({});
    setSlideGlobalTextColorHex(SLIDE_DEFAULT_TEXT_COLOR_HEX);
    setSlideGlobalBackgroundColorHex(SLIDE_DEFAULT_BG_COLOR_HEX);
    savedClipboardImageUrlsRef.current = new Set();
    setSlideWebcamDesired(false);
    setSlideWebcamError('');
    setSlideWebcamCaptureMode('camera');
    setSlideWebcamCameraDeviceId(null);
    setSlideWebcamDeviceList([]);
    setSlideWebcamSourceMenuOpen(false);
    if (isRegistryMode) {
      const sid = registryWorkspaceId;
      if (!sid) {
        setLoadError(`워크스페이스 정보가 없어 슬라이드 ${registryNoun} 등록을 열 수 없습니다.`);
        setExtraPageCount(1);
        return;
      }
      const cached = isLayoutRegistryMode
        ? loadProgramSlideRegisteredLayouts(sid)
        : loadProgramSlideRegisteredPages(sid);
      const cachedLimited = cached.slice(0, registryMaxCount);
      if (cachedLimited.length > 0) {
        const cachedState = buildRegistryEditorStateFromPages(cachedLimited);
        if (isLayoutRegistryMode) {
          setRegisteredLayouts(cached);
        } else {
          setRegisteredPages(cached);
        }
        setRegistryPageNamesByIndex(cachedState.names);
        setRegistryPageIdsByIndex(cachedState.ids);
        setLayouts(cachedState.layouts);
        {
          const defaults = deriveSlideGlobalDefaultsFromLayouts(cachedState.layouts);
          setSlideGlobalTextColorHex(defaults.textColorHex);
          setSlideGlobalBackgroundColorHex(defaults.backgroundColorHex);
        }
        setEditableSlideOrder(cachedState.order);
        setExtraPageCount(cachedState.extraPageCount);
        setSelectedEditablePageIndex(0);
        setHasUnsavedSlideDraft(false);
      } else {
        if (isLayoutRegistryMode) {
          setRegisteredLayouts([]);
        } else {
          setRegisteredPages([]);
        }
        setRegistryPageNamesByIndex({});
        setRegistryPageIdsByIndex({});
        setLayouts({});
        setEditableSlideOrder([]);
        setExtraPageCount(1);
        setSelectedEditablePageIndex(0);
        setHasUnsavedSlideDraft(false);
      }
      let cancelled = false;
      void (async () => {
        try {
          const pagesRaw = isLayoutRegistryMode
            ? await getProgramSlideRegisteredLayouts(sid)
            : await getProgramSlideRegisteredPages(sid);
          const pages = pagesRaw.slice(0, registryMaxCount);
          if (cancelled) return;
          if (isLayoutRegistryMode) {
            setRegisteredLayouts(pages);
          } else {
            setRegisteredPages(pages);
          }
          if (pages.length > 0) {
            const nextState = buildRegistryEditorStateFromPages(pages);
            setRegistryPageNamesByIndex(nextState.names);
            setRegistryPageIdsByIndex(nextState.ids);
            setLayouts(nextState.layouts);
            {
              const defaults = deriveSlideGlobalDefaultsFromLayouts(nextState.layouts);
              setSlideGlobalTextColorHex(defaults.textColorHex);
              setSlideGlobalBackgroundColorHex(defaults.backgroundColorHex);
            }
            setEditableSlideOrder(nextState.order);
            setExtraPageCount(nextState.extraPageCount);
            setSelectedEditablePageIndex(0);
          }
          setHasUnsavedSlideDraft(false);
        } catch (e) {
          if (!cancelled && cachedLimited.length === 0) {
            setLoadError(
              e instanceof Error ? e.message : `슬라이드 ${registryNoun} 등록을 불러오지 못했습니다.`
            );
          }
        }
      })();
      return () => {
        cancelled = true;
      };
    }
    if (!p) {
      setExtraPageCount(1);
      return;
    }
    const previewLayouts = aiPreviewLayoutsFromPayload(p.aiPreviewLayouts);
    const minExtra = previewLayouts.length || normalizeExtraMemoBodies(p.extraSlideBodies).length;
    const initialContentPageCount = Math.max(1, minExtra);
    const initialMap: Record<number, ProgramSlideLayoutV1> = {};
    previewLayouts.forEach((layout, idx) => {
      initialMap[idx] = layout;
    });
    const initialSeeded = seedMinimumPresentationContentPages(initialMap, initialContentPageCount);
    setExtraPageCount(initialSeeded.pageCount);
    setLayouts(initialSeeded.layouts);
    setEditableSlideOrder(defaultPresentationContentPageOrder(initialSeeded.pageCount));
    const previewHoldId = previewLayouts.length ? Date.now() + Math.random() : 0;
    if (previewHoldId) {
      aiPreviewHoldRef.current = previewHoldId;
      setHasUnsavedSlideDraft(true);
    }

    let cancelled = false;
    if (p.workspaceId.trim()) {
      const ownerRef = p.owner;
      const sid = p.workspaceId.trim();
      void (async () => {
        try {
          if (previewLayouts.length) {
            loadedSlideVersionRef.current = null;
            if (ownerRef) {
              const rows = await fetchProgramContentSlidePages(sid, ownerRef);
              if (cancelled || aiPreviewHoldRef.current !== previewHoldId) return;
              loadedSlideVersionRef.current = {
                ownerKey: programContentSlidePresenceKey(ownerRef),
                signature: buildSlideRowsUpdatedAtSignature(rows),
              };
            }
            if (cancelled || aiPreviewHoldRef.current !== previewHoldId) return;
            savedClipboardImageUrlsRef.current = collectSlideImageStorageUrlsFromLayouts(initialSeeded.layouts);
            setHasUnsavedSlideDraft(true);
            return;
          }
          const next: Record<number, ProgramSlideLayoutV1> = {};
          let maxIdx = -1;
          loadedSlideVersionRef.current = null;
          if (ownerRef) {
            const rows = await fetchProgramContentSlidePages(sid, ownerRef);
            if (cancelled) return;
            loadedSlideVersionRef.current = {
              ownerKey: programContentSlidePresenceKey(ownerRef),
              signature: buildSlideRowsUpdatedAtSignature(rows),
            };
            for (const row of rows) {
              next[row.page_index] = layoutFromDbRow(row);
              maxIdx = Math.max(maxIdx, row.page_index);
            }
          }
          setHasUnsavedSlideDraft(false);
          if (maxIdx < 0 && ownerRef) {
            const clonedDraftLayouts = await materializePendingProgramContentSlideClone(sid, ownerRef);
            if (cancelled) return;
            setHasUnsavedSlideDraft(Object.keys(clonedDraftLayouts).length > 0);
            Object.entries(clonedDraftLayouts).forEach(([idxRaw, layout]) => {
              const idx = Number(idxRaw);
              if (!Number.isFinite(idx) || idx < 0) return;
              next[idx] = layout;
              maxIdx = Math.max(maxIdx, idx);
            });
          }
          // 통합 저장 전 로컬 pending 초안(더티)을 병합해, 다시 열어도 미저장 편집이 보이도록 한다.
          // 첫 저장 때 새로 만든 presentation_ref 초안은 owner 후보로 못 찾으므로 annualCellKey로도 매칭.
          const pendingDraft = findPendingProgramContentSlideDraftForOpen({
            workspaceId: sid,
            owner: ownerRef,
            learningTextOwner: p.learningTextOwner,
            annualCellKey: p.annualCellKey,
            scopeId: p.scopeId,
          });
          if (pendingDraft && Object.keys(pendingDraft.layouts).length > 0) {
            Object.entries(pendingDraft.layouts).forEach(([idxRaw, layout]) => {
              const idx = Number(idxRaw);
              if (!Number.isFinite(idx) || idx < 0) return;
              next[idx] = layout;
              maxIdx = Math.max(maxIdx, idx);
            });
            setHasUnsavedSlideDraft(true);
            // 저장이 이어지는 owner를 초안 owner로 맞춘다(중복 ref 생성 방지 위해 createPresentationRefOnSave 해제).
            const draftOwner = pendingDraft.owner;
            const sameOwner =
              ownerRef && programContentSlidePresenceKey(ownerRef) === programContentSlidePresenceKey(draftOwner);
            if (!sameOwner) {
              updateProgramContentSlidePayloadOwner(k, draftOwner);
              setPayload((prev) =>
                prev ? { ...prev, owner: draftOwner, createPresentationRefOnSave: undefined } : prev
              );
            }
          }
          const contentPageCount = Math.max(1, maxIdx + 1);
          const seeded = seedMinimumPresentationContentPages(next, contentPageCount);
          setLayouts(seeded.layouts);
          {
            const defaults = deriveSlideGlobalDefaultsFromLayouts(seeded.layouts);
            setSlideGlobalTextColorHex(defaults.textColorHex);
            setSlideGlobalBackgroundColorHex(defaults.backgroundColorHex);
          }
          savedClipboardImageUrlsRef.current = collectSlideImageStorageUrlsFromLayouts(seeded.layouts);
          setExtraPageCount(seeded.pageCount);
          setEditableSlideOrder(defaultPresentationContentPageOrder(seeded.pageCount));
        } catch (e) {
          if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e));
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [clearDeleteSlideHold, isLayoutRegistryMode, isRegistryMode, k, registryMaxCount, registryNoun, registryWorkspaceId]);

  React.useEffect(() => {
    const onRemap = () => {
      const p = readProgramContentSlidePayload(k);
      if (p) setPayload(p);
    };
    const onStorage = (e: StorageEvent) => {
      if (!e.key) return;
      if (e.key === 'cp:program-workspace:v1' || e.key.startsWith('cp.programContentSlide.v1:')) {
        onRemap();
      }
    };
    window.addEventListener(PROGRAM_CONTENT_SLIDE_OWNER_REMAPPED_EVENT, onRemap);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(PROGRAM_CONTENT_SLIDE_OWNER_REMAPPED_EVENT, onRemap);
      window.removeEventListener('storage', onStorage);
    };
  }, [k]);

  React.useEffect(() => {
    const sid = isRegistryMode ? registryWorkspaceId : payload?.workspaceId?.trim() ?? '';
    if (!sid) {
      setRegisteredPages([]);
      setRegisteredLayouts([]);
      return;
    }
    // 이벤트/스토리지 동기화는 LS만 읽는다.
    // (예전: CHANGED → forceRefresh GET → save+emit → CHANGED … 초당 수십 회 이그레스 루프)
    const syncPagesFromLocal = () => {
      setRegisteredPages(loadProgramSlideRegisteredPages(sid));
    };
    const syncLayoutsFromLocal = () => {
      setRegisteredLayouts(loadProgramSlideRegisteredLayouts(sid));
    };
    void getProgramSlideRegisteredPages(sid)
      .then((pages) => setRegisteredPages(pages))
      .catch(() => syncPagesFromLocal());
    void getProgramSlideRegisteredLayouts(sid)
      .then((layouts) => setRegisteredLayouts(layouts))
      .catch(() => syncLayoutsFromLocal());
    const onPagesChanged = (e: Event) => {
      const evt = e as CustomEvent<{ workspaceId?: string }>;
      const changedWs = (evt.detail?.workspaceId ?? '').trim();
      if (changedWs && changedWs !== sid) return;
      syncPagesFromLocal();
    };
    const onLayoutsChanged = (e: Event) => {
      const evt = e as CustomEvent<{ workspaceId?: string }>;
      const changedWs = (evt.detail?.workspaceId ?? '').trim();
      if (changedWs && changedWs !== sid) return;
      syncLayoutsFromLocal();
    };
    const onStorage = (e: StorageEvent) => {
      if (!e.key?.startsWith('cp.programSlideRegisteredPages.v1:')) return;
      if (!e.key.endsWith(sid)) return;
      syncPagesFromLocal();
    };
    const onLayoutStorage = (e: StorageEvent) => {
      if (!e.key?.startsWith('cp.programSlideRegisteredLayouts.v1:')) return;
      if (!e.key.endsWith(sid)) return;
      syncLayoutsFromLocal();
    };
    window.addEventListener(PROGRAM_SLIDE_REGISTERED_PAGES_CHANGED_EVENT, onPagesChanged as EventListener);
    window.addEventListener(PROGRAM_SLIDE_REGISTERED_LAYOUTS_CHANGED_EVENT, onLayoutsChanged as EventListener);
    window.addEventListener('storage', onStorage);
    window.addEventListener('storage', onLayoutStorage);
    return () => {
      window.removeEventListener(PROGRAM_SLIDE_REGISTERED_PAGES_CHANGED_EVENT, onPagesChanged as EventListener);
      window.removeEventListener(PROGRAM_SLIDE_REGISTERED_LAYOUTS_CHANGED_EVENT, onLayoutsChanged as EventListener);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('storage', onLayoutStorage);
    };
  }, [isRegistryMode, payload?.workspaceId, registryWorkspaceId]);

  React.useEffect(() => {
    setLayoutPickerPageIndex(null);
    setLayoutPickerHoverId(undefined);
    if (!openedRegisteredPage) return;
    setOpenedRegisteredPage(null);
    if (registeredPageRestoreEditModeRef.current != null) {
      setEditMode(registeredPageRestoreEditModeRef.current);
      registeredPageRestoreEditModeRef.current = null;
    }
    // 현재 슬라이드 페이지를 넘기면 등록 페이지 미리보기는 자동 해제
  }, [selectedEditablePageIndex]);

  const totalSlides = isRegistryMode ? extraPageCount : 1 + extraPageCount;

  React.useEffect(() => {
    setEditableSlideOrder((prev) => {
      const next: number[] = [];
      const seen = new Set<number>();
      for (const value of prev) {
        if (value < 0 || value >= extraPageCount || seen.has(value)) continue;
        seen.add(value);
        next.push(value);
      }
      for (let index = 0; index < extraPageCount; index += 1) {
        if (seen.has(index)) continue;
        seen.add(index);
        next.push(index);
      }
      if (next.length === prev.length && next.every((value, index) => value === prev[index])) return prev;
      return next;
    });
  }, [extraPageCount]);

  React.useEffect(() => {
    setSelectedEditablePageIndex((prev) => {
      if (prev == null) return prev;
      if (prev >= 0 && prev < extraPageCount) return prev;
      return extraPageCount > 0 ? extraPageCount - 1 : null;
    });
  }, [extraPageCount]);

  React.useEffect(() => () => clearDeleteSlideHold(), [clearDeleteSlideHold]);

  const handleCanvasLayoutChange = React.useCallback((pageIndex: number, next: ProgramSlideLayoutV1) => {
    setLayouts((prev) => {
      const prevLayout = prev[pageIndex] ?? EMPTY_SLIDE_LAYOUT;
      const preservedAppliedLayoutId =
        (next.appliedLayoutTemplateId ?? '').trim() ||
        (prevLayout.appliedLayoutTemplateId ?? '').trim() ||
        '';
      const preservedDefaultTextColorHex = normalizeColorInputHex(
        (next.defaultTextColorHex ?? '').trim() ||
          (prevLayout.defaultTextColorHex ?? '').trim() ||
          slideGlobalTextColorHex,
        SLIDE_DEFAULT_TEXT_COLOR_HEX
      );
      const preservedDefaultBackgroundColorHex = normalizeColorInputHex(
        (next.defaultBackgroundColorHex ?? '').trim() ||
          (prevLayout.defaultBackgroundColorHex ?? '').trim() ||
          slideGlobalBackgroundColorHex,
        SLIDE_DEFAULT_BG_COLOR_HEX
      );
      const rawPageBackgroundColorHex =
        (next.pageBackgroundColorHex ?? '').trim() || (prevLayout.pageBackgroundColorHex ?? '').trim();
      const pageBackgroundColorHex = rawPageBackgroundColorHex
        ? normalizeColorInputHex(rawPageBackgroundColorHex, SLIDE_DEFAULT_BG_COLOR_HEX)
        : undefined;
      const rawPageTextColorHex =
        (next.pageTextColorHex ?? '').trim() || (prevLayout.pageTextColorHex ?? '').trim();
      const pageTextColorHex = rawPageTextColorHex
        ? normalizeColorInputHex(rawPageTextColorHex, SLIDE_DEFAULT_TEXT_COLOR_HEX)
        : undefined;
      return {
        ...prev,
        [pageIndex]: {
          ...next,
          appliedLayoutTemplateId: preservedAppliedLayoutId || undefined,
          defaultTextColorHex: preservedDefaultTextColorHex,
          defaultBackgroundColorHex: preservedDefaultBackgroundColorHex,
          pageBackgroundColorHex,
          pageTextColorHex,
        },
      };
    });
    setHasUnsavedSlideDraft(true);
  }, [slideGlobalBackgroundColorHex, slideGlobalTextColorHex]);

  const goPrev = React.useCallback(() => {
    if (isCommunicationView) {
      setIsCommunicationView(false);
      if (editableSlideOrder.length === 0) {
        setSelectedEditablePageIndex(isRegistryMode ? 0 : null);
        return;
      }
      setSelectedEditablePageIndex(editableSlideOrder[editableSlideOrder.length - 1] ?? null);
      return;
    }
    setSelectedEditablePageIndex((current) => {
      if (editableSlideOrder.length === 0) return isRegistryMode ? 0 : null;
      if (current == null) return isRegistryMode ? editableSlideOrder[0] ?? 0 : null;
      const currentOrderIndex = editableSlideOrder.indexOf(current);
      if (currentOrderIndex <= 0) return isRegistryMode ? editableSlideOrder[0] ?? 0 : null;
      return editableSlideOrder[currentOrderIndex - 1] ?? (isRegistryMode ? editableSlideOrder[0] ?? 0 : null);
    });
  }, [editableSlideOrder, isCommunicationView, isRegistryMode]);

  const goNext = React.useCallback(() => {
    if (isCommunicationView) return;
    if (editableSlideOrder.length === 0) {
      if (!isRegistryMode) setIsCommunicationView(true);
      else setSelectedEditablePageIndex(0);
      return;
    }
    if (selectedEditablePageIndex == null) {
      setSelectedEditablePageIndex(editableSlideOrder[0] ?? (isRegistryMode ? 0 : null));
      return;
    }
    const currentOrderIndex = editableSlideOrder.indexOf(selectedEditablePageIndex);
    if (currentOrderIndex < 0) {
      setSelectedEditablePageIndex(editableSlideOrder[0] ?? (isRegistryMode ? 0 : null));
      return;
    }
    if (currentOrderIndex >= editableSlideOrder.length - 1) {
      if (!isRegistryMode) setIsCommunicationView(true);
      return;
    }
    setSelectedEditablePageIndex(editableSlideOrder[currentOrderIndex + 1] ?? selectedEditablePageIndex);
  }, [editableSlideOrder, isCommunicationView, isRegistryMode, selectedEditablePageIndex]);

  const openCommunicationView = React.useCallback(() => {
    if (isRegistryMode) return;
    setIsCommunicationView(true);
  }, [isRegistryMode]);

  const openSlidePage = React.useCallback((pageIndex: number | null) => {
    setIsCommunicationView(false);
    setSelectedEditablePageIndex(pageIndex);
  }, []);

  /** 튜토리얼 advanceWhenSelectorExists용 — 질문 페이지 진입 여부를 DOM 메타로 노출 */
  React.useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    if (isCommunicationView && !isRegistryMode) {
      root.setAttribute('data-tutorial-communication', '1');
    } else {
      root.removeAttribute('data-tutorial-communication');
    }
    return () => {
      root.removeAttribute('data-tutorial-communication');
    };
  }, [isCommunicationView, isRegistryMode]);

  const deleteSelectedSlide = React.useCallback(() => {
    if (selectedEditablePageIndex == null || extraPageCount <= 0) return;

    if (extraPageCount === 1) {
      const pageIndex = selectedEditablePageIndex;
      const emptyShell: ProgramSlideLayoutV1 = {
        ...EMPTY_SLIDE_LAYOUT,
        defaultTextColorHex: slideGlobalTextColorHex,
        defaultBackgroundColorHex: slideGlobalBackgroundColorHex,
      };
      setLayouts({ [pageIndex]: emptyShell });
      setWebcamDrawStrokesByPage((prev) => {
        const next = { ...prev };
        delete next[contentDrawPageKey(pageIndex)];
        return next;
      });
      setBoardDrawStrokesByPage((prev) => {
        const next = { ...prev };
        delete next[contentDrawPageKey(pageIndex)];
        return next;
      });
      setEditableSlideOrder([pageIndex]);
      setSelectedEditablePageIndex(pageIndex);
      setHasUnsavedSlideDraft(true);
      setDraggingSlideId(null);
      return;
    }

    const removedPageIndex = selectedEditablePageIndex;
    const selectedOrderIndex = editableSlideOrder.indexOf(removedPageIndex);
    const remainingOrder = editableSlideOrder.filter((pageIndex) => pageIndex !== removedPageIndex);
    const normalizePageIndex = (pageIndex: number) => (pageIndex > removedPageIndex ? pageIndex - 1 : pageIndex);
    const nextSelectedOldPageIndex =
      selectedOrderIndex >= 0
        ? remainingOrder[Math.min(selectedOrderIndex, remainingOrder.length - 1)] ?? null
        : remainingOrder[0] ?? null;

    const nextLayouts: Record<number, ProgramSlideLayoutV1> = {};
    Object.entries(layouts).forEach(([pageIndexRaw, layout]) => {
      const pageIndex = Number(pageIndexRaw);
      if (!Number.isFinite(pageIndex) || pageIndex === removedPageIndex) return;
      nextLayouts[normalizePageIndex(pageIndex)] = cloneSlideLayout(layout);
    });

    setLayouts(nextLayouts);
    setWebcamDrawStrokesByPage((prev) => remapDrawStrokesByPage(prev, removedPageIndex));
    setBoardDrawStrokesByPage((prev) => remapDrawStrokesByPage(prev, removedPageIndex));
    if (isRegistryMode) {
      setRegistryPageNamesByIndex((prevNames) => {
        const next: Record<number, string> = {};
        Object.entries(prevNames).forEach(([idxRaw, name]) => {
          const idx = Number(idxRaw);
          if (!Number.isFinite(idx) || idx === removedPageIndex) return;
          next[normalizePageIndex(idx)] = name;
        });
        return next;
      });
      setRegistryPageIdsByIndex((prevIds) => {
        const next: Record<number, string> = {};
        Object.entries(prevIds).forEach(([idxRaw, id]) => {
          const idx = Number(idxRaw);
          if (!Number.isFinite(idx) || idx === removedPageIndex) return;
          next[normalizePageIndex(idx)] = id;
        });
        return next;
      });
    }
    const nextOrder = remainingOrder.map(normalizePageIndex);
    setEditableSlideOrder(nextOrder);
    setSelectedEditablePageIndex(
      nextOrder.length === 0
        ? null
        : nextSelectedOldPageIndex == null
          ? nextOrder[0] ?? null
          : normalizePageIndex(nextSelectedOldPageIndex)
    );
    setExtraPageCount((prev) => Math.max(1, prev - 1));
    setHasUnsavedSlideDraft(true);
    setDraggingSlideId(null);
  }, [
    editableSlideOrder,
    extraPageCount,
    isRegistryMode,
    layouts,
    selectedEditablePageIndex,
    slideGlobalBackgroundColorHex,
    slideGlobalTextColorHex,
  ]);

  const cloneWebcamDrawStrokes = React.useCallback((strokes: WebcamDrawStroke[]): WebcamDrawStroke[] => {
    return strokes.map((stroke, i) => ({
      ...stroke,
      id: `wd-${Date.now()}-${i}`,
      points: stroke.points.map((p) => ({ ...p })),
    }));
  }, []);

  const copySelectedSlidePage = React.useCallback(() => {
    const pageIndex = selectedEditablePageIndexRef.current;
    if (pageIndex == null || !editMode) return;
    const layout = layoutsRef.current[pageIndex] ?? EMPTY_SLIDE_LAYOUT;
    slideBlockClipboardActiveRef.current = false;
    slidePageClipboardRef.current = {
      layout: cloneSlideLayout(layout),
      webcamDrawStrokes: cloneWebcamDrawStrokes(
        webcamDrawStrokesByPageRef.current[contentDrawPageKey(pageIndex)] ?? []
      ),
    };
  }, [cloneWebcamDrawStrokes, editMode]);

  const pasteDuplicatedSlidePage = React.useCallback(() => {
    const clip = slidePageClipboardRef.current;
    if (!clip || !editMode) return;
    if (isRegistryMode && extraPageCountRef.current >= registryMaxCount) return;

    const newPageIndex = extraPageCountRef.current;
    const sel = selectedEditablePageIndexRef.current;
    const layout = duplicateSlideLayoutWithFreshIds(clip.layout);

    setLayouts((m) => ({ ...m, [newPageIndex]: layout }));
    if (clip.webcamDrawStrokes.length) {
      setWebcamDrawStrokesByPage((w) => ({
        ...w,
        [contentDrawPageKey(newPageIndex)]: cloneWebcamDrawStrokes(clip.webcamDrawStrokes),
      }));
    }
    setEditableSlideOrder((order) => {
      const base = order.filter((pageIndex) => pageIndex !== newPageIndex);
      if (sel == null) return [...base, newPageIndex];
      const pos = base.indexOf(sel);
      if (pos < 0) return [...base, newPageIndex];
      const next = [...base];
      next.splice(pos + 1, 0, newPageIndex);
      return next;
    });
    setSelectedEditablePageIndex(newPageIndex);
    setExtraPageCount((c) => c + 1);
    setHasUnsavedSlideDraft(true);
  }, [cloneWebcamDrawStrokes, editMode, isRegistryMode, registryMaxCount]);

  const canDeleteSelectedSlide = editMode && selectedEditablePageIndex != null && extraPageCount > 0;
  const canAddSlide =
    editMode && (!isRegistryMode || extraPageCount < registryMaxCount);


  const slideDragSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    })
  );

  const handleSlideDragStart = React.useCallback((event: DragStartEvent) => {
    const activePageIndex = parseSlideSortableId(String(event.active.id));
    setDraggingSlideId(String(event.active.id));
    if (activePageIndex != null) setSelectedEditablePageIndex(activePageIndex);
  }, []);

  const handleSlideDragOver = React.useCallback((event: DragOverEvent) => {
    const activePageIndex = parseSlideSortableId(String(event.active.id));
    const overPageIndex = parseSlideSortableId(String(event.over?.id ?? ''));
    if (activePageIndex == null || overPageIndex == null || activePageIndex === overPageIndex) return;
    setEditableSlideOrder((prevOrder) => {
      const fromIndex = prevOrder.indexOf(activePageIndex);
      const toIndex = prevOrder.indexOf(overPageIndex);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return prevOrder;
      const nextOrder = arrayMove(prevOrder, fromIndex, toIndex);
      setHasUnsavedSlideDraft(true);
      return nextOrder;
    });
  }, []);

  const handleSlideDragEnd = React.useCallback((event: DragEndEvent) => {
    const activePageIndex = parseSlideSortableId(String(event.active.id));
    const overPageIndex = parseSlideSortableId(String(event.over?.id ?? ''));
    setDraggingSlideId(null);
    if (activePageIndex == null || overPageIndex == null || activePageIndex === overPageIndex) return;
    setHasUnsavedSlideDraft(true);
  }, []);

  const saveSlidesToServer = React.useCallback(async () => {
    if (isRegistryMode) {
      const sid = registryWorkspaceId.trim();
      if (!sid) {
        setSlideSaveMessage('워크스페이스 정보가 없어 저장할 수 없습니다.');
        return;
      }
      setSavingSlides(true);
      setSlideSaveMessage('');
      try {
        const orderForSave =
          editableSlideOrder.length === extraPageCount
            ? editableSlideOrder
            : Array.from({ length: extraPageCount }, (_, idx) => idx);
        const pages: ProgramSlideRegisteredPage[] = [];
        for (let idx = 0; idx < extraPageCount; idx += 1) {
          const sourcePageIndex = orderForSave[idx] ?? idx;
          const id = (registryPageIdsByIndex[sourcePageIndex] ?? '').trim() || createPresentationRefId();
          const fallbackName = programSlideRegistryFallbackName(idx);
          const name = normalizeProgramSlideRegistryName(
            registryPageNamesByIndex[sourcePageIndex] ?? '',
            fallbackName
          );
          pages.push({
            id,
            name,
            layout: await finalizePendingSlideImagesInLayout(
              cloneSlideLayout(layouts[sourcePageIndex] ?? EMPTY_SLIDE_LAYOUT)
            ),
            order: idx,
            updatedAt: Date.now(),
          });
        }
        const saved = isLayoutRegistryMode
          ? await replaceProgramSlideRegisteredLayouts(sid, pages)
          : await replaceProgramSlideRegisteredPages(sid, pages);
        if (isLayoutRegistryMode) {
          setRegisteredLayouts(saved);
        } else {
          setRegisteredPages(saved);
        }
        setRegistryPageIdsByIndex(
          Object.fromEntries(saved.map((page, idx) => [idx, page.id]))
        );
        setRegistryPageNamesByIndex(
          Object.fromEntries(saved.map((page, idx) => [idx, page.name]))
        );
        setHasUnsavedSlideDraft(false);
        setSlideSaveMessage(`슬라이드 ${registryNoun} 등록이 저장되었습니다.`);
        window.setTimeout(() => setSlideSaveMessage(''), 1800);
      } catch (e) {
        setSlideSaveMessage(
          e instanceof Error ? e.message : `슬라이드 ${registryNoun} 등록 저장에 실패했습니다.`
        );
      } finally {
        setSavingSlides(false);
      }
      return;
    }

    const p = readProgramContentSlidePayload(k);
    if ((!p?.owner && !p?.createPresentationRefOnSave) || !p?.workspaceId.trim() || !p.scopeId?.trim()) {
      setSlideSaveMessage('저장할 수 있는 프레젠테이션 연결 정보가 없습니다.');
      return;
    }
    setSlideSaveMessage('');
    try {
      const coSave = await confirmAndSaveDirtyProgramWorkspaceBeforeAttachment({
        scopeId: p.scopeId,
        workspaceId: p.workspaceId,
        gradeId: p.contextGradeId || p.createPresentationRefOnSave?.gradeId,
        academicYear: p.academicYear,
        termNumber: p.termNumber,
        attachmentKind: 'slide',
      });
      if (coSave === 'cancel') return;
    } catch (e) {
      setSlideSaveMessage(
        e instanceof Error ? e.message : '프로그램 표 저장에 실패했습니다. 슬라이드를 저장하지 않았습니다.'
      );
      return;
    }
    setSavingSlides(true);
    try {
      const sid = p.workspaceId.trim();
      const orderForSave =
        editableSlideOrder.length === extraPageCount
          ? editableSlideOrder
          : Array.from({ length: extraPageCount }, (_, idx) => idx);
      const layoutsToPersist: Record<number, ProgramSlideLayoutV1> = {};
      const savedLayoutBySourcePage = new Map<number, ProgramSlideLayoutV1>();
      const persistIdxToSourcePage = new Map<number, number>();
      const emptyShellLayout: ProgramSlideLayoutV1 = {
        ...EMPTY_SLIDE_LAYOUT,
        defaultTextColorHex: slideGlobalTextColorHex,
        defaultBackgroundColorHex: slideGlobalBackgroundColorHex,
      };
      let persistIdx = 0;
      for (let idx = 0; idx < extraPageCount; idx += 1) {
        const sourcePageIndex = orderForSave[idx] ?? idx;
        const draft = cloneSlideLayout(layouts[sourcePageIndex] ?? EMPTY_SLIDE_LAYOUT);
        if (!slideLayoutHasMeaningfulCanvasContent(draft)) continue;
        layoutsToPersist[persistIdx] = draft;
        savedLayoutBySourcePage.set(sourcePageIndex, draft);
        persistIdxToSourcePage.set(persistIdx, sourcePageIndex);
        persistIdx += 1;
      }
      const hasMeaningfulSlides = persistIdx > 0;

      let owner = p.owner;
      let previousDraftOwner: ProgramContentSlideOwnerRef | null = null;
      const loadedVersion = loadedSlideVersionRef.current;
      if (owner && loadedVersion && loadedVersion.ownerKey === programContentSlidePresenceKey(owner)) {
        const remoteSignature = await fetchProgramContentSlideRemoteSignature(sid, owner);
        if (
          remoteSignature !== loadedVersion.signature &&
          !window.confirm(
            '이 슬라이드를 연 뒤 다른 탭·기기에서 먼저 저장된 내용이 있습니다.\n\n' +
              '[확인] 지금 화면 내용으로 덮어쓰기\n[취소] 저장하지 않기 (새로고침해서 최신 내용 확인)'
          )
        ) {
          setSlideSaveMessage('다른 곳에서 저장된 내용이 있어 저장하지 않았습니다. 새로고침 후 확인해 주세요.');
          return;
        }
      }
      console.info('[program-slide] save requested', {
        owner,
        hasMeaningfulSlides,
        hasCreatePresentationRefOnSave: !!p.createPresentationRefOnSave,
        annualKey: p.createPresentationRefOnSave?.annualKey ?? '',
        scopeId: p.scopeId ?? '',
      });
      let learningPatch: ProgramSlideLearningTextPatch | undefined;
      if (
        hasMeaningfulSlides &&
        (!owner || owner.kind !== 'presentation_ref') &&
        p.createPresentationRefOnSave
      ) {
        if (await findExistingPresentationRefOnSourceOwner({ workspaceId: sid, sourceOwner: owner })) {
          throw new Error(PRESENTATION_REF_CONFLICT_MESSAGE);
        }
        previousDraftOwner = owner;
        const presentationRefId = createPresentationRefId();
        const nextLearningText = learningTextWithPresentationRef(
          p.createPresentationRefOnSave.learningText,
          presentationRefId
        );
        console.info('[program-slide] generated presentationRefId', {
          presentationRefId,
          previousOwner: previousDraftOwner,
          annualKey: p.createPresentationRefOnSave.annualKey,
        });
        let ownerForProgramTableNotify: ProgramContentSlideOwnerRef | null = previousDraftOwner;
        if (owner) {
          if (owner.kind === 'program_cell' || owner.kind === 'fixed_row' || owner.kind === 'scoped_row') {
            learningPatch = {
              kind: owner.kind,
              id: owner.id.trim(),
              learningText: nextLearningText,
            };
            /**
             * 플로팅 저장이 곧 DB 반영이므로, 원본 행의 presentation_ref_id도 함께 즉시 기록한다.
             * (learningPatch의 learning_text는 아래 persist bundle에서 같이 반영)
             */
            await persistPresentationRefIdToSourceOwner({
              workspaceId: sid,
              sourceOwner: owner,
              presentationRefId,
            });
          }
        } else {
          ownerForProgramTableNotify = await createProgramContentOwnerForPresentationRef(p, nextLearningText);
        }
        /** 프로그램 표는 `storage`/커스텀 이벤트로만 학습내용을 갱신한다. 행은 DB owner id로만 찾는다. */
        notifyProgramContentSlideLearningUpdated({
          workspaceId: sid,
          scopeId: p.scopeId,
          /** 표 초안 패치 시 행 식별 실패(특히 presentation_ref 직후) 방지 — DB와 동일 키 */
          annualKey: (p.annualCellKey ?? '').trim() || undefined,
          learningText: nextLearningText,
          presentationRefId,
          owner: ownerForProgramTableNotify,
        });
        owner = { kind: 'presentation_ref', id: presentationRefId };
        updateProgramContentSlidePayloadOwner(k, owner);
        setPayload((prev) =>
          prev
            ? {
                ...prev,
                owner,
                createPresentationRefOnSave: undefined,
              }
            : prev
        );
      }
      if (!owner) throw new Error('저장할 수 있는 프레젠테이션 연결 정보가 없습니다.');
      if (previousDraftOwner) {
        discardPendingProgramContentSlideDraftForOwner(sid, previousDraftOwner);
      }

      const scopeId = p.scopeId.trim();
      if (!hasMeaningfulSlides) {
        const presentationRefId =
          (owner.kind === 'presentation_ref' ? owner.id.trim() : '') ||
          (p.bindingLearningText
            ? resolvePresentationRefIdFromLearningText(p.bindingLearningText)
            : '') ||
          (p.createPresentationRefOnSave?.learningText
            ? resolvePresentationRefIdFromLearningText(p.createPresentationRefOnSave.learningText)
            : '');
        const sourceOwner =
          p.learningTextOwner?.id?.trim() && p.learningTextOwner.kind !== 'presentation_ref'
            ? p.learningTextOwner
            : owner.kind !== 'presentation_ref'
              ? owner
              : null;
        await deleteProgramContentSlidePagesForOwner(sid, owner);
        await clearPresentationRefBindingAfterEmptySlideSave({
          workspaceId: sid,
          presentationRefId: presentationRefId || undefined,
          sourceOwner,
          bindingLearningText: p.bindingLearningText ?? p.createPresentationRefOnSave?.learningText,
          academicYear: p.academicYear,
          termNumber: p.termNumber,
          scopeId,
          annualCellKey: p.annualCellKey,
          contextGradeId: p.contextGradeId,
          createPresentationRefOnSave: p.createPresentationRefOnSave,
        });
        discardPendingProgramContentSlideDraftForOwner(sid, owner);
        notifyProgramContentSlideLearningUpdated({
          workspaceId: sid,
          scopeId,
          annualKey: (p.annualCellKey ?? '').trim() || undefined,
          learningText:
            learningTextStorageWithoutPresentationRef(
              p.bindingLearningText ?? p.createPresentationRefOnSave?.learningText ?? ''
            ) ?? '',
          owner: sourceOwner ?? owner,
        });
      } else {
        const persistedLayouts = await persistProgramContentSlideLayoutsForOwner(
          sid,
          owner,
          layoutsToPersist,
          learningPatch ?? null
        );
        persistIdxToSourcePage.forEach((sourcePageIndex, idx) => {
          const finalized = persistedLayouts[idx];
          if (finalized) {
            layoutsToPersist[idx] = finalized;
            savedLayoutBySourcePage.set(sourcePageIndex, finalized);
          }
        });
        discardPendingProgramContentSlideDraftForOwner(sid, owner);
        if (learningPatch?.learningText) {
          notifyProgramContentSlideLearningUpdated({
            workspaceId: sid,
            scopeId,
            annualKey: (p.annualCellKey ?? '').trim() || undefined,
            learningText: learningPatch.learningText,
            presentationRefId: owner.kind === 'presentation_ref' ? owner.id.trim() : undefined,
            owner:
              learningPatch.kind === 'program_cell' ||
              learningPatch.kind === 'fixed_row' ||
              learningPatch.kind === 'scoped_row'
                ? { kind: learningPatch.kind, id: learningPatch.id.trim() }
                : owner,
          });
        }
      }

      const nextClipboardUrls = collectSlideImageStorageUrlsFromLayouts(layoutsToPersist);
      await releaseRemovedSlideImageUrls(savedClipboardImageUrlsRef.current, nextClipboardUrls);
      savedClipboardImageUrlsRef.current = nextClipboardUrls;
      notifySlideImageStorageChanged();
      setLayouts(() => {
        const next: Record<number, ProgramSlideLayoutV1> = {};
        for (let idx = 0; idx < extraPageCount; idx += 1) {
          const sourcePageIndex = orderForSave[idx] ?? idx;
          const saved = savedLayoutBySourcePage.get(sourcePageIndex);
          next[sourcePageIndex] = cloneSlideLayout(saved ?? emptyShellLayout);
        }
        return next;
      });
      {
        const savedSignature = hasMeaningfulSlides ? getProgramContentSlideCachedSignature(sid, owner) : '0';
        loadedSlideVersionRef.current =
          savedSignature != null
            ? { ownerKey: programContentSlidePresenceKey(owner), signature: savedSignature }
            : null;
      }
      console.info('[program-slide] db save completed', { owner, hasMeaningfulSlides });
      aiPreviewHoldRef.current = 0;
      setHasUnsavedSlideDraft(false);
      setSlideSaveMessage(
        !hasMeaningfulSlides
          ? '슬라이드가 없어 프레젠테이션 연결이 해제되었습니다.'
          : '프레젠테이션이 저장되었습니다.'
      );
      window.setTimeout(() => setSlideSaveMessage(''), 1800);
    } catch (e) {
      setSlideSaveMessage(e instanceof Error ? e.message : '프레젠테이션 저장에 실패했습니다.');
    } finally {
      setSavingSlides(false);
    }
  }, [
    editableSlideOrder,
    extraPageCount,
    isLayoutRegistryMode,
    isRegistryMode,
    k,
    layouts,
    registryNoun,
    registryPageIdsByIndex,
    registryPageNamesByIndex,
    registryWorkspaceId,
    slideGlobalBackgroundColorHex,
    slideGlobalTextColorHex,
  ]);

  React.useEffect(
    () => () => {
      revokeAllPendingInLayouts(layoutsRef.current);
    },
    []
  );

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (slideCanvasHandleRef.current?.tryConsumeArrowKeyForSelection(e)) return;
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowUp') {
        if (isRegistryMode || isCommunicationView) return;
        e.preventDefault();
        openCommunicationView();
      } else if (e.key === 'ArrowDown') {
        if (!isCommunicationView) return;
        e.preventDefault();
        setIsCommunicationView(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, isCommunicationView, isRegistryMode, openCommunicationView]);

  React.useEffect(() => {
    scrollFooterSlideTabIntoView(
      footerDotsScrollRef.current,
      isCommunicationView ? 'communication' : footerSlideTabKey(selectedEditablePageIndex)
    );
  }, [selectedEditablePageIndex, editableSlideOrder.length, isRegistryMode, isCommunicationView]);

  React.useEffect(() => {
    const isTypingTarget = (target: EventTarget | null): boolean => {
      if (!target || !(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      return Boolean(target.isContentEditable || target.closest('[contenteditable="true"]'));
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (!editMode) return;
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key !== 'c' && key !== 'v') return;
      if (e.shiftKey) return;
      if (key === 'v' && e.repeat) return;
      if (isTypingTarget(e.target) || isTypingTarget(document.activeElement)) return;
      if (selectedEditablePageIndexRef.current == null) return;

      if (key === 'c') {
        if (slideCanvasHandleRef.current?.hasEffectiveBlockSelection()) {
          markSlideBlockClipboardActive();
          return;
        }
        e.preventDefault();
        copySelectedSlidePage();
        return;
      }
      if (slideBlockClipboardActiveRef.current) return;
      if (!slidePageClipboardRef.current) return;
      e.preventDefault();
      pasteDuplicatedSlidePage();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [copySelectedSlidePage, editMode, markSlideBlockClipboardActive, pasteDuplicatedSlidePage]);

  const activeEditableOrderIndex =
    selectedEditablePageIndex == null ? -1 : editableSlideOrder.indexOf(selectedEditablePageIndex);
  const measureSlideCanvasOverflow = React.useCallback(() => {
    if (isCommunicationView) {
      setSlideCanvasOverflowsX(false);
      setSlideCanvasFitWidth(false);
      setSlideCanvasFitScale(1);
      return;
    }
    const el = slideBodyScrollRef.current;
    const wrap = el?.parentElement;
    const layoutW = getLayoutViewportSize().width;
    const width =
      wrap && wrap.clientWidth > 0
        ? wrap.clientWidth
        : el && el.clientWidth > 0
          ? el.clientWidth
          : layoutW;
    const overflows = width > 0 && width < SLIDE_BODY_CANVAS_MIN_WIDTH - 0.5;
    setSlideCanvasOverflowsX(overflows);
    setSlideCanvasFitScale(
      overflows ? Math.max(0.15, Math.min(1, width / SLIDE_BODY_CANVAS_MIN_WIDTH)) : 1
    );
    if (!overflows) setSlideCanvasFitWidth(false);
  }, [isCommunicationView]);

  const bindSlideBodyScrollEl = React.useCallback(
    (node: HTMLDivElement | null) => {
      slideBodyScrollRoRef.current?.disconnect();
      slideBodyScrollRoRef.current = null;
      slideBodyScrollRef.current = node;
      if (!node) return;
      const ro = new ResizeObserver(() => measureSlideCanvasOverflow());
      slideBodyScrollRoRef.current = ro;
      ro.observe(node);
      measureSlideCanvasOverflow();
    },
    [measureSlideCanvasOverflow]
  );

  React.useLayoutEffect(() => {
    measureSlideCanvasOverflow();
    window.addEventListener('resize', measureSlideCanvasOverflow);
    window.visualViewport?.addEventListener('resize', measureSlideCanvasOverflow);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, measureSlideCanvasOverflow);
    return () => {
      window.removeEventListener('resize', measureSlideCanvasOverflow);
      window.visualViewport?.removeEventListener('resize', measureSlideCanvasOverflow);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, measureSlideCanvasOverflow);
    };
  }, [measureSlideCanvasOverflow]);
  const slideIndex = isCommunicationView
    ? totalSlides
    : isRegistryMode
      ? selectedEditablePageIndex == null
        ? 0
        : activeEditableOrderIndex >= 0
          ? activeEditableOrderIndex
          : Math.min(selectedEditablePageIndex, Math.max(0, totalSlides - 1))
      : selectedEditablePageIndex == null
        ? 0
        : activeEditableOrderIndex >= 0
          ? activeEditableOrderIndex + 1
          : Math.min(selectedEditablePageIndex + 1, totalSlides - 1);
  const pageIndexForCanvas = selectedEditablePageIndex ?? (isRegistryMode ? 0 : -1);
  const isTitleSlideView = !isRegistryMode && !isCommunicationView && slideIndex === 0;
  const webcamDrawStorageKey = resolvePresentationDrawPageKey({
    isRegistryMode,
    isCommunicationView,
    selectedEditablePageIndex,
  });
  const setWebcamDrawStrokesForPage = React.useCallback(
    (next: WebcamDrawStroke[]) => {
      if (webcamDrawStorageKey == null) return;
      setWebcamDrawStrokesByPage((prev) => ({ ...prev, [webcamDrawStorageKey]: next }));
    },
    [webcamDrawStorageKey]
  );
  const webcamDrawStrokes =
    webcamDrawStorageKey != null ? webcamDrawStrokesByPage[webcamDrawStorageKey] ?? [] : [];
  const showWebcamDraw = slideWebcamDesired && !slideWebcamLoading && webcamDrawStorageKey != null;
  const setBoardDrawStrokesForPage = React.useCallback(
    (next: WebcamDrawStroke[]) => {
      if (webcamDrawStorageKey == null) return;
      setBoardDrawStrokesByPage((prev) => ({ ...prev, [webcamDrawStorageKey]: next }));
    },
    [webcamDrawStorageKey]
  );
  const boardDrawStrokes =
    webcamDrawStorageKey != null ? boardDrawStrokesByPage[webcamDrawStorageKey] ?? [] : [];
  const showBoardDrawOverlay =
    !isRegistryMode && !slideWebcamDesired && webcamDrawStorageKey != null && slideBoardDrawOpen;
  const toggleSlideBoardDraw = React.useCallback(() => {
    setSlideBoardDrawOpen((open) => {
      if (open) {
        setBoardDrawStrokesByPage({});
        return false;
      }
      return true;
    });
  }, []);

  const {
    view: webcamView,
    controls: webcamViewControls,
    transformed: webcamViewTransformed,
    style: webcamViewStyle,
  } = useWebcamViewTransform(() => webcamStreamRef.current?.getVideoTracks()[0] ?? null);

  const handleWebcamDrawDownload = React.useCallback(
    async (layerEl: HTMLDivElement | null) => {
      if (!layerEl || slideWebcamLoading || !slideWebcamDesired) return;
      const rect = layerEl.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      try {
        await ensureWebcamFrozen();
        const canvas = await renderWebcamDrawCompositeAsync({
          width: rect.width,
          height: rect.height,
          captureMode: slideWebcamCaptureMode,
          screenVideo: screenVideoRef.current,
          webcamVideo: webcamVideoRef.current,
          frozenBlobUrl: frozenBlobUrlRef.current,
          strokes: webcamDrawStrokes,
          view: webcamView,
        });
        if (!canvas) {
          setSlideWebcamError('이미지를 만들 수 없습니다. 잠시 후 다시 시도해 주세요.');
          window.setTimeout(() => setSlideWebcamError(''), 4200);
          return;
        }
        const blob = await canvasToPngBlob(canvas);
        if (!blob) {
          setSlideWebcamError('PNG 저장에 실패했습니다.');
          window.setTimeout(() => setSlideWebcamError(''), 4200);
          return;
        }
        downloadPngBlob(blob, defaultWebcamDrawExportFilename());
      } catch {
        setSlideWebcamError('이미지 저장에 실패했습니다.');
        window.setTimeout(() => setSlideWebcamError(''), 4200);
      }
    },
    [
      ensureWebcamFrozen,
      slideWebcamCaptureMode,
      webcamDrawStrokes,
      webcamView,
      slideWebcamLoading,
      slideWebcamDesired,
    ]
  );
  const layoutForCanvas = React.useMemo(
    () => cloneSlideLayout(layouts[pageIndexForCanvas] ?? EMPTY_SLIDE_LAYOUT),
    [layouts, pageIndexForCanvas]
  );
  const effectiveGlobalTextColorHex = normalizeColorInputHex(
    layoutForCanvas.defaultTextColorHex,
    slideGlobalTextColorHex
  );
  const effectivePageTextColorHex = normalizeColorInputHex(
    layoutForCanvas.pageTextColorHex,
    effectiveGlobalTextColorHex
  );
  const effectiveGlobalBackgroundColorHex = normalizeColorInputHex(
    layoutForCanvas.defaultBackgroundColorHex,
    slideGlobalBackgroundColorHex
  );
  const effectivePageBackgroundColorHex = normalizeColorInputHex(
    layoutForCanvas.pageBackgroundColorHex,
    effectiveGlobalBackgroundColorHex
  );
  const registeredLayoutMap = React.useMemo(() => {
    const m = new Map<string, ProgramSlideRegisteredLayout>();
    for (const item of registeredLayouts) m.set(item.id, item);
    return m;
  }, [registeredLayouts]);
  const appliedLayoutIdForCanvas = (layoutForCanvas.appliedLayoutTemplateId ?? '').trim();
  const displayAppliedLayoutIdForCanvas = React.useMemo(() => {
    if (layoutPickerPageIndex != null && layoutPickerPageIndex === pageIndexForCanvas) {
      if (layoutPickerHoverId !== undefined) {
        return (layoutPickerHoverId ?? '').trim();
      }
      return layoutPickerOriginalId.trim();
    }
    return appliedLayoutIdForCanvas;
  }, [
    appliedLayoutIdForCanvas,
    layoutPickerHoverId,
    layoutPickerOriginalId,
    layoutPickerPageIndex,
    pageIndexForCanvas,
  ]);
  const appliedLayoutForCanvas = React.useMemo(() => {
    if (!displayAppliedLayoutIdForCanvas) return null;
    return registeredLayoutMap.get(displayAppliedLayoutIdForCanvas) ?? null;
  }, [displayAppliedLayoutIdForCanvas, registeredLayoutMap]);
  const appliedBackgroundLayoutForCanvas = React.useMemo(
    () => (appliedLayoutForCanvas ? cloneSlideLayout(appliedLayoutForCanvas.layout) : null),
    [appliedLayoutForCanvas]
  );
  const appliedLayoutCanvasBackgroundColorHex = React.useMemo(() => {
    if (!appliedBackgroundLayoutForCanvas) return effectivePageBackgroundColorHex;
    const layoutDefaultBg = normalizeColorInputHex(
      appliedBackgroundLayoutForCanvas.defaultBackgroundColorHex,
      slideGlobalBackgroundColorHex
    );
    return normalizeColorInputHex(
      appliedBackgroundLayoutForCanvas.pageBackgroundColorHex,
      layoutDefaultBg
    );
  }, [appliedBackgroundLayoutForCanvas, slideGlobalBackgroundColorHex]);
  const appliedLayoutCanvasTextColorHex = React.useMemo(() => {
    if (!appliedBackgroundLayoutForCanvas) return effectivePageTextColorHex;
    const layoutDefaultText = normalizeColorInputHex(
      appliedBackgroundLayoutForCanvas.defaultTextColorHex,
      slideGlobalTextColorHex
    );
    return normalizeColorInputHex(
      appliedBackgroundLayoutForCanvas.pageTextColorHex,
      layoutDefaultText
    );
  }, [appliedBackgroundLayoutForCanvas, effectivePageTextColorHex, slideGlobalTextColorHex]);
  const resolvedPageBackgroundColorHex = appliedBackgroundLayoutForCanvas
    ? appliedLayoutCanvasBackgroundColorHex
    : effectivePageBackgroundColorHex;
  const resolvedPageTextColorHex = appliedBackgroundLayoutForCanvas
    ? appliedLayoutCanvasTextColorHex
    : effectivePageTextColorHex;
  const openedRegisteredLayout = React.useMemo(
    () => (openedRegisteredPage ? cloneSlideLayout(openedRegisteredPage.layout) : null),
    [openedRegisteredPage]
  );
  const openedRegisteredPageBackgroundColorHex = React.useMemo(() => {
    if (!openedRegisteredLayout) return resolvedPageBackgroundColorHex;
    const defaultBg = normalizeColorInputHex(
      openedRegisteredLayout.defaultBackgroundColorHex,
      slideGlobalBackgroundColorHex
    );
    return normalizeColorInputHex(openedRegisteredLayout.pageBackgroundColorHex, defaultBg);
  }, [openedRegisteredLayout, resolvedPageBackgroundColorHex, slideGlobalBackgroundColorHex]);
  const openedRegisteredPageTextColorHex = React.useMemo(() => {
    if (!openedRegisteredLayout) return resolvedPageTextColorHex;
    const defaultText = normalizeColorInputHex(
      openedRegisteredLayout.defaultTextColorHex,
      slideGlobalTextColorHex
    );
    return normalizeColorInputHex(openedRegisteredLayout.pageTextColorHex, defaultText);
  }, [openedRegisteredLayout, resolvedPageTextColorHex, slideGlobalTextColorHex]);
  const displayPageBackgroundColorHex = openedRegisteredLayout
    ? openedRegisteredPageBackgroundColorHex
    : resolvedPageBackgroundColorHex;
  const displayPageTextColorHex = openedRegisteredLayout
    ? openedRegisteredPageTextColorHex
    : resolvedPageTextColorHex;
  const slideRootDefaultTextHex =
    isTitleSlideView && !openedRegisteredLayout
      ? normalizeColorInputHex(slideGlobalTextColorHex, SLIDE_DEFAULT_TEXT_COLOR_HEX)
      : displayPageTextColorHex;
  const isLayoutBackgroundPreviewing =
    layoutPickerPageIndex != null &&
    layoutPickerPageIndex === pageIndexForCanvas &&
    layoutPickerHoverId !== undefined;
  const layoutSandwichUnderClassName =
    'cp-program-slide-layout-sandwich-under' +
    (isLayoutBackgroundPreviewing ? ' cp-program-slide-layout-sandwich-under--preview' : '');

  const closeLayoutPicker = React.useCallback(() => {
    setLayoutPickerPageIndex(null);
    setLayoutPickerHoverId(undefined);
  }, []);

  const closeSlideSettingsModal = React.useCallback(() => {
    setSlideSettingsModalOpen(false);
  }, []);

  const closeRegistryNameModal = React.useCallback(() => {
    setRegistryNameModal(null);
  }, []);

  const openRegistryNameModal = React.useCallback(
    (pageIndex: number) => {
      const fallbackName = programSlideRegistryFallbackName(pageIndex);
      const current = normalizeProgramSlideRegistryName(
        registryPageNamesByIndex[pageIndex] ?? '',
        fallbackName
      );
      closeLayoutPicker();
      closeSlideSettingsModal();
      setRegistryNameModal({ pageIndex, draft: current, fallbackName });
    },
    [closeLayoutPicker, closeSlideSettingsModal, registryPageNamesByIndex]
  );

  const confirmRegistryNameModal = React.useCallback(() => {
    if (!registryNameModal) return;
    const next = normalizeProgramSlideRegistryName(
      registryNameModal.draft,
      registryNameModal.fallbackName
    );
    setRegistryPageNamesByIndex((prev) => ({ ...prev, [registryNameModal.pageIndex]: next }));
    setHasUnsavedSlideDraft(true);
    setRegistryNameModal(null);
  }, [registryNameModal]);

  const addSlide = React.useCallback(() => {
    if (isRegistryMode && extraPageCountRef.current >= registryMaxCount) return;
    const newPageIndex = extraPageCountRef.current;
    const empty: ProgramSlideLayoutV1 = {
      ...EMPTY_SLIDE_LAYOUT,
      defaultTextColorHex: slideGlobalTextColorHex,
      defaultBackgroundColorHex: slideGlobalBackgroundColorHex,
    };
    setLayouts((m) => ({ ...m, [newPageIndex]: empty }));
    if (isRegistryMode) {
      const fallbackName = programSlideRegistryFallbackName(newPageIndex);
      setRegistryPageNamesByIndex((prevNames) => ({ ...prevNames, [newPageIndex]: fallbackName }));
      setRegistryPageIdsByIndex((prevIds) => ({
        ...prevIds,
        [newPageIndex]: createPresentationRefId(),
      }));
    }
    setSelectedEditablePageIndex(newPageIndex);
    setHasUnsavedSlideDraft(true);
    setExtraPageCount((prev) => prev + 1);
    if (isRegistryMode) {
      openRegistryNameModal(newPageIndex);
    }
  }, [
    isRegistryMode,
    openRegistryNameModal,
    registryMaxCount,
    slideGlobalBackgroundColorHex,
    slideGlobalTextColorHex,
  ]);

  const openLayoutPicker = React.useCallback((pageIndex: number) => {
    const committedId = (layoutsRef.current[pageIndex]?.appliedLayoutTemplateId ?? '').trim();
    setLayoutPickerOriginalId(committedId);
    setLayoutPickerHoverId(undefined);
    setSlideSettingsModalOpen(false);
    closeRegistryNameModal();
    setLayoutPickerPageIndex(pageIndex);
  }, [closeRegistryNameModal]);

  const applySlideGlobalDefaults = React.useCallback((textColorHex: string, backgroundColorHex: string) => {
    const nextText = normalizeColorInputHex(textColorHex, SLIDE_DEFAULT_TEXT_COLOR_HEX);
    const nextBg = normalizeColorInputHex(backgroundColorHex, SLIDE_DEFAULT_BG_COLOR_HEX);
    setSlideGlobalTextColorHex(nextText);
    setSlideGlobalBackgroundColorHex(nextBg);
    setLayouts((prev) => {
      const nextLayouts: Record<number, ProgramSlideLayoutV1> = {};
      Object.entries(prev).forEach(([idxRaw, layout]) => {
        const idx = Number(idxRaw);
        if (!Number.isFinite(idx)) return;
        const cloned = cloneSlideLayout(layout);
        nextLayouts[idx] = {
          ...cloned,
          defaultTextColorHex: nextText,
          defaultBackgroundColorHex: nextBg,
        };
      });
      return nextLayouts;
    });
    setHasUnsavedSlideDraft(true);
  }, []);

  const applyPageBackgroundColor = React.useCallback((pageIndex: number, colorHex: string | null) => {
    setLayouts((prev) => {
      const prevLayout = cloneSlideLayout(prev[pageIndex] ?? EMPTY_SLIDE_LAYOUT);
      const normalized = colorHex ? normalizeColorInputHex(colorHex, SLIDE_DEFAULT_BG_COLOR_HEX) : '';
      const nextLayout: ProgramSlideLayoutV1 = {
        ...prevLayout,
        pageBackgroundColorHex: normalized || undefined,
      };
      return { ...prev, [pageIndex]: nextLayout };
    });
    setHasUnsavedSlideDraft(true);
  }, []);

  const applyPageTextColor = React.useCallback((pageIndex: number, colorHex: string | null) => {
    setLayouts((prev) => {
      const prevLayout = cloneSlideLayout(prev[pageIndex] ?? EMPTY_SLIDE_LAYOUT);
      const normalized = colorHex ? normalizeColorInputHex(colorHex, SLIDE_DEFAULT_TEXT_COLOR_HEX) : '';
      const nextLayout: ProgramSlideLayoutV1 = {
        ...prevLayout,
        pageTextColorHex: normalized || undefined,
      };
      return { ...prev, [pageIndex]: nextLayout };
    });
    setHasUnsavedSlideDraft(true);
  }, []);

  const applyLayoutTemplateToPage = React.useCallback((pageIndex: number, layoutTemplateId: string | null) => {
    const nextId = (layoutTemplateId ?? '').trim();
    setLayouts((prev) => {
      const prevLayout = cloneSlideLayout(prev[pageIndex] ?? EMPTY_SLIDE_LAYOUT);
      const currentId = (prevLayout.appliedLayoutTemplateId ?? '').trim();
      if (currentId === nextId) return prev;
      const nextLayout: ProgramSlideLayoutV1 = {
        ...prevLayout,
        appliedLayoutTemplateId: nextId || undefined,
      };
      return { ...prev, [pageIndex]: nextLayout };
    });
    setHasUnsavedSlideDraft(true);
    closeLayoutPicker();
  }, [closeLayoutPicker]);

  React.useEffect(() => {
    if (layoutPickerPageIndex == null && !slideSettingsModalOpen && !registryNameModal) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      closeLayoutPicker();
      closeSlideSettingsModal();
      closeRegistryNameModal();
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      const layoutEl = layoutPickerRef.current;
      const settingsEl = slideSettingsModalRef.current;
      const nameEl = registryNameModalRef.current;
      if (layoutEl?.contains(target) || settingsEl?.contains(target) || nameEl?.contains(target)) return;
      closeLayoutPicker();
      closeSlideSettingsModal();
      closeRegistryNameModal();
    };
    window.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [
    closeLayoutPicker,
    closeRegistryNameModal,
    closeSlideSettingsModal,
    layoutPickerPageIndex,
    registryNameModal,
    slideSettingsModalOpen,
  ]);

  React.useEffect(() => {
    if (!registryNameModal) return;
    const input = registryNameInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [registryNameModal?.pageIndex]);

  const knownIdsForWebcamSplit =
    webcamSessionBaselineSnapshot?.[pageIndexForCanvas] ?? EMPTY_KNOWN_IDS_FOR_WEBCAM_SPLIT;
  const rawLayoutForWebcamPage = layouts[pageIndexForCanvas] ?? EMPTY_SLIDE_LAYOUT;
  const layoutWebcamUnder = React.useMemo(
    () => cloneSlideLayout(pruneLayoutToKnownEntityIds(rawLayoutForWebcamPage, knownIdsForWebcamSplit)),
    [rawLayoutForWebcamPage, knownIdsForWebcamSplit]
  );
  const layoutWebcamOver = React.useMemo(
    () => cloneSlideLayout(pruneLayoutToUnknownEntityIds(rawLayoutForWebcamPage, knownIdsForWebcamSplit)),
    [rawLayoutForWebcamPage, knownIdsForWebcamSplit]
  );

  const handleWebcamOverlayLayoutChange = React.useCallback(
    (pageIndex: number, next: ProgramSlideLayoutV1) => {
      const base = webcamSessionBaselineSnapshot?.[pageIndex] ?? EMPTY_KNOWN_IDS_FOR_WEBCAM_SPLIT;
      const prevFull = layoutsRef.current[pageIndex] ?? EMPTY_SLIDE_LAYOUT;
      const withLayoutBinding: ProgramSlideLayoutV1 = {
        ...next,
        appliedLayoutTemplateId: prevFull.appliedLayoutTemplateId,
        defaultTextColorHex: prevFull.defaultTextColorHex,
        defaultBackgroundColorHex: prevFull.defaultBackgroundColorHex,
        pageBackgroundColorHex: prevFull.pageBackgroundColorHex,
        pageTextColorHex: prevFull.pageTextColorHex,
      };
      if (!slideWebcamDesired) {
        handleCanvasLayoutChange(pageIndex, withLayoutBinding);
        return;
      }
      const merged = mergeWebcamSessionLayouts(base, prevFull, withLayoutBinding);
      handleCanvasLayoutChange(pageIndex, {
        ...merged,
        appliedLayoutTemplateId: prevFull.appliedLayoutTemplateId,
        defaultTextColorHex: prevFull.defaultTextColorHex,
        defaultBackgroundColorHex: prevFull.defaultBackgroundColorHex,
        pageBackgroundColorHex: prevFull.pageBackgroundColorHex,
        pageTextColorHex: prevFull.pageTextColorHex,
      });
    },
    [slideWebcamDesired, webcamSessionBaselineSnapshot, handleCanvasLayoutChange]
  );

  const openPublicQrOverlayForMode = React.useCallback(
    async (mode: 'full' | 'question') => {
      const p = payload;
      const ownerRef = p?.owner;
      const sid = p?.workspaceId?.trim() ?? '';
      if (!p || ownerRef?.kind !== 'presentation_ref' || !sid || !ownerRef.id?.trim()) return;
      const setLoading = setQuestionQrLoading;
      setLoading(true);
      setPublicQrError('');
      try {
        // 로컬 초안 슬라이드가 DB에 반영되기 전에 QR을 열면 빈 캔버스로 보이므로 먼저 flush
        try {
          await flushPendingProgramContentSlideDraftForOwner(sid, ownerRef);
          const learningOwner = p.learningTextOwner;
          if (
            learningOwner?.id?.trim() &&
            learningOwner.kind !== 'presentation_ref' &&
            learningOwner.id.trim() !== ownerRef.id.trim()
          ) {
            await flushPendingProgramContentSlideDraftForOwner(sid, learningOwner);
          }
        } catch {
          /* flush 실패해도 세션 생성은 시도 */
        }
        // 같은 워크스페이스·프레젠 세션을 재사용하므로 토큰은 공개 QR과 동일 — URL 파라미터만 달라진다.
        const session = await createPresentationPublicSession({
          workspaceId: sid,
          presentationRefId: ownerRef.id,
          meta: p.meta,
          extraPageCount,
        });
        const publicUrl =
          mode === 'question'
            ? `${session.publicUrl}${session.publicUrl.includes('?') ? '&' : '?'}view=question`
            : session.publicUrl;
        setPublicQrOverlay({ publicUrl, expiresAt: session.expiresAt, mode });
      } catch (e) {
        setPublicQrError(e instanceof Error ? e.message : 'QR을 만들지 못했습니다.');
        window.setTimeout(() => setPublicQrError(''), 2800);
      } finally {
        setLoading(false);
      }
    },
    [extraPageCount, payload]
  );

  /** 질문 QR: 저장된 프레젠은 질문 페이지 QR — 저장 전은 아래 소통방 QR(openLessonCommQr)로 대체 */
  const openQuestionPageQr = React.useCallback(
    () => openPublicQrOverlayForMode('question'),
    [openPublicQrOverlayForMode]
  );

  const refreshCommunicationBoard = React.useCallback(async () => {
    const p = payload;
    const sid = (p?.workspaceId ?? '').trim();
    const gradeId = (p?.contextGradeId ?? p?.createPresentationRefOnSave?.gradeId ?? '').trim();
    const ownerRef = p?.owner;
    if (!sid) return;
    setCommunicationLoadError('');
    communicationResponsesCacheRef.current.clear();
    try {
      let nextGroups: ClassMemberGroupMeta[] = [];
      let nextByGroup: Record<string, ClassMemberRow[]> = {};
      let savedGroupId: string | null = null;

      if (gradeId) {
        const layouts = await getClassMemberGradeLayoutsMap(sid, [gradeId]);
        const layout = layouts[gradeId];
        nextGroups = [...(layout?.groups ?? [])].sort(
          (a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id)
        );
        nextByGroup = layout?.membersByGroupId ?? {};
      }
      setCommunicationGroups(nextGroups);
      setCommunicationMembersByGroupId(nextByGroup);

      if (ownerRef?.kind === 'presentation_ref' && ownerRef.id.trim()) {
        const refId = ownerRef.id.trim();
        const [comm, questions] = await Promise.all([
          getPresentationCommunication({
            presentationRefId: refId,
            workspaceId: sid,
          }),
          listPresentationCommunicationQuestions({
            presentationRefId: refId,
            workspaceId: sid,
          }),
        ]);
        setCommunicationQuestions(questions);
        setCommunicationIsOpen(Boolean(comm?.isOpen));
        const editor = communicationEditorRef.current;
        const localDraft = editor.localDrafts[editor.index];
        const hasLocalDraftContent = Boolean(
          (localDraft?.promptText || '').trim() ||
            (localDraft?.answerText || '').trim() ||
            localDraft?.question ||
            (editor.prompt || '').trim() ||
            (editor.answer || '').trim() ||
            editor.question
        );
        let nextIndex = editor.index;
        if (questions.length > 0) {
          const activeId = (comm?.activeQuestionId ?? '').trim();
          const found = activeId ? questions.findIndex((q) => q.id === activeId) : -1;
          if (!hasLocalDraftContent) {
            nextIndex = found >= 0 ? found : 0;
            const q = questions[nextIndex]!;
            setCommunicationPrompt(q.promptText);
            setCommunicationAnswer(q.answerText ?? '');
            setCommunicationQuestion(q.question ?? null);
            setCommunicationMultiResponseDraft(Boolean(q.multiResponse));
            setCommunicationLocalDrafts({});
          } else if (nextIndex > questions.length) {
            nextIndex = questions.length;
          }
          savedGroupId = (comm?.memberGroupId ?? '').trim() || null;
          const qForResponses =
            nextIndex < questions.length ? questions[nextIndex] : null;
          if (qForResponses) {
            const responses = await listPresentationCommunicationResponses({
              presentationRefId: refId,
              workspaceId: sid,
              questionId: qForResponses.id,
            });
            communicationResponsesCacheRef.current.set(qForResponses.id, responses);
            setCommunicationResponses(responses);
          } else {
            setCommunicationResponses([]);
          }
        } else {
          if (!hasLocalDraftContent) {
            setCommunicationPrompt('');
            setCommunicationAnswer('');
            setCommunicationQuestion(null);
            setCommunicationMultiResponseDraft(false);
            setCommunicationLocalDrafts({});
            nextIndex = 0;
          }
          setCommunicationResponses([]);
          if (comm) {
            savedGroupId = (comm.memberGroupId ?? '').trim() || null;
          }
        }
        setCommunicationQuestionIndex(nextIndex);
      }

      setCommunicationSelectedGroupId((prev) => {
        const preferred = savedGroupId ?? prev;
        if (preferred && nextGroups.some((g) => g.id === preferred)) return preferred;
        return nextGroups[0]?.id ?? null;
      });
    } catch (e) {
      setCommunicationLoadError(e instanceof Error ? e.message : '질문 데이터를 불러오지 못했습니다.');
    }
  }, [payload]);

  const communicationMembers = React.useMemo(() => {
    const gid = (communicationSelectedGroupId ?? '').trim();
    if (!gid) return [];
    return communicationMembersByGroupId[gid] ?? [];
  }, [communicationMembersByGroupId, communicationSelectedGroupId]);

  const communicationGroupMemberCounts = React.useMemo(() => {
    const out: Record<string, number> = {};
    for (const g of communicationGroups) {
      out[g.id] = communicationMembersByGroupId[g.id]?.length ?? 0;
    }
    return out;
  }, [communicationGroups, communicationMembersByGroupId]);

  /** CSV/PDF 전체 내보내기용 — 그룹 순서로 이어붙인 전체 멤버·번호(그룹과 무관하게 안정된 번호) */
  const communicationAllMembersForExport = React.useMemo(
    () => flattenGradeLayout({ groups: communicationGroups, membersByGroupId: communicationMembersByGroupId }),
    [communicationGroups, communicationMembersByGroupId]
  );
  const communicationAllMemberNumberById = React.useMemo(() => {
    const layout = { groups: communicationGroups, membersByGroupId: communicationMembersByGroupId };
    // 고정 번호를 쓰면 멤버 관리·소통 패널과 같은 번호(그룹별 공통 헬퍼)로 내보낸다.
    if (layoutHasFixedMemberNumbers(layout)) return buildLayoutMemberNumberMap(layout);
    const map = new Map<string, number>();
    communicationAllMembersForExport.forEach((m, i) => map.set(m.id, i + 1));
    return map;
  }, [communicationAllMembersForExport, communicationGroups, communicationMembersByGroupId]);

  const [exportingCommunicationCsv, setExportingCommunicationCsv] = React.useState(false);
  const [exportingCommunicationPdf, setExportingCommunicationPdf] = React.useState(false);

  /** 현재 프레젠테이션의 모든 질문 + 각 질문의 응답을 서버에서 새로 불러와 섹션으로 만든다. */
  const fetchAllCommunicationExportSections = React.useCallback(async (): Promise<
    CommunicationExportSection[]
  > => {
    const p = payload;
    const sid = (p?.workspaceId ?? '').trim();
    const ownerRef = p?.owner;
    if (!sid || ownerRef?.kind !== 'presentation_ref' || !ownerRef.id.trim()) return [];
    const refId = ownerRef.id.trim();
    const questions = await listPresentationCommunicationQuestions({
      presentationRefId: refId,
      workspaceId: sid,
    });
    const sections: CommunicationExportSection[] = [];
    for (const q of questions) {
      const responses = await listPresentationCommunicationResponses({
        presentationRefId: refId,
        workspaceId: sid,
        questionId: q.id,
      });
      sections.push(
        buildCommunicationExportSection({
          promptText: q.promptText,
          answerText: q.answerText ?? '',
          question: q.question ?? null,
          members: communicationAllMembersForExport,
          memberNumberById: communicationAllMemberNumberById,
          responses,
        })
      );
    }
    return sections;
  }, [payload, communicationAllMembersForExport, communicationAllMemberNumberById]);

  const communicationExportFileBaseName = React.useCallback(() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
    return `질문응답_${stamp}`;
  }, []);

  /** 문서 최상위에 표시할 편제명·단원명·쪽수·학습내용(프레젠테이션 슬라이드 상단 정보와 동일 출처) */
  const communicationExportDocumentMeta = React.useMemo((): CommunicationExportDocumentMeta => {
    const m = payload?.meta;
    const pageMainV = (m?.page ?? '').trim();
    const pageAssistV = (m?.assistantPage ?? '').trim();
    return {
      track: (m?.templateLabel ?? '').trim(),
      unit: (m?.unit ?? '').trim(),
      page: [pageMainV, pageAssistV].filter(Boolean).join(' '),
      programContent: (m?.programContent ?? '').trim(),
    };
  }, [payload]);

  const handleExportCommunicationCsv = React.useCallback(async () => {
    if (exportingCommunicationCsv) return;
    setExportingCommunicationCsv(true);
    try {
      const sections = await fetchAllCommunicationExportSections();
      await downloadCommunicationResponsesExcel({
        sections,
        fileBaseName: communicationExportFileBaseName(),
        documentMeta: communicationExportDocumentMeta,
      });
    } catch (e) {
      console.error('질문·응답 엑셀 내보내기 실패:', e);
      showPresentationToastError('엑셀 내보내기에 실패했습니다.');
    } finally {
      setExportingCommunicationCsv(false);
    }
  }, [
    exportingCommunicationCsv,
    fetchAllCommunicationExportSections,
    communicationExportFileBaseName,
    communicationExportDocumentMeta,
    showPresentationToastError,
  ]);

  const handleExportCommunicationPdf = React.useCallback(async () => {
    if (exportingCommunicationPdf) return;
    setExportingCommunicationPdf(true);
    try {
      const sections = await fetchAllCommunicationExportSections();
      await savePresentationCommunicationResponsesAsPdf({
        sections,
        fileBaseName: communicationExportFileBaseName(),
        documentMeta: communicationExportDocumentMeta,
      });
    } catch (e) {
      console.error('질문·응답 PDF 내보내기 실패:', e);
      showPresentationToastError('PDF 내보내기에 실패했습니다.');
    } finally {
      setExportingCommunicationPdf(false);
    }
  }, [
    exportingCommunicationPdf,
    fetchAllCommunicationExportSections,
    communicationExportFileBaseName,
    communicationExportDocumentMeta,
    showPresentationToastError,
  ]);

  const hasUnsavedCommunicationDraft = React.useMemo(() => {
    if (isRegistryMode || !isCommunicationView) return false;
    const saved = communicationQuestions[communicationQuestionIndex];
    const isDraftSlot =
      communicationQuestionIndex >= communicationQuestions.length || !saved;
    const prompt = (communicationPrompt || '').trim();
    const answer = (communicationAnswer || '').trim();
    const questionJson = communicationQuestion ? JSON.stringify(communicationQuestion) : '';
    if (isDraftSlot) {
      return Boolean(prompt || answer || questionJson);
    }
    const savedPrompt = (saved.promptText || '').trim();
    const savedAnswer = (saved.answerText || '').trim();
    const savedQuestionJson = saved.question ? JSON.stringify(saved.question) : '';
    return (
      prompt !== savedPrompt ||
      answer !== savedAnswer ||
      questionJson !== savedQuestionJson
    );
  }, [
    isRegistryMode,
    isCommunicationView,
    communicationQuestions,
    communicationQuestionIndex,
    communicationPrompt,
    communicationAnswer,
    communicationQuestion,
  ]);

  /**
   * 학생 기기(소통방) 실시간 응답 — DB에 쓰지 않고 메모리에서 학생 블럭에 바로 합친다(질문을 닫을 때 1회 일괄 저장).
   * live: 지금 열린 질문의 응답 / held: 닫은 뒤 일괄 저장 → 다시 불러올 때까지 잠시 유지(깜빡임 방지)
   */
  const [lessonCommLiveResponses, setLessonCommLiveResponses] = React.useState<{
    questionId: string;
    rows: PresentationCommunicationResponse[];
  } | null>(null);
  const [lessonCommHeldResponses, setLessonCommHeldResponses] = React.useState<{
    questionId: string;
    rows: PresentationCommunicationResponse[];
  } | null>(null);
  const currentCommunicationQuestionId = communicationQuestions[communicationQuestionIndex]?.id ?? '';
  const displayedCommunicationResponses = React.useMemo(() => {
    const qid = currentCommunicationQuestionId;
    if (!qid) return communicationResponses;
    const byMember = new Map<string, PresentationCommunicationResponse>();
    if (lessonCommHeldResponses?.questionId === qid) lessonCommHeldResponses.rows.forEach((r) => byMember.set(r.memberId, r));
    if (lessonCommLiveResponses?.questionId === qid) lessonCommLiveResponses.rows.forEach((r) => byMember.set(r.memberId, r));
    if (!byMember.size) return communicationResponses;
    /* 같은 학생은 학생 기기 응답이 최신 → 저장된 행을 대신함(일괄 저장도 같은 학생 기존 행을 지우고 넣음) */
    return [...communicationResponses.filter((r) => !byMember.has(r.memberId)), ...Array.from(byMember.values())];
  }, [communicationResponses, currentCommunicationQuestionId, lessonCommLiveResponses, lessonCommHeldResponses]);

  /** 재생 가능: 저장된 질문이 있고, 편집 중(미저장 변경)이 아닐 때만 */
  const canPlayCommunicationQuestion =
    Boolean(communicationQuestions[communicationQuestionIndex]) &&
    !hasUnsavedCommunicationDraft &&
    !communicationPlayBusy;

  const buildCommunicationMonitorSnapshot =
    React.useCallback((): PresentationCommunicationMonitorSnapshot | null => {
      const p = payload;
      const sid = (p?.workspaceId ?? '').trim();
      const ownerRef = p?.owner;
      if (!sid || ownerRef?.kind !== 'presentation_ref' || !ownerRef.id.trim()) return null;
      return {
        presentationRefId: ownerRef.id.trim(),
        workspaceId: sid,
        promptText: communicationPrompt,
        answerText: communicationAnswer,
        question: communicationQuestion,
        groups: communicationGroups,
        selectedGroupId: communicationSelectedGroupId,
        membersByGroupId: communicationMembersByGroupId,
        responses: displayedCommunicationResponses,
        updatedAt: Date.now(),
        playState: {
          isOpen: communicationIsOpen,
          multiResponse: communicationMultiResponseDraft,
          canPlay: canPlayCommunicationQuestion,
          disabledReason: hasUnsavedCommunicationDraft
            ? '저장하지 않은 변경사항이 있습니다. 먼저 저장한 뒤 재생해 주세요.'
            : !communicationQuestions[communicationQuestionIndex]
              ? '재생할 질문이 없습니다. 먼저 질문을 저장해 주세요.'
              : undefined,
        },
        questionNav: {
          canPrev: communicationQuestionIndex > 0,
          canNext: communicationQuestionIndex < communicationQuestions.length - 1,
          currentIndex: communicationQuestionIndex + 1,
          totalCount: Math.max(communicationQuestions.length, communicationQuestionIndex + 1, 1),
          canAdd: communicationQuestionIndex < communicationQuestions.length,
        },
      };
    }, [
      payload,
      communicationPrompt,
      communicationAnswer,
      communicationQuestion,
      communicationGroups,
      communicationSelectedGroupId,
      communicationMembersByGroupId,
      displayedCommunicationResponses,
      communicationIsOpen,
      communicationMultiResponseDraft,
      canPlayCommunicationQuestion,
      hasUnsavedCommunicationDraft,
      communicationQuestionIndex,
      communicationQuestions,
    ]);

  React.useEffect(() => {
    if (!isCommunicationView || isRegistryMode) return;
    const snap = buildCommunicationMonitorSnapshot();
    if (!snap) return;
    publishPresentationCommunicationMonitorSnapshot(snap);
  }, [isCommunicationView, isRegistryMode, buildCommunicationMonitorSnapshot]);

  const handleOpenCommunicationMonitor = React.useCallback(() => {
    const snap = buildCommunicationMonitorSnapshot();
    if (!snap) {
      showPresentationToastError('모니터링할 질문 데이터가 없습니다.');
      return;
    }
    const requestId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `comm-mon-${Date.now()}`;
    let settled = false;
    let unsub: () => void = () => undefined;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      unsub();
      if (ok) {
        showPresentationToastSuccess('모니터링 팝업이 열렸습니다.');
      } else {
        showPresentationToastError(
          '클래스 플래너 탭이 없어 모니터링 팝업을 열지 못했습니다.'
        );
      }
    };
    unsub = subscribePresentationCommunicationMonitor({
      onAck: (id) => {
        if (id === requestId) finish(true);
      },
    });
    requestOpenPresentationCommunicationMonitor(snap, requestId);
    window.setTimeout(() => finish(false), 320);
  }, [
    buildCommunicationMonitorSnapshot,
    showPresentationToastError,
    showPresentationToastSuccess,
  ]);

  React.useEffect(() => {
    if (!isCommunicationView || isRegistryMode) return;
    void refreshCommunicationBoard();
  }, [isCommunicationView, isRegistryMode, refreshCommunicationBoard]);

  React.useEffect(() => {
    if (!isCommunicationView || isRegistryMode) return;
    const p = payload;
    const sid = (p?.workspaceId ?? '').trim();
    const ownerRef = p?.owner;
    if (!sid || ownerRef?.kind !== 'presentation_ref' || !ownerRef.id.trim()) return;
    const refId = ownerRef.id.trim();
    return subscribePresentationCommunicationResponses({
      presentationRefId: refId,
      workspaceId: sid,
      onChange: (changedQuestionId) => {
        // 실제로 응답이 바뀐 질문의 캐시만 무효화한다(다른 질문 캐시는 그대로 재사용).
        if (changedQuestionId) {
          communicationResponsesCacheRef.current.delete(changedQuestionId);
        } else {
          communicationResponsesCacheRef.current.clear();
        }
        const qId = communicationQuestions[communicationQuestionIndex]?.id;
        if (!qId) return;
        // 지금 화면에 보이는 질문과 무관한 변경이면 다시 조회하지 않고, 다음에 그 질문으로 이동할 때 새로 불러온다.
        if (changedQuestionId && changedQuestionId !== qId) return;
        void listPresentationCommunicationResponses({
          presentationRefId: refId,
          workspaceId: sid,
          questionId: qId,
        })
          .then((responses) => {
            communicationResponsesCacheRef.current.set(qId, responses);
            setCommunicationResponses(responses);
          })
          .catch(() => undefined);
      },
    });
  }, [
    isCommunicationView,
    isRegistryMode,
    payload,
    communicationQuestions,
    communicationQuestionIndex,
  ]);

  const persistCommunicationGroupSelection = React.useCallback(
    async (groupId: string) => {
      const p = payload;
      const sid = (p?.workspaceId ?? '').trim();
      const gradeId = (p?.contextGradeId ?? p?.createPresentationRefOnSave?.gradeId ?? '').trim();
      const ownerRef = p?.owner;
      if (!sid || ownerRef?.kind !== 'presentation_ref' || !ownerRef.id.trim()) return;
      try {
        await upsertPresentationCommunication({
          presentationRefId: ownerRef.id.trim(),
          workspaceId: sid,
          promptText: communicationPrompt,
          answerText: communicationAnswer,
          gradeId: gradeId || null,
          memberGroupId: groupId,
        });
      } catch {
        /* 그룹 전환 중 저장 실패는 표시만 유지 — 안내 저장에서 재시도 */
      }
    },
    [communicationAnswer, communicationPrompt, payload]
  );

  const handleSelectCommunicationGroup = React.useCallback(
    (groupId: string) => {
      const next = groupId.trim();
      if (!next) return;
      setCommunicationSelectedGroupId(next);
      void persistCommunicationGroupSelection(next);
    },
    [persistCommunicationGroupSelection]
  );

  /**
   * 질문 페이지 저장에 쓸 presentation_ref 확보 — 질문 저장 버튼 / 리모컨 질문 공용.
   * 이미 있으면 그대로, 학습내용에 ref 가 있으면 재사용, 아니면(진도표에서 연 수업 프레젠) 새로 발급해 원본 칸에 연결.
   * 동시에 여러 번 불려도 ref 를 한 번만 발급(진행 중 Promise 공유).
   */
  const ensureRefInFlightRef = React.useRef<Promise<string> | null>(null);
  /** 방금 발급한 ref (setPayload 반영 전 다시 불려도 같은 ref 사용) */
  const ensuredRefRef = React.useRef<{ k: string; id: string } | null>(null);
  const ensureCommunicationPresentationRefId = React.useCallback(
    (p: ProgramContentSlidePayloadV1): Promise<string> => {
      if (p.owner?.kind === 'presentation_ref' && p.owner.id.trim()) return Promise.resolve(p.owner.id.trim());
      if (ensuredRefRef.current?.k === k) return Promise.resolve(ensuredRefRef.current.id);
      if (ensureRefInFlightRef.current) return ensureRefInFlightRef.current;
      const run = (async () => {
        const sid = p.workspaceId.trim();
        let owner = p.owner;
        // 학습내용에 이미 presentation_ref가 있으면 그걸 우선 사용
        if ((!owner || owner.kind !== 'presentation_ref') && p.bindingLearningText) {
          const existingRef = resolvePresentationRefIdFromLearningText(p.bindingLearningText);
          if (existingRef) {
            owner = { kind: 'presentation_ref', id: existingRef };
            updateProgramContentSlidePayloadOwner(k, owner);
            setPayload((prev) => (prev ? { ...prev, owner } : prev));
          }
        }
        if ((!owner || owner.kind !== 'presentation_ref') && p.createPresentationRefOnSave) {
          if (await findExistingPresentationRefOnSourceOwner({ workspaceId: sid, sourceOwner: owner })) {
            throw new Error(PRESENTATION_REF_CONFLICT_MESSAGE);
          }
          const presentationRefId = createPresentationRefId();
          const nextLearningText = learningTextWithPresentationRef(
            p.createPresentationRefOnSave.learningText,
            presentationRefId
          );
          let ownerForProgramTableNotify: ProgramContentSlideOwnerRef | null = owner ?? null;
          if (owner) {
            /**
             * 질문 저장은 여기서 presentation_communication_questions에 즉시 DB 반영된다.
             * 원본 행(owner)에도 새 presentation_ref_id를 즉시 반영해야, 진도표 저장을 건너뛰어도
             * 다음에 같은 칸을 열 때 같은 ref를 재사용해 "고아 ref" 중복 발급을 막을 수 있다.
             */
            await persistPresentationRefIdToSourceOwner({
              workspaceId: sid,
              sourceOwner: owner,
              presentationRefId,
            });
          } else {
            ownerForProgramTableNotify = await createProgramContentOwnerForPresentationRef(
              p,
              nextLearningText
            );
          }
          notifyProgramContentSlideLearningUpdated({
            workspaceId: sid,
            scopeId: p.scopeId,
            annualKey: (p.annualCellKey ?? '').trim() || undefined,
            learningText: nextLearningText,
            presentationRefId,
            owner: ownerForProgramTableNotify,
          });
          owner = { kind: 'presentation_ref', id: presentationRefId };
          updateProgramContentSlidePayloadOwner(k, owner);
          setPayload((prev) =>
            prev
              ? {
                  ...prev,
                  owner,
                  createPresentationRefOnSave: undefined,
                }
              : prev
          );
        }
        if (!owner || owner.kind !== 'presentation_ref' || !owner.id.trim()) {
          throw new Error('먼저 슬라이드를 저장해 프레젠테이션을 만든 뒤 질문을 저장해 주세요.');
        }
        ensuredRefRef.current = { k, id: owner.id.trim() };
        return owner.id.trim();
      })();
      ensureRefInFlightRef.current = run;
      return run.finally(() => {
        ensureRefInFlightRef.current = null;
      });
    },
    [k]
  );

  const handleSaveCommunicationPrompt = React.useCallback(
    async (override?: {
      question?: CommunicationQuestion | null;
      promptText?: string;
      answerText?: string;
    }) => {
      const p = payload;
      if (!p?.workspaceId.trim() || !p.scopeId?.trim()) {
        const msg = '워크스페이스 정보가 없어 질문을 저장할 수 없습니다.';
        setCommunicationSaveError(msg);
        setSlideSaveMessage(msg);
        throw new Error(msg);
      }
      const sid = p.workspaceId.trim();
      const gradeId = (p.contextGradeId ?? p.createPresentationRefOnSave?.gradeId ?? '').trim();
      const nextPrompt =
        override?.promptText !== undefined ? override.promptText : communicationPrompt;
      const nextAnswer =
        override?.answerText !== undefined ? override.answerText : communicationAnswer;
      const nextQuestion =
        override?.question !== undefined ? override.question : communicationQuestion;
      if (override?.promptText !== undefined) setCommunicationPrompt(override.promptText);
      if (override?.answerText !== undefined) setCommunicationAnswer(override.answerText);
      if (override?.question !== undefined) setCommunicationQuestion(override.question);
      setCommunicationSaveError(null);
      setCommunicationSaving(true);
      try {
        const ownerRefId = await ensureCommunicationPresentationRefId(p);
        const currentSaved = communicationQuestions[communicationQuestionIndex];
        const isDraft =
          communicationQuestionIndex >= communicationQuestions.length || !currentSaved;
        const saved = await upsertPresentationCommunicationQuestion({
          presentationRefId: ownerRefId,
          workspaceId: sid,
          promptText: nextPrompt,
          answerText: nextAnswer,
          question: nextQuestion,
          questionId: isDraft ? null : currentSaved.id,
          gradeId: gradeId || null,
          memberGroupId: communicationSelectedGroupId,
        });
        await refreshCommunicationBoard();
        setCommunicationLocalDrafts({});
        setCommunicationQuestions((prev) => {
          const idx = prev.findIndex((q) => q.id === saved.id);
          if (idx >= 0) setCommunicationQuestionIndex(idx);
          return prev;
        });
        // 저장(내용 변경) 시 서버에서 자동 일시정지됨 — refreshCommunicationBoard가 이미 반영
        setCommunicationSaveError(null);
        setSlideSaveMessage('질문이 저장되었습니다.');
        window.setTimeout(() => setSlideSaveMessage(''), 1800);
      } catch (e) {
        const msg = e instanceof Error ? e.message : '질문 저장에 실패했습니다.';
        setCommunicationSaveError(msg);
        setSlideSaveMessage(msg);
        throw e instanceof Error ? e : new Error(msg);
      } finally {
        setCommunicationSaving(false);
      }
    },
    [
      communicationAnswer,
      communicationPrompt,
      communicationQuestion,
      communicationQuestions,
      communicationQuestionIndex,
      communicationSelectedGroupId,
      ensureCommunicationPresentationRefId,
      payload,
      refreshCommunicationBoard,
    ]
  );

  const stashCommunicationDraftAtIndex = React.useCallback(
    (index: number) => {
      setCommunicationLocalDrafts((prev) => ({
        ...prev,
        [index]: {
          promptText: communicationPrompt,
          answerText: communicationAnswer,
          question: communicationQuestion,
          multiResponse: communicationMultiResponseDraft,
        },
      }));
    },
    [
      communicationPrompt,
      communicationAnswer,
      communicationQuestion,
      communicationMultiResponseDraft,
    ]
  );

  const loadCommunicationDraftAtIndex = React.useCallback(
    async (index: number, questions: PresentationCommunicationQuestionRow[]) => {
      const local = communicationLocalDrafts[index];
      /** 저장된 질문(index < questions.length)이면, 편집 중인 로컬 초안이 있어도 DB 응답은 그대로 불러온다. */
      const savedQuestion = index < questions.length ? questions[index] ?? null : null;

      if (local) {
        setCommunicationQuestionIndex(index);
        setCommunicationPrompt(local.promptText);
        setCommunicationAnswer(local.answerText);
        setCommunicationQuestion(local.question);
        setCommunicationMultiResponseDraft(Boolean(local.multiResponse));
        setCommunicationIsOpen(false);
        if (!savedQuestion) {
          setCommunicationResponses([]);
          return;
        }
      } else if (!savedQuestion) {
        setCommunicationQuestionIndex(index);
        setCommunicationPrompt('');
        setCommunicationAnswer('');
        setCommunicationQuestion(null);
        setCommunicationResponses([]);
        setCommunicationIsOpen(false);
        setCommunicationMultiResponseDraft(false);
        return;
      } else {
        setCommunicationQuestionIndex(index);
        setCommunicationPrompt(savedQuestion.promptText);
        setCommunicationAnswer(savedQuestion.answerText ?? '');
        setCommunicationQuestion(savedQuestion.question ?? null);
        setCommunicationMultiResponseDraft(Boolean(savedQuestion.multiResponse));
        setCommunicationIsOpen(false);
      }

      const p = payload;
      const sid = (p?.workspaceId ?? '').trim();
      const ownerRef = p?.owner;
      if (!sid || ownerRef?.kind !== 'presentation_ref' || !ownerRef.id.trim()) {
        setCommunicationResponses([]);
        return;
      }
      const refId = ownerRef.id.trim();
      /** 질문 전환 직후에는 항상 정지 상태 — 재생 여부는 재생 버튼으로만 바뀐다. */
      const playStateForSaved = {
        isOpen: false,
        multiResponse: Boolean(savedQuestion.multiResponse),
        canPlay: true,
        disabledReason: undefined as string | undefined,
      };
      const questionNavForIndex = {
        canPrev: index > 0,
        canNext: index < questions.length - 1,
        currentIndex: index + 1,
        totalCount: Math.max(questions.length, index + 1, 1),
        canAdd: true,
      };

      try {
        await setPresentationCommunicationActiveQuestion({
          presentationRefId: refId,
          workspaceId: sid,
          questionId: savedQuestion.id,
        });
      } catch (e) {
        console.error(e instanceof Error ? e.message : '활성 질문 설정에 실패했습니다.');
      }

      /** 캐시에 있으면 DB를 다시 조회하지 않고 그대로 재사용한다(실시간 구독이 변경 시에만 무효화). */
      const cachedResponses = communicationResponsesCacheRef.current.get(savedQuestion.id);
      if (cachedResponses) {
        setCommunicationResponses(cachedResponses);
        publishPresentationCommunicationMonitorSnapshot({
          presentationRefId: refId,
          workspaceId: sid,
          promptText: savedQuestion.promptText,
          answerText: savedQuestion.answerText ?? '',
          question: savedQuestion.question ?? null,
          groups: communicationGroups,
          selectedGroupId: communicationSelectedGroupId,
          membersByGroupId: communicationMembersByGroupId,
          responses: cachedResponses,
          updatedAt: Date.now(),
          playState: playStateForSaved,
          questionNav: questionNavForIndex,
        });
        return;
      }

      try {
        const responses = await listPresentationCommunicationResponses({
          presentationRefId: refId,
          workspaceId: sid,
          questionId: savedQuestion.id,
        });
        communicationResponsesCacheRef.current.set(savedQuestion.id, responses);
        setCommunicationResponses(responses);
        publishPresentationCommunicationMonitorSnapshot({
          presentationRefId: refId,
          workspaceId: sid,
          promptText: savedQuestion.promptText,
          answerText: savedQuestion.answerText ?? '',
          question: savedQuestion.question ?? null,
          groups: communicationGroups,
          selectedGroupId: communicationSelectedGroupId,
          membersByGroupId: communicationMembersByGroupId,
          responses,
          updatedAt: Date.now(),
          playState: playStateForSaved,
          questionNav: questionNavForIndex,
        });
      } catch (e) {
        console.error(e instanceof Error ? e.message : '응답을 불러오지 못했습니다.');
      }
    },
    [
      communicationLocalDrafts,
      payload,
      communicationGroups,
      communicationSelectedGroupId,
      communicationMembersByGroupId,
    ]
  );

  const applyCommunicationQuestionAtIndex = React.useCallback(
    async (questions: PresentationCommunicationQuestionRow[], index: number) => {
      stashCommunicationDraftAtIndex(communicationQuestionIndex);
      await loadCommunicationDraftAtIndex(index, questions);
    },
    [stashCommunicationDraftAtIndex, loadCommunicationDraftAtIndex, communicationQuestionIndex]
  );

  const handleCommunicationQuestionPrev = React.useCallback(() => {
    if (communicationQuestionIndex <= 0) return;
    void applyCommunicationQuestionAtIndex(
      communicationQuestions,
      communicationQuestionIndex - 1
    );
  }, [
    applyCommunicationQuestionAtIndex,
    communicationQuestionIndex,
    communicationQuestions,
  ]);

  const handleCommunicationQuestionNext = React.useCallback(() => {
    const currentHasContent = Boolean(
      (communicationPrompt || '').trim() ||
        (communicationAnswer || '').trim() ||
        communicationQuestion
    );
    if (!currentHasContent) return;
    const nextIndex = communicationQuestionIndex + 1;
    // 저장된 마지막 다음·또는 첫 초안 다음으로 빈 슬롯 허용
    void applyCommunicationQuestionAtIndex(communicationQuestions, nextIndex);
  }, [
    applyCommunicationQuestionAtIndex,
    communicationQuestionIndex,
    communicationQuestions,
    communicationPrompt,
    communicationAnswer,
    communicationQuestion,
  ]);

  const handleConfirmDeleteCommunicationQuestion = React.useCallback(async () => {
    const p = payload;
    const sid = (p?.workspaceId ?? '').trim();
    const ownerRef = p?.owner;
    const q = communicationQuestions[communicationQuestionIndex];
    if (!sid || ownerRef?.kind !== 'presentation_ref' || !ownerRef.id.trim() || !q) return;
    setCommunicationDeleting(true);
    try {
      await deletePresentationCommunicationQuestion({
        presentationRefId: ownerRef.id.trim(),
        workspaceId: sid,
        questionId: q.id,
      });
      setCommunicationDeleteConfirmOpen(false);
      await refreshCommunicationBoard();
    } catch (e) {
      console.error(e instanceof Error ? e.message : '질문 삭제에 실패했습니다.');
    } finally {
      setCommunicationDeleting(false);
    }
  }, [
    communicationQuestionIndex,
    communicationQuestions,
    payload,
    refreshCommunicationBoard,
  ]);

  React.useEffect(() => {
    if (!slideTimerActive) {
      timerLastMinuteChimeDoneForSessionRef.current = 0;
      return;
    }
    if (slideTimerPaused) return;
    if (slideTimerTotalMs <= 60_000) return;
    const remainingMs =
      slideTimerTotalMs > 0 ? Math.max(0, slideTimerEndsAtRef.current - Date.now()) : 0;
    if (remainingMs <= 0 || remainingMs > 60_000) return;
    const sid = slideTimerSessionIdRef.current;
    if (timerLastMinuteChimeDoneForSessionRef.current === sid) return;
    timerLastMinuteChimeDoneForSessionRef.current = sid;
    void playTimerOneMinuteChime();
    void slideTimerTick;
  }, [slideTimerActive, slideTimerPaused, slideTimerTotalMs, slideTimerTick]);

  const toggleRegisteredPageInline = React.useCallback(
    (page: ProgramSlideRegisteredPage) => {
      setOpenedRegisteredPage((prev) => {
        if (prev?.id === page.id) {
          if (registeredPageRestoreEditModeRef.current != null) {
            setEditMode(registeredPageRestoreEditModeRef.current);
            registeredPageRestoreEditModeRef.current = null;
          }
          return null;
        }
        if (!prev) {
          registeredPageRestoreEditModeRef.current = editMode;
          if (editMode) setEditMode(false);
        }
        return page;
      });
    },
    [editMode]
  );

  /** 질문 페이지: 저장된 질문과 다르거나, 새 초안에 내용이 있으면 FAB 저장 활성화 */
  const hasUnsavedAnything = hasUnsavedSlideDraft || hasUnsavedCommunicationDraft;

  const handlePlayCommunicationQuestion = React.useCallback(async () => {
    const p = payload;
    const sid = (p?.workspaceId ?? '').trim();
    const ownerRef = p?.owner;
    const q = communicationQuestions[communicationQuestionIndex];
    if (!sid || ownerRef?.kind !== 'presentation_ref' || !ownerRef.id.trim() || !q) return;
    setCommunicationPlayBusy(true);
    try {
      const refId = ownerRef.id.trim();
      const row = await playPresentationCommunicationQuestion({
        presentationRefId: refId,
        workspaceId: sid,
        questionId: q.id,
        multiResponse: communicationMultiResponseDraft,
      });
      setCommunicationIsOpen(Boolean(row.isOpen));
      setCommunicationQuestions((prev) =>
        prev.map((item) =>
          item.id === q.id ? { ...item, multiResponse: communicationMultiResponseDraft } : item
        )
      );
      const responses = await listPresentationCommunicationResponses({
        presentationRefId: refId,
        workspaceId: sid,
        questionId: q.id,
      });
      setCommunicationResponses(responses);
      publishPresentationCommunicationMonitorSnapshot({
        presentationRefId: refId,
        workspaceId: sid,
        promptText: q.promptText,
        answerText: q.answerText ?? '',
        question: q.question ?? null,
        groups: communicationGroups,
        selectedGroupId: communicationSelectedGroupId,
        membersByGroupId: communicationMembersByGroupId,
        responses,
        updatedAt: Date.now(),
        playState: {
          isOpen: Boolean(row.isOpen),
          multiResponse: communicationMultiResponseDraft,
          canPlay: true,
          disabledReason: undefined,
        },
        questionNav: {
          canPrev: communicationQuestionIndex > 0,
          canNext: communicationQuestionIndex < communicationQuestions.length - 1,
          currentIndex: communicationQuestionIndex + 1,
          totalCount: Math.max(communicationQuestions.length, communicationQuestionIndex + 1, 1),
        },
      });
      showPresentationToastSuccess('문제를 재생했습니다. 공개 화면에 표시됩니다.');
    } catch (e) {
      showPresentationToastError(e instanceof Error ? e.message : '재생에 실패했습니다.');
    } finally {
      setCommunicationPlayBusy(false);
    }
  }, [
    payload,
    communicationQuestions,
    communicationQuestionIndex,
    communicationMultiResponseDraft,
    communicationGroups,
    communicationSelectedGroupId,
    communicationMembersByGroupId,
    showPresentationToastSuccess,
    showPresentationToastError,
  ]);

  const handleStopCommunicationQuestion = React.useCallback(async () => {
    const p = payload;
    const sid = (p?.workspaceId ?? '').trim();
    const ownerRef = p?.owner;
    if (!sid || ownerRef?.kind !== 'presentation_ref' || !ownerRef.id.trim()) return;
    setCommunicationPlayBusy(true);
    try {
      await stopPresentationCommunication({ presentationRefId: ownerRef.id.trim(), workspaceId: sid });
      setCommunicationIsOpen(false);
      // 원탭 모니터링 모달의 재생 컨트롤도 즉시 정지 상태로 반영
      const snap = buildCommunicationMonitorSnapshot();
      if (snap) {
        publishPresentationCommunicationMonitorSnapshot({
          ...snap,
          playState: { ...(snap.playState ?? {}), isOpen: false, multiResponse: communicationMultiResponseDraft, canPlay: true },
          updatedAt: Date.now(),
        });
      }
      showPresentationToastSuccess('정지했습니다. 공개 화면에서 숨겨집니다.');
    } catch (e) {
      showPresentationToastError(e instanceof Error ? e.message : '정지에 실패했습니다.');
    } finally {
      setCommunicationPlayBusy(false);
    }
  }, [
    payload,
    showPresentationToastSuccess,
    showPresentationToastError,
    buildCommunicationMonitorSnapshot,
    communicationMultiResponseDraft,
  ]);

  const handleToggleCommunicationMultiResponse = React.useCallback(() => {
    setCommunicationMultiResponseDraft((prev) => {
      const next = !prev;
      const p = payload;
      const sid = (p?.workspaceId ?? '').trim();
      const ownerRef = p?.owner;
      const q = communicationQuestions[communicationQuestionIndex];
      // 재생 중일 때만 즉시 서버에 반영(재생 전이면 다음 재생 시 함께 저장됨)
      if (communicationIsOpen && sid && ownerRef?.kind === 'presentation_ref' && ownerRef.id.trim() && q) {
        const refId = ownerRef.id.trim();
        void playPresentationCommunicationQuestion({
          presentationRefId: refId,
          workspaceId: sid,
          questionId: q.id,
          multiResponse: next,
        }).catch((e) => {
          showPresentationToastError(e instanceof Error ? e.message : '응답 모드 변경에 실패했습니다.');
        });
        setCommunicationQuestions((prevQs) =>
          prevQs.map((item) => (item.id === q.id ? { ...item, multiResponse: next } : item))
        );
      }
      return next;
    });
  }, [communicationIsOpen, payload, communicationQuestions, communicationQuestionIndex, showPresentationToastError]);

  /**
   * 원격(원탭 모니터링 모달)에서의 "다음 질문"은 새 초안 슬롯을 만들지 않고
   * 이미 저장된 질문 사이에서만 이동한다(편집 동작은 프레젠테이션 탭에서만 허용).
   */
  const handleCommunicationQuestionNextSaved = React.useCallback(() => {
    if (communicationQuestionIndex >= communicationQuestions.length - 1) return;
    void applyCommunicationQuestionAtIndex(communicationQuestions, communicationQuestionIndex + 1);
  }, [applyCommunicationQuestionAtIndex, communicationQuestionIndex, communicationQuestions]);

  /** 모니터링 팝업「+」: 질문 페이지「다음」과 같은 방식으로 새(빈) 질문 슬롯으로 이동 */
  const handleCommunicationQuestionAdd = React.useCallback(() => {
    if (communicationQuestionIndex >= communicationQuestions.length) return;
    void applyCommunicationQuestionAtIndex(communicationQuestions, communicationQuestions.length);
  }, [applyCommunicationQuestionAtIndex, communicationQuestionIndex, communicationQuestions]);

  /** 원탭 모니터링 모달 → 프레젠테이션 탭: 재생/정지·질문탐색·추가·저장 원격 명령 수신 */
  React.useEffect(() => {
    if (isRegistryMode) return undefined;
    const ownerRef = payload?.owner;
    const refId = ownerRef?.kind === 'presentation_ref' ? ownerRef.id.trim() : '';
    if (!refId) return undefined;
    return subscribePresentationCommunicationMonitor({
      onCommand: ({ presentationRefId, command, payload: cmdPayload }) => {
        if (presentationRefId !== refId) return;
        if (command === 'addQuestion') {
          handleCommunicationQuestionAdd();
          return;
        }
        if (command === 'saveQuestion') {
          if (!cmdPayload) return;
          // 질문 페이지 저장과 같은 핸들러(같은 저장소) — 실패 메시지는 핸들러가 표시
          void handleSaveCommunicationPrompt({
            question: cmdPayload.question,
            promptText: cmdPayload.promptText,
            answerText: cmdPayload.answerText,
          }).catch(() => undefined);
          return;
        }
        if (command === 'play') void handlePlayCommunicationQuestion();
        else if (command === 'stop') void handleStopCommunicationQuestion();
        else if (command === 'toggleMultiResponse') handleToggleCommunicationMultiResponse();
        else if (command === 'prevQuestion') handleCommunicationQuestionPrev();
        else if (command === 'nextQuestion') handleCommunicationQuestionNextSaved();
      },
    });
  }, [
    isRegistryMode,
    payload,
    handlePlayCommunicationQuestion,
    handleStopCommunicationQuestion,
    handleToggleCommunicationMultiResponse,
    handleCommunicationQuestionPrev,
    handleCommunicationQuestionNextSaved,
    handleCommunicationQuestionAdd,
    handleSaveCommunicationPrompt,
  ]);

  const handleFloatingSave = React.useCallback(async () => {
    if (hasUnsavedCommunicationDraft) {
      try {
        await handleSaveCommunicationPrompt();
      } catch {
        /* 메시지는 handleSaveCommunicationPrompt에서 표시 */
      }
      // 슬라이드도 dirty면 이어서 저장
      if (hasUnsavedSlideDraft) {
        await saveSlidesToServer();
      }
      return;
    }
    await saveSlidesToServer();
  }, [
    hasUnsavedCommunicationDraft,
    hasUnsavedSlideDraft,
    handleSaveCommunicationPrompt,
    saveSlidesToServer,
  ]);

  const handleOfflineHtmlExport = React.useCallback(async () => {
    if (offlineHtmlExporting) return;
    const meta = payload?.meta;
    const createOnSave = payload?.createPresentationRefOnSave;
    let learningMemoNote = (meta?.learningMemoNote ?? '').trim();
    let evaluationMemoNote = (meta?.evaluationMemoNote ?? '').trim();
    if (!learningMemoNote && createOnSave?.learningText) {
      learningMemoNote = parseMemoFieldStorageDeep(createOnSave.learningText).note.trim();
    }
    if (!evaluationMemoNote && createOnSave?.recordEvaluationText) {
      evaluationMemoNote = parseMemoFieldStorageDeep(createOnSave.recordEvaluationText).note.trim();
    }
    const program = isRegistryMode ? '학습내용' : (meta?.programContent ?? '').trim();
    const unit = isRegistryMode ? '단원명' : (meta?.unit ?? '').trim();
    setOfflineHtmlExporting(true);
    try {
      await downloadProgramSlideOfflineHtml({
        includeTitleSlide: !isRegistryMode,
        title: {
          learningTitle: (meta?.programContent ?? '').trim(),
          achievementText: meta?.achievementText ?? '',
          learningMemoNote,
          evaluationMemoNote,
          track: isRegistryMode ? '편제명' : (meta?.templateLabel ?? '').trim(),
          unit,
          pageMain: isRegistryMode ? '쪽수' : (meta?.page ?? '').trim(),
          pageAssist: isRegistryMode ? '' : (meta?.assistantPage ?? '').trim(),
          program,
          weeklyTrackColorHex: isRegistryMode ? '' : (meta?.weeklyGuideTrackColorHex ?? '').trim(),
        },
        layouts: layoutsRef.current,
        extraPageCount: extraPageCountRef.current,
        editableSlideOrder,
        fileNameHint: program || unit || '수업프레젠테이션',
        canvasAspect: readLiveSlideCanvasAspect() ?? undefined,
      });
      showPresentationToastSuccess('오프라인 HTML을 저장했습니다. 더블클릭해서 슬라이드를 넘길 수 있습니다.');
    } catch (e) {
      showPresentationToastError(e instanceof Error ? e.message : '오프라인 HTML 저장에 실패했습니다.');
    } finally {
      setOfflineHtmlExporting(false);
    }
  }, [
    offlineHtmlExporting,
    payload,
    isRegistryMode,
    editableSlideOrder,
    showPresentationToastSuccess,
    showPresentationToastError,
  ]);

  /* 리모컨 수신(수업 프레젠): 같은 브라우저 BroadcastChannel 전용 — Supabase 미사용, 무료 이용 가능 */
  /* 온라인(다른 기기) 리모컨은 Cloudflare 공급자 + 유료 이용권일 때만 — Supabase 공급자면 훅에서 로컬 전용 */
  const { canUseFeature } = usePaidPassGate();
  const canUseRemoteOnline = canUseFeature('remote_control');
  /* 리모컨 '소통 프레젠테이션' 만들기: 반 이름 기본값 = 학습내용 제목 (같은 브라우저 리모컨용) */
  const commPrefillTitle = (payload?.meta?.programContent ?? '').trim();
  React.useEffect(() => {
    try {
      if (commPrefillTitle) window.sessionStorage.setItem('cp-comm-prefill-title', commPrefillTitle);
    } catch {
      /* ignore */
    }
  }, [commPrefillTitle]);

  /* ───── 학생 기기 연결(소통방) — 저장 전 질문 QR · 리모컨 질문 전달 · 응답 표시 ───── */
  /** 질문 페이지(공개 세션)를 쓸 수 있는 저장된 프레젠인지 */
  const canSharePublicForQr =
    payload?.owner?.kind === 'presentation_ref' &&
    !!payload.owner.id?.trim() &&
    !!(payload?.workspaceId ?? '').trim();
  /** 학급 명단(번호 → 멤버). 응답 저장 시 번호로 멤버를 찾는다 */
  const lessonCommMembersByNo = React.useMemo(() => {
    const rows = communicationMembers;
    if (!rows.length) return [];
    const nums = getClassMemberDisplayNumbers(rows);
    return rows.map((member, i) => ({ no: nums[i] ?? i + 1, member }));
  }, [communicationMembers]);
  /** 번호 → 학생 이름 (교사 화면 표시용 — 서버·학생에게 보내지 않음) */
  const lessonCommRosterNames = React.useMemo(
    () => new Map(lessonCommMembersByNo.map(({ no, member }) => [no, String(member.name ?? '').trim()] as [number, string])),
    [lessonCommMembersByNo]
  );
  /** 학급 명단 → 번호별 개인 입장번호(멤버 관리의 6자리). 입장번호가 없는 멤버는 제외. 서버에는 해시로만 저장 */
  const lessonCommRoster = React.useMemo<LessonCommRosterEntry[]>(
    () =>
      lessonCommMembersByNo.flatMap(({ no, member }) => {
        const e = String(member.entryNumber ?? '').trim();
        return isValidEntryNumber(e) ? [{ no, entry: e }] : [];
      }),
    [lessonCommMembersByNo]
  );
  /** 이번 세션에서 질문 QR 을 눌렀는지 — 누르기 전에는 소통방 연결·명단 전송을 하지 않는다 */
  const [lessonCommActivated, setLessonCommActivated] = React.useState(false);
  /** 학생 기기 질문 id → 질문 페이지(DB) 질문 (응답 일괄 저장용) */
  const lessonCommLinkRef = React.useRef(new Map<string, { dbQuestionId: string; cq: CommunicationQuestion | null }>());

  /** 대기 중인 응답 묶음 저장(저장 실패·탭 닫힘 후 다음 기회에) */
  const flushPendingCommBatches = React.useCallback(async () => {
    for (const b of loadPendingCommBatches()) {
      try {
        await savePresentationCommunicationResponsesBatch(b);
        removePendingCommBatch(b.questionId);
      } catch {
        /* 다음 기회에 다시 */
      }
    }
  }, []);

  /** 학생 기기 질문이 닫히거나 다음 질문으로 바뀔 때 → 그 질문 응답을 1회 일괄 저장 */
  const persistLessonCommQuestion = (snap: LessonCommQuestionSnapshot, presentationRefIdOverride?: string) => {
    const link = lessonCommLinkRef.current.get(snap.question.id);
    if (!link) {
      if (snap.answers.length) showPresentationToastSuccess('저장하면 응답이 기록돼요. (지금 응답은 질문 페이지에 남지 않아요)');
      return;
    }
    const sid = (payload?.workspaceId ?? '').trim();
    const ownerRef = payload?.owner;
    const refId = presentationRefIdOverride ?? (ownerRef?.kind === 'presentation_ref' ? ownerRef.id.trim() : '');
    remoteQLog('질문 닫힘 → 응답 일괄 저장', { commQuestionId: snap.question.id, dbQuestionId: link.dbQuestionId, answers: snap.answers.length });
    if (!snap.answers.length || !sid || !refId) return;
    const batch: PendingCommBatch = {
      presentationRefId: refId,
      workspaceId: sid,
      questionId: link.dbQuestionId,
      rows: buildCommResponseRows(snap.answers, link.cq, lessonCommMembersByNo),
      queuedAt: Date.now(),
    };
    if (!batch.rows.length) return;
    /* 저장된 응답을 다시 불러올 때까지 학생 블럭에 그대로 보이게 유지 */
    setLessonCommHeldResponses({ questionId: batch.questionId, rows: commRowsToResponses(batch.rows, batch.questionId) });
    queuePendingCommBatch(batch);
    void savePresentationCommunicationResponsesBatch(batch)
      .then(async () => {
        removePendingCommBatch(batch.questionId);
        const cur = communicationQuestions[communicationQuestionIndex];
        if (cur?.id === batch.questionId) {
          const responses = await listPresentationCommunicationResponses({
            presentationRefId: batch.presentationRefId,
            workspaceId: batch.workspaceId,
            questionId: batch.questionId,
          });
          communicationResponsesCacheRef.current.set(batch.questionId, responses);
          setCommunicationResponses(responses);
        } else {
          communicationResponsesCacheRef.current.delete(batch.questionId);
        }
        setLessonCommHeldResponses((h) => (h?.questionId === batch.questionId ? null : h));
        void flushPendingCommBatches();
      })
      .catch((e) => {
        showPresentationToastError(
          e instanceof Error ? `학생 응답 저장 실패(다음에 다시 시도해요): ${e.message}` : '학생 응답을 저장하지 못했어요. 다음에 다시 시도해요.'
        );
      });
  };
  const lessonComm = useLessonCommRoom({
    canUse: canUseRemoteOnline && !isRegistryMode,
    className: commPrefillTitle,
    presKey: payload?.owner ? `${payload.owner.kind}:${payload.owner.id}` : `t:${commPrefillTitle.trim()}`,
    roster: lessonCommRoster,
    rosterNames: lessonCommRosterNames,
    activated: lessonCommActivated,
    onQuestionEnded: (snap) => persistLessonCommQuestion(snap),
  });
  const [lessonCommPanelHidden, setLessonCommPanelHidden] = React.useState(false);

  /* 학생 기기 응답 → 연결된 질문 페이지 질문의 학생 블럭에 바로 반영(번호 → 학급 멤버). DB 쓰기 없음 */
  const lessonCommLinkedDbQuestionId = lessonComm.question
    ? lessonCommLinkRef.current.get(lessonComm.question.id)?.dbQuestionId ?? ''
    : '';
  const lessonCommUnmatchedAnswers = React.useMemo(() => {
    const nos = new Set(lessonCommMembersByNo.map((m) => m.no));
    return lessonComm.answers.filter((a) => a.number == null || !nos.has(a.number));
  }, [lessonComm.answers, lessonCommMembersByNo]);
  React.useEffect(() => {
    const q = lessonComm.question;
    const link = q ? lessonCommLinkRef.current.get(q.id) : undefined;
    if (!q || !link || !lessonComm.answers.length) {
      setLessonCommLiveResponses(null);
      return;
    }
    const rows = commRowsToResponses(buildCommResponseRows(lessonComm.answers, link.cq, lessonCommMembersByNo), link.dbQuestionId);
    setLessonCommLiveResponses(rows.length ? { questionId: link.dbQuestionId, rows } : null);
  }, [lessonComm.question, lessonComm.answers, lessonCommMembersByNo, lessonCommLinkedDbQuestionId]);
  /** 학생 블럭이 보이는 중(질문 화면 + 연결된 저장 질문)이면 팝업은 블럭에 못 넣는 응답(방 입장번호 학생)만 */
  const lessonCommGridVisible =
    isCommunicationView && !!lessonCommLinkedDbQuestionId && lessonCommLinkedDbQuestionId === currentCommunicationQuestionId;

  /* 명단 최신화(폴링 없음): 이번 세션에서 질문 QR 을 눌러 방을 쓰는 중일 때만
     멤버 관리에서 저장하면(같은 탭 이벤트 / 다른 탭 storage 이벤트) 로컬 캐시만 다시 읽음
     → 명단 서명이 바뀌면 useLessonCommRoom 이 해시 명단을 다시 보내고, 서버는 달라진 번호만 재입장 처리 */
  const lessonCommRoomId = lessonComm.room?.roomId ?? '';
  React.useEffect(() => {
    if (!lessonCommActivated || !lessonCommRoomId || isRegistryMode) return undefined;
    const sid = (payload?.workspaceId ?? '').trim();
    const gradeId = (payload?.contextGradeId ?? payload?.createPresentationRefOnSave?.gradeId ?? '').trim();
    if (!sid || !gradeId) return undefined;
    const reload = () => {
      const layout = loadClassMemberGradeLayoutFromStorage(sid, gradeId);
      if (layout) setCommunicationMembersByGroupId(layout.membersByGroupId);
    };
    const onChanged = (e: Event) => {
      const d = (e as CustomEvent<{ workspaceId?: string; gradeId?: string }>).detail;
      if (d?.workspaceId && d.workspaceId !== sid) return;
      if (d?.gradeId && d.gradeId !== gradeId) return;
      reload();
    };
    const key = classMembersStorageKey(sid, gradeId);
    const onStorage = (e: StorageEvent) => {
      if (e.key === key) reload();
    };
    window.addEventListener(CLASS_MEMBERS_CHANGED_EVENT, onChanged as EventListener);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CLASS_MEMBERS_CHANGED_EVENT, onChanged as EventListener);
      window.removeEventListener('storage', onStorage);
    };
  }, [lessonCommActivated, lessonCommRoomId, isRegistryMode, payload?.workspaceId, payload?.contextGradeId, payload?.createPresentationRefOnSave?.gradeId]);

  /* 질문 페이지에서 재생 중인 저장된 질문 → 학생 기기로 전달 / 정지하면 닫기(닫을 때 응답 일괄 저장) */
  const playingDbQuestion =
    communicationIsOpen && communicationQuestionIndex < communicationQuestions.length
      ? communicationQuestions[communicationQuestionIndex] ?? null
      : null;
  const lessonCommQuestionId = lessonComm.question?.id ?? '';
  /** 이번 재생에서 이미 처리한 DB 질문 id — 패널에서 닫은 뒤 다시 보내지 않도록(정지 → 재생 시 다시 전송) */
  const lessonCommForwardedRef = React.useRef('');
  /* 새로고침 등으로 이번 탭에서 질문 QR 을 누르지 않았어도, 이미 만든 소통방이 있으면 재생할 때 연결(같은 방·같은 링크) */
  const lessonCommAutoActivatingRef = React.useRef(false);
  React.useEffect(() => {
    if (lessonCommActivated || isRegistryMode || !playingDbQuestion || !canUseRemoteOnline) return;
    if (!lessonComm.room || lessonCommAutoActivatingRef.current) return;
    if (lessonCommForwardedRef.current === playingDbQuestion.id) return;
    lessonCommAutoActivatingRef.current = true;
    void lessonComm
      .ensureRoom()
      .then((err) => {
        if (!err) setLessonCommActivated(true);
      })
      .finally(() => {
        lessonCommAutoActivatingRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonCommActivated, isRegistryMode, playingDbQuestion?.id, canUseRemoteOnline, !!lessonComm.room]);
  React.useEffect(() => {
    if (!lessonCommActivated || !lessonComm.connected || isRegistryMode) return;
    const links = lessonCommLinkRef.current;
    const curDb = lessonCommQuestionId ? links.get(lessonCommQuestionId)?.dbQuestionId : undefined;
    if (playingDbQuestion && isPersistableCommQuestionId(playingDbQuestion.id)) {
      if (lessonCommForwardedRef.current === playingDbQuestion.id) return;
      /* 접속 학생 0명이면 보내지 않음 — 재생 중 첫 학생이 접속하면(아래 deps) 그때 전달 */
      if (lessonComm.onlineCount <= 0) return;
      if (curDb === playingDbQuestion.id) {
        lessonCommForwardedRef.current = playingDbQuestion.id;
        return;
      }
      /* 학생 기기 질문 id 는 재생마다 새로 — 같은 질문을 정지 후 다시 재생해도 서버가 중복으로 거부하지 않게.
         DB 질문 id 는 링크로 연결(응답 → 학생 블럭, 닫을 때 일괄 저장) */
      const commId = `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
      links.set(commId, { dbQuestionId: playingDbQuestion.id, cq: playingDbQuestion.question ?? null });
      const sent = lessonComm.sendQuestion(
        communicationToLessonCommQuestion(commId, playingDbQuestion.promptText, playingDbQuestion.question ?? null),
        'page'
      );
      if (sent === false) {
        links.delete(commId);
        return;
      }
      lessonCommForwardedRef.current = playingDbQuestion.id;
      setLessonCommPanelHidden(false);
      return;
    }
    /* 질문 페이지 질문이 정지됨 → 학생 기기 질문도 닫기 (리모컨 질문 중 저장 전 단계는 링크가 없어 그대로) */
    lessonCommForwardedRef.current = '';
    if (curDb) lessonComm.closeQuestion(lessonCommQuestionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonCommActivated, lessonComm.connected, isRegistryMode, playingDbQuestion?.id, lessonCommQuestionId, lessonComm.onlineCount > 0]);

  /* 탭을 닫거나 새로고침: 열린 질문 응답을 대기열에 남겨 다음에 저장 */
  const lessonCommSnapshot = lessonComm.snapshot;
  React.useEffect(() => {
    if (!lessonCommActivated) return undefined;
    const onUnload = () => {
      const snap = lessonCommSnapshot();
      if (!snap?.answers.length) return;
      const link = lessonCommLinkRef.current.get(snap.question.id);
      const sid = (payload?.workspaceId ?? '').trim();
      const ownerRef = payload?.owner;
      if (!link || !sid || ownerRef?.kind !== 'presentation_ref' || !ownerRef.id.trim()) return;
      queuePendingCommBatch({
        presentationRefId: ownerRef.id.trim(),
        workspaceId: sid,
        questionId: link.dbQuestionId,
        rows: buildCommResponseRows(snap.answers, link.cq, lessonCommMembersByNo),
        queuedAt: Date.now(),
      });
    };
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pagehide', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pagehide', onUnload);
    };
  }, [lessonCommActivated, lessonCommSnapshot, payload?.workspaceId, payload?.owner, lessonCommMembersByNo]);

  /** 개별 디바이스 프레젠테이션 연결(질문 페이지 「연결」 버튼): 소통방(없으면 만들기) QR — PLUS(리모트 이용권) 필요. 누른 뒤부터 연결·명단 전송 */
  const openLessonCommQr = React.useCallback(async () => {
    if (!canUseRemoteOnline) {
      setPublicQrError('개별 디바이스 프레젠테이션은 PLUS 이용권에서 사용할 수 있어요.');
      window.setTimeout(() => setPublicQrError(''), 4200);
      return;
    }
    setQuestionQrLoading(true);
    setPublicQrError('');
    try {
      /* 명단이 아직 없을 때만 1회 조회(이미 불러온 명단은 멤버 관리 저장 이벤트로 최신 유지) */
      if (!communicationGroups.length) await refreshCommunicationBoard();
      const err = await lessonComm.ensureRoom();
      if (err) {
        setPublicQrError(
          err === 'not_paid'
            ? '개별 디바이스 프레젠테이션은 PLUS 이용권에서 사용할 수 있어요.'
            : err === 'unauthorized'
              ? '로그인 후 사용할 수 있어요.'
              : `개별 디바이스 프레젠테이션을 만들지 못했어요 (${err}).`
        );
        window.setTimeout(() => setPublicQrError(''), 4200);
        return;
      }
      setLessonCommActivated(true);
      void flushPendingCommBatches();
      setPublicQrOverlay({ publicUrl: '', expiresAt: '', mode: 'comm' });
    } finally {
      setQuestionQrLoading(false);
    }
  }, [canUseRemoteOnline, communicationGroups.length, refreshCommunicationBoard, lessonComm, flushPendingCommBatches]);

  /**
   * 질문 QR 하나로 통일 — PLUS 전용(이용권별 권한 'remote_control', 관리자 FREE/PLUS 설정과 동일 기준):
   * - PLUS: 저장 여부와 관계없이 학생 기기 연결(소통방) QR. 저장된 프레젠은 질문을 닫을 때 응답이 질문 페이지에 기록됨
   * - 무료: QR을 열지 않고 PLUS 안내만 표시(기존 질문 페이지 공개 QR로 대체하지 않음)
   * - 등록(템플릿) 모드는 소통방을 쓰지 않으므로 기존 질문 페이지 QR 유지(PLUS일 때만)
   */
  const handleOpenQuestionQr = React.useCallback(() => {
    if (!canUseRemoteOnline) return openLessonCommQr();
    if (isRegistryMode && canSharePublicForQr) return openQuestionPageQr();
    return openLessonCommQr();
  }, [canUseRemoteOnline, isRegistryMode, canSharePublicForQr, openQuestionPageQr, openLessonCommQr]);

  /* 리모컨 슬라이드 제어: 0 = 타이틀, 1..n = 슬라이드, 마지막 = 소통 화면 */
  const remoteSlideTotal = totalSlides + 1;
  const remoteSlideState = React.useMemo(
    () => ({
      index: Math.max(0, Math.min(slideIndex, remoteSlideTotal - 1)),
      total: remoteSlideTotal,
      label: isCommunicationView ? '소통' : slideIndex === 0 ? '타이틀' : undefined,
    }),
    [slideIndex, remoteSlideTotal, isCommunicationView]
  );
  const remoteGoTo = React.useCallback(
    (target: number) => {
      const i = Math.max(0, Math.min(target, totalSlides));
      if (i >= totalSlides) {
        setIsCommunicationView(true);
        return;
      }
      setIsCommunicationView(false);
      setSelectedEditablePageIndex(i === 0 ? null : editableSlideOrder[i - 1] ?? null);
    },
    [totalSlides, editableSlideOrder]
  );
  const onRemoteSlideNav = React.useCallback(
    (dir: 'prev' | 'next' | 'first' | 'last' | 'goto' | 'sync', index?: number) => {
      if (dir === 'prev') goPrev();
      else if (dir === 'next') goNext();
      else if (dir === 'first') remoteGoTo(0);
      else if (dir === 'last') remoteGoTo(totalSlides);
      else if (dir === 'goto' && index != null) remoteGoTo(index);
    },
    [goPrev, goNext, remoteGoTo, totalSlides]
  );
  const { overlays: remoteOverlays } = useRemoteReceiver({
    presentationKind: 'lesson',
    remoteEnabled: canUseRemoteOnline,
    title: (payload?.meta?.programContent ?? '').trim(),
    enabled: !isRegistryMode && !!payload,
    /* 리모컨 음악은 슬라이드 BGM 플레이어로 재생 → 두 곡이 겹치지 않고(나중 선택이 우선) 버튼 UI도 함께 펼쳐짐 */
    bgmController: slideRemoteBgm,
    slideState: isRegistryMode ? null : remoteSlideState,
    onSlideNav: isRegistryMode ? undefined : onRemoteSlideNav,
  });

  if (!k.trim() && !isRegistryMode) {
    return (
      <>
        <div className="cp-program-slide-root" data-slide-surface="light" role="alert">
          <ProgramSlideTopBar
            editMode={editMode}
            onEditModeChange={setEditMode}
            slideTimerActive={slideTimerActive}
            onSlideTimerButtonClick={handleSlideTimerNavButtonClick}
            onSlideBgmButtonClick={() => setSlideBgmModalOpen(true)}
            slideBgmTransport={slideBgmTransport}
            onSlideBgmPauseToggle={handleSlideBgmPauseToggle}
            onSlideBgmStop={handleSlideBgmStop}
            onSlideDiceButtonClick={() => setSlideDiceModalOpen(true)}
            slideLiveCaptionSupported={slideLiveCaptionSupported}
            slideLiveCaptionOn={slideLiveCaptionOn}
            onSlideLiveCaptionMicClick={
              slideLiveCaptionSupported ? handleSlideLiveCaptionMicClick : undefined
            }
            onSlideLiveCaptionStop={
              slideLiveCaptionSupported ? handleSlideLiveCaptionStopClick : undefined
            }
          />
          <p className="cp-program-slide-error">표시할 데이터가 없습니다. 연간진도표 또는 프로그램 표에서 행을 선택한 뒤 Shift+U를 눌러 주세요.</p>
        </div>
        <ProgramSlideTimerModal
          open={slideTimerModalOpen}
          draftSeconds={slideTimerDraftSec}
          onClose={() => setSlideTimerModalOpen(false)}
          onAddSeconds={addSlideTimerDraftSeconds}
          onReset={resetSlideTimerDraft}
          onStart={startSlideTimerFromModal}
        />
        <ProgramSlideBgmModal open={slideBgmModalOpen} onClose={() => setSlideBgmModalOpen(false)} onPick={handleSlideBgmPick} />
        <ProgramSlideDiceModal
          open={slideDiceModalOpen}
          onClose={() => setSlideDiceModalOpen(false)}
          memberLookup={slideDiceMemberLookup}
        />
      </>
    );
  }

  if (!payload && !isRegistryMode) {
    return (
      <>
        <div className="cp-program-slide-root" data-slide-surface="light" role="alert">
          <ProgramSlideTopBar
            editMode={editMode}
            onEditModeChange={setEditMode}
            slideTimerActive={slideTimerActive}
            onSlideTimerButtonClick={handleSlideTimerNavButtonClick}
            onSlideBgmButtonClick={() => setSlideBgmModalOpen(true)}
            slideBgmTransport={slideBgmTransport}
            onSlideBgmPauseToggle={handleSlideBgmPauseToggle}
            onSlideBgmStop={handleSlideBgmStop}
            onSlideDiceButtonClick={() => setSlideDiceModalOpen(true)}
            slideLiveCaptionSupported={slideLiveCaptionSupported}
            slideLiveCaptionOn={slideLiveCaptionOn}
            onSlideLiveCaptionMicClick={
              slideLiveCaptionSupported ? handleSlideLiveCaptionMicClick : undefined
            }
            onSlideLiveCaptionStop={
              slideLiveCaptionSupported ? handleSlideLiveCaptionStopClick : undefined
            }
          />
          <p className="cp-program-slide-error">
            슬라이드 데이터를 찾을 수 없습니다. 같은 브라우저에서 열었는지 확인하거나, 다시 Shift+U로 열어 주세요.
          </p>
        </div>
        <ProgramSlideTimerModal
          open={slideTimerModalOpen}
          draftSeconds={slideTimerDraftSec}
          onClose={() => setSlideTimerModalOpen(false)}
          onAddSeconds={addSlideTimerDraftSeconds}
          onReset={resetSlideTimerDraft}
          onStart={startSlideTimerFromModal}
        />
        <ProgramSlideBgmModal open={slideBgmModalOpen} onClose={() => setSlideBgmModalOpen(false)} onPick={handleSlideBgmPick} />
        <ProgramSlideDiceModal
          open={slideDiceModalOpen}
          onClose={() => setSlideDiceModalOpen(false)}
          memberLookup={slideDiceMemberLookup}
        />
      </>
    );
  }

  const safePayload = payload ?? {
    v: 1 as const,
    workspaceId: registryWorkspaceId,
    scopeId: `registry:${registryWorkspaceId}`,
    annualCellKey: undefined,
    contextGradeId: undefined,
    academicYear: new Date().getFullYear(),
    termNumber: 1 as const,
    owner: null,
    extraSlideBodies: [''],
    meta: {
      templateLabel: '',
      unit: '',
      page: '',
      assistantPage: '',
      programContent: '',
      achievementText: '',
    },
  };
  const { meta, owner, workspaceId } = safePayload;
  const track = isRegistryMode ? '편제명' : (meta.templateLabel ?? '').trim();
  const unit = isRegistryMode ? '단원명' : (meta.unit ?? '').trim();
  const program = isRegistryMode ? '학습내용' : (meta.programContent ?? '').trim();
  const pageMain = isRegistryMode ? '쪽수' : (meta.page ?? '').trim();
  const pageAssist = isRegistryMode ? '' : (meta.assistantPage ?? '').trim();
  const weeklyTrackHex = normalizeWeeklyGuideColorHex(meta.weeklyGuideTrackColorHex ?? '');
  const trackSwatchBg = weeklyTrackHex ? lightenRgbHexTowardWhite(weeklyTrackHex, SLIDE_TRACK_BG_LIGHTEN) : '';
  const trackSwatchStyle: React.CSSProperties | undefined =
    !isRegistryMode && weeklyTrackHex
      ? {
          backgroundColor: trackSwatchBg,
          color: '#1f2937',
          borderColor: 'rgba(0, 0, 0, 0.22)',
        }
      : undefined;

  const isFirst = !isRegistryMode && slideIndex === 0;
  const layoutPickerPageHasAppliedLayout =
    layoutPickerPageIndex != null &&
    !!(layouts[layoutPickerPageIndex]?.appliedLayoutTemplateId ?? '').trim();
  const createOnSave = safePayload.createPresentationRefOnSave;
  let titleLearningMemoNote = (meta.learningMemoNote ?? '').trim();
  let titleEvaluationMemoNote = (meta.evaluationMemoNote ?? '').trim();
  if (!titleLearningMemoNote && createOnSave?.learningText) {
    titleLearningMemoNote = parseMemoFieldStorageDeep(createOnSave.learningText).note.trim();
  }
  if (!titleEvaluationMemoNote && createOnSave?.recordEvaluationText) {
    titleEvaluationMemoNote = parseMemoFieldStorageDeep(createOnSave.recordEvaluationText).note.trim();
  }
  const persistedLearningTitle = (meta.programContent ?? '').trim();
  const persistedAchievementText = (meta.achievementText ?? '').trim();
  const titlePanelProps = {
    learningTitle: sessionTitleOverride ?? persistedLearningTitle,
    achievementText: sessionAchievementOverride ?? persistedAchievementText,
    learningMemoNote: sessionLearningMemoOverride ?? titleLearningMemoNote,
    evaluationMemoNote: sessionEvaluationOverride ?? titleEvaluationMemoNote,
    titleSessionEditable: !isRegistryMode,
    onSessionTitleChange: setSessionTitleOverride,
    onSessionAchievementChange: setSessionAchievementOverride,
    onSessionLearningMemoChange: setSessionLearningMemoOverride,
    onSessionEvaluationChange: setSessionEvaluationOverride,
  };
  const canCreateOwnerOnSave = !!safePayload.createPresentationRefOnSave;
  /** DB에 저장할 owner·바인딩이 있을 때만 저장 FAB 노출 */
  const canPersistSlides =
    isRegistryMode ||
    ((!!owner || canCreateOwnerOnSave) &&
      !!workspaceId.trim() &&
      !!safePayload.scopeId?.trim());
  /**
   * 예전에는 저장 불가 세션을 캔버스 readOnly로 잠갔으나,
   * 학습내용/DB 행이 없어도 로컬에서 프레젠을 쓸 수 있도록 편집은 허용한다.
   */
  const canvasReadOnly = false;
  const isLocalOnlySlideSession = !isRegistryMode && !canPersistSlides;

  const slideWebcamNavTools =
    slideWebcamDesired ? (
      <>
        <div className="cp-program-slide-webcam-source-wrap cp-program-slide-webcam-source-wrap--topnav" ref={slideWebcamSourceMenuRef}>
          <button
            type="button"
            className="cp-program-slide-webcam-nav-block-btn cp-program-slide-webcam-nav-block-btn--transport"
            disabled={slideWebcamLoading}
            title={
              slideWebcamLoading
                ? '미디어 연결 중…'
                : '카메라·화면 입력 바꾸기'
            }
            aria-label="카메라 또는 화면 입력 바꾸기"
            aria-expanded={slideWebcamSourceMenuOpen}
            aria-haspopup="true"
            onClick={(e) => {
              e.stopPropagation();
              setSlideWebcamSourceMenuOpen((open) => {
                const next = !open;
                if (next && navigator.mediaDevices?.enumerateDevices) {
                  void navigator.mediaDevices.enumerateDevices().then((ds) => {
                    setSlideWebcamDeviceList(filterVideoInputDevices(ds));
                  });
                }
                return next;
              });
            }}
          >
            <img src="/change.svg" alt="" className="cp-program-slide-webcam-swap-icon-topnav" width={15} height={15} aria-hidden />
          </button>
          {slideWebcamSourceMenuOpen ? (
            <div className="cp-program-slide-webcam-source-menu cp-program-slide-webcam-source-menu--topnav" role="menu" aria-label="비디오 입력">
              <div className="cp-program-slide-webcam-source-menu-section" role="presentation">
                {slideWebcamDeviceList.length === 0 ? (
                  <div className="cp-program-slide-webcam-source-menu-empty">장치 목록을 불러오는 중…</div>
                ) : (
                  slideWebcamDeviceList.map((d) => {
                    const label = simplifyVideoInputDeviceLabel(d.label.trim() || '카메라');
                    const selected =
                      slideWebcamCaptureMode === 'camera' && slideWebcamCameraDeviceId === d.deviceId;
                    return (
                      <button
                        key={d.deviceId}
                        type="button"
                        role="menuitem"
                        className={'cp-program-slide-webcam-source-item' + (selected ? ' is-selected' : '')}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSlideWebcamCaptureMode('camera');
                          setSlideWebcamCameraDeviceId(d.deviceId);
                          setSlideWebcamSourceMenuOpen(false);
                        }}
                      >
                        {label}
                      </button>
                    );
                  })
                )}
              </div>
              <div className="cp-program-slide-webcam-source-menu-divider" role="separator" />
              <button
                type="button"
                role="menuitem"
                className={
                  'cp-program-slide-webcam-source-item' + (slideWebcamCaptureMode === 'screen' ? ' is-selected' : '')
                }
                onClick={(e) => {
                  e.stopPropagation();
                  setSlideWebcamCaptureMode('screen');
                  setSlideWebcamSourceMenuOpen(false);
                }}
              >
                화면 공유
              </button>
              <button
                type="button"
                role="menuitem"
                className={
                  'cp-program-slide-webcam-source-item' +
                  (slideWebcamCaptureMode === 'screen_camera' ? ' is-selected' : '')
                }
                onClick={(e) => {
                  e.stopPropagation();
                  setSlideWebcamCaptureMode('screen_camera');
                  setSlideWebcamSourceMenuOpen(false);
                }}
              >
                화면 공유 + 웹캠
              </button>
            </div>
          ) : null}
        </div>
        <span className="cp-program-slide-bgm-nav-block-gap" aria-hidden />
        <button
          type="button"
          data-tutorial-slide="webcam-pause"
          className="cp-program-slide-webcam-nav-block-btn cp-program-slide-webcam-nav-block-btn--transport"
          disabled={slideWebcamLoading}
          title={
            slideWebcamLoading
              ? '미디어 연결 중…'
              : frozenFrameUrl
                ? '미리보기 재생'
                : '미리보기 일시정지'
          }
          aria-label={
            slideWebcamLoading
              ? '미디어 연결 중'
              : frozenFrameUrl
                ? '미리보기 재생'
                : '미리보기 일시정지'
          }
          aria-pressed={!!frozenFrameUrl}
          onClick={(e) => {
            e.stopPropagation();
            toggleWebcamFreeze();
          }}
        >
          <span className="cp-program-slide-webcam-btn-icon cp-program-slide-bgm-nav-block-icon--compact" aria-hidden>
            {frozenFrameUrl ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M8 5.14v13.72c0 .89 1.02 1.39 1.71.83l9.06-6.86a1 1 0 0 0 0-1.6L9.71 4.31A1 1 0 0 0 8 5.14Z" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <rect x="7" y="6" width="3.5" height="12" rx="1" />
                <rect x="13.5" y="6" width="3.5" height="12" rx="1" />
              </svg>
            )}
          </span>
        </button>
        <span className="cp-program-slide-bgm-nav-block-gap" aria-hidden />
        <button
          type="button"
          data-tutorial-slide="webcam-stop"
          className="cp-program-slide-webcam-nav-block-btn cp-program-slide-webcam-nav-block-btn--transport"
          title="미리보기 정지(끄기)"
          aria-label="미리보기 정지"
          onClick={(e) => {
            e.stopPropagation();
            setSlideWebcamDesired(false);
            setSlideWebcamSourceMenuOpen(false);
          }}
        >
          <span className="cp-program-slide-webcam-btn-icon cp-program-slide-bgm-nav-block-icon--compact" aria-hidden>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
              <rect x="7" y="7" width="10" height="10" rx="1.5" />
            </svg>
          </span>
        </button>
      </>
    ) : null;

  const registeredPageButtons =
    !isRegistryMode && registeredPages.length > 0 ? (
      <div className="cp-program-slide-registered-page-buttons-scroll">
        <div className="cp-program-slide-registered-page-buttons" role="group" aria-label="등록 슬라이드 페이지">
          {registeredPages.map((page) => {
            const active = openedRegisteredPage?.id === page.id;
            return (
              <button
                key={page.id}
                type="button"
                className={'cp-theme-toggle cp-program-slide-registered-page-btn' + (active ? ' is-active' : '')}
                title={active ? `${page.name} 닫기` : `${page.name} 열기`}
                onClick={() => toggleRegisteredPageInline(page)}
              >
                {page.name}
              </button>
            );
          })}
        </div>
      </div>
    ) : null;

  const topBarProps = {
    titleText: isRegistryMode ? (isLayoutRegistryMode ? '슬라이드 레이아웃 등록' : '슬라이드 페이지 등록') : '프로그램 내용 슬라이드',
    editMode,
    onEditModeChange: (next: boolean) => {
      if (isRegistryMode) return;
      if (openedRegisteredPage) return;
      setEditMode(next);
    },
    lockEditModeToggle: isRegistryMode || !!openedRegisteredPage,
    registeredPageButtons,
    /** 저장된 프레젠: 질문 페이지 QR · 저장 전: 소통방(학생 기기) QR */
    showQuestionQrButton: false,
    questionQrLoading,
    onOpenQuestionQr: () => void handleOpenQuestionQr(),
    /** 저장 전에도 미리보기 가능 — 공개 QR과 달리 항상 표시 */
    showSlideBoardButton: isRegistryMode ? false : true,
    slideBoardDrawOpen,
    onSlideBoardButtonClick: isRegistryMode ? undefined : toggleSlideBoardDraw,
    showSlideWebcamButton: isRegistryMode ? false : true,
    slideWebcamOn: slideWebcamDesired,
    slideWebcamLoading,
    onSlideWebcamCameraClick: isRegistryMode ? undefined : handleSlideWebcamNavCameraClick,
    slideTimerActive,
    onSlideTimerButtonClick: isRegistryMode ? undefined : handleSlideTimerNavButtonClick,
    onSlideBgmButtonClick: isRegistryMode ? undefined : () => setSlideBgmModalOpen(true),
    slideBgmTransport,
    onSlideBgmPauseToggle: isRegistryMode ? undefined : handleSlideBgmPauseToggle,
    onSlideBgmStop: isRegistryMode ? undefined : handleSlideBgmStop,
    onSlideDiceButtonClick: isRegistryMode ? undefined : () => setSlideDiceModalOpen(true),
    slideWebcamTools: isRegistryMode ? null : slideWebcamNavTools,
    slideLiveCaptionSupported,
    slideLiveCaptionOn,
    onSlideLiveCaptionMicClick:
      !isRegistryMode && slideLiveCaptionSupported ? handleSlideLiveCaptionMicClick : undefined,
    onSlideLiveCaptionStop:
      !isRegistryMode && slideLiveCaptionSupported ? handleSlideLiveCaptionStopClick : undefined,
    offlineHtmlExporting,
    onOfflineHtmlExport: handleOfflineHtmlExport,
  };

  const footerPageTabCount = editableSlideOrder.length;
  const footerDotsHasTitleTab = !isRegistryMode;
  const footerDotsHasCommunicationTab = !isRegistryMode;
  const footerDotsScrollable = isSlideFooterDotsScrollable(footerPageTabCount);
  const footerDotsScrollStyle = slideFooterDotsScrollStyle(
    footerPageTabCount,
    footerDotsHasTitleTab,
    isRegistryMode,
    footerDotsHasCommunicationTab
  );

  const dots =
    totalSlides > 0 ? (
      <div
        ref={footerDotsScrollRef}
        className={
          'cp-program-slide-dots-scroll' +
          (footerDotsScrollable ? ' is-scrollable' : '') +
          (footerDotsHasTitleTab ? ' has-title' : '')
        }
        style={footerDotsScrollStyle}
      >
        <div className="cp-program-slide-dots" role="tablist" aria-label="슬라이드 페이지">
        {!isRegistryMode ? (
          <button
            type="button"
            role="tab"
            aria-selected={!isCommunicationView && slideIndex === 0}
            aria-label={`타이틀 슬라이드${!isCommunicationView && slideIndex === 0 ? ' (현재)' : ''}`}
            className={
              'cp-program-slide-dot is-title' +
              (!isCommunicationView && slideIndex === 0 ? ' is-active' : '')
            }
            onClick={() => openSlidePage(null)}
            title="타이틀 슬라이드"
            data-footer-slide-tab="title"
          >
            <span className="cp-program-slide-dot-label">타이틀</span>
          </button>
        ) : null}
        <DndContext
          sensors={slideDragSensors}
          collisionDetection={closestCenter}
          onDragStart={handleSlideDragStart}
          onDragOver={handleSlideDragOver}
          onDragEnd={handleSlideDragEnd}
          onDragCancel={() => setDraggingSlideId(null)}
        >
          <SortableContext
            items={editableSlideOrder.map((pageIndex) => slideSortableId(pageIndex))}
            strategy={horizontalListSortingStrategy}
          >
            {editableSlideOrder.map((pageIndex, displayIndex) => (
              <SortableSlideTab
                key={`${slideSortableId(pageIndex)}@${displayIndex}`}
                id={slideSortableId(pageIndex)}
                tabKey={footerSlideTabKey(pageIndex)}
                isActive={!isCommunicationView && selectedEditablePageIndex === pageIndex}
                isDraggingState={draggingSlideId === slideSortableId(pageIndex)}
                label={
                  isRegistryMode
                    ? registryPageNamesByIndex[pageIndex] ??
                      programSlideRegistryFallbackName(displayIndex)
                    : String(displayIndex + 1)
                }
                onClick={() => openSlidePage(pageIndex)}
                onDoubleClick={
                  editMode || isRegistryMode ? () => openLayoutPicker(pageIndex) : undefined
                }
              />
            ))}
          </SortableContext>
        </DndContext>
        {!isRegistryMode ? (
          <button
            type="button"
            role="tab"
            aria-selected={isCommunicationView}
            aria-label={`질문${isCommunicationView ? ' (현재)' : ''}`}
            className={
              'cp-program-slide-dot is-communication' + (isCommunicationView ? ' is-active' : '')
            }
            onClick={() => openCommunicationView()}
            title="질문"
            data-footer-slide-tab="communication"
            data-tutorial-slide="tab-question"
          >
            <span className="cp-program-slide-dot-label">질문</span>
          </button>
        ) : null}
      </div>
      </div>
    ) : null;

  const slideTimerRemainingMs =
    slideTimerActive && slideTimerTotalMs > 0
      ? slideTimerPaused
        ? slideTimerPausedRemainingMs
        : Math.max(0, slideTimerEndsAtRef.current - Date.now())
      : 0;
  const slideTimerFillPct =
    slideTimerActive && slideTimerTotalMs > 0
      ? Math.min(100, Math.max(0, (slideTimerRemainingMs / slideTimerTotalMs) * 100))
      : 0;
  const slideTimerRemainingSecLabel = Math.max(0, Math.ceil(slideTimerRemainingMs / 1000));
  const slideTimerRemainingFloorSec = Math.max(0, Math.floor(slideTimerRemainingMs / 1000));
  const slideTimerMmSsLabel = `${String(Math.floor(slideTimerRemainingFloorSec / 60)).padStart(2, '0')}:${String(
    slideTimerRemainingFloorSec % 60
  ).padStart(2, '0')}`;

  const isSlideTimerLastMinute =
    slideTimerActive &&
    slideTimerTotalMs > 60_000 &&
    slideTimerRemainingMs > 0 &&
    slideTimerRemainingMs <= 60_000;

  const renderSlideWebcamBackgroundLayer = (
    extraLayerClass: string,
    options?: { clickToFreeze?: boolean }
  ) => {
    const clickToFreeze = options?.clickToFreeze ?? true;
    return (
    <div
      className={'cp-program-slide-webcam-layer cp-program-slide-webcam-layer--bg' + extraLayerClass}
      role="region"
      aria-label="웹캠·화면 배경"
    >
      {slideWebcamCaptureMode === 'screen_camera' ? (
        <div className="cp-program-slide-webcam-screen-fill" aria-hidden>
          <video
            ref={screenVideoRef}
            className="cp-program-slide-webcam-video cp-program-slide-webcam-video--contain"
            autoPlay
            playsInline
            muted
          />
        </div>
      ) : null}
      <div
        className={
          'cp-program-slide-webcam-video-crop' +
          (slideWebcamCaptureMode === 'screen_camera' ? ' cp-program-slide-webcam-video-crop--pip' : '') +
          (frozenFrameUrl ? ' is-frozen-preview' : '') +
          (clickToFreeze ? '' : ' cp-program-slide-webcam-video-crop--no-click-freeze')
        }
        role={frozenFrameUrl ? 'img' : clickToFreeze ? 'button' : undefined}
        tabIndex={frozenFrameUrl || !clickToFreeze ? -1 : 0}
        title={
          frozenFrameUrl
            ? '일시정지됨. 상단 네비 웹캠 블록에서 재생합니다.'
            : clickToFreeze
              ? slideWebcamCaptureMode === 'screen_camera'
                ? '클릭: 미리보기 일시정지 (상단 네비 웹캠 블록에서도 가능)'
                : '클릭: 미리보기 일시정지 (상단 네비 웹캠 블록에서도 가능)'
              : '웹캠 미리보기'
        }
        aria-label={
          frozenFrameUrl
            ? '일시정지된 미리보기. 상단 네비 웹캠 블록에서 재생'
            : clickToFreeze
              ? slideWebcamCaptureMode === 'screen_camera'
                ? '웹캠 미리보기 일시정지'
                : '미리보기 일시정지'
              : '웹캠 미리보기'
        }
        aria-pressed={clickToFreeze ? !!frozenFrameUrl : undefined}
        onClick={
          clickToFreeze
            ? () => {
                if (!frozenFrameUrl) toggleWebcamFreeze();
              }
            : undefined
        }
        onKeyDown={
          clickToFreeze
            ? (e) => {
                if (frozenFrameUrl) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleWebcamFreeze();
                }
              }
            : undefined
        }
      >
        <video
          ref={webcamVideoRef}
          className={
            'cp-program-slide-webcam-video' +
            (slideWebcamCaptureMode === 'screen' ? ' cp-program-slide-webcam-video--contain' : '') +
            (webcamViewTransformed ? ' cp-program-slide-webcam-video--view-transformed' : '')
          }
          style={webcamViewStyle}
          autoPlay
          playsInline
          muted
        />
        {frozenFrameUrl ? (
          <img
            src={frozenFrameUrl}
            alt=""
            className={
              'cp-program-slide-webcam-video cp-program-slide-webcam-freeze-overlay' +
              (webcamViewTransformed ? ' cp-program-slide-webcam-video--view-transformed' : '')
            }
            style={webcamViewStyle}
          />
        ) : null}
      </div>
      {slideWebcamLoading ? (
        <div className="cp-program-slide-webcam-loading" aria-live="polite">
          미디어 연결 중…
        </div>
      ) : null}
    </div>
    );
  };

  const tutorialWorkspaceId = (registryWorkspaceId || payload?.workspaceId || '').trim();

  return (
    <TutorialProvider workspaceId={tutorialWorkspaceId}>
    <PresentationTutorialBootstrap />
    <div
      className="cp-program-slide-root"
      data-slide-surface="light"
      data-tutorial-slide="page"
      style={
        {
          '--cp-slide-page-bg': displayPageBackgroundColorHex,
          '--cp-slide-default-text': slideRootDefaultTextHex,
        } as React.CSSProperties
      }
    >
      <ProgramSlideTopBar {...topBarProps} />
      {slideLiveCaptionError ? (
        <div className="cp-program-slide-live-caption-error" role="alert">
          {slideLiveCaptionError}
        </div>
      ) : null}

      <div
        className={
          'cp-program-slide-hero cp-program-slide-hero--headline' +
          (slideTimerActive ? ' is-timer-counting' : '') +
          (slideLiveCaptionOn ? ' is-live-caption' : '')
        }
      >
        <div
          className="cp-program-slide-hero-inner"
          role={slideTimerActive ? 'button' : undefined}
          tabIndex={slideTimerActive ? 0 : undefined}
          aria-label={
            slideTimerActive
              ? slideTimerPaused
                ? '타이머 재개 (단원·학습내용 영역)'
                : '타이머 일시정지 (단원·학습내용 영역)'
              : undefined
          }
          onClick={slideTimerActive ? () => toggleSlideTimerHeroPause() : undefined}
          onKeyDown={
            slideTimerActive
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleSlideTimerHeroPause();
                  }
                }
              : undefined
          }
        >
          <div className="cp-program-slide-hero-body">
            <div className="cp-program-slide-hero-meta-row" role="group" aria-label="편제, 단원, 쪽수">
              {track ? (
                <span
                  className={
                    'cp-program-slide-hero-track' +
                    (isRegistryMode ? ' cp-program-slide-hero-track--registry-placeholder' : '') +
                    (weeklyTrackHex && !isRegistryMode ? ' cp-program-slide-hero-track--weekly-swatch' : '')
                  }
                  style={trackSwatchStyle}
                >
                  {track}
                </span>
              ) : null}
              <div
                className={
                  'cp-program-slide-hero-unit' + (!unit && !isRegistryMode ? ' is-placeholder' : '')
                }
              >
                {unit || '단원'}
              </div>
              {pageMain || pageAssist ? (
                <div className="cp-program-slide-hero-pages" aria-label="쪽수 및 보조쪽수">
                  {pageMain ? <span className="cp-program-slide-hero-page-main">{pageMain}</span> : null}
                  {pageMain && pageAssist ? <span className="cp-program-slide-hero-page-gap"> </span> : null}
                  {pageAssist ? <span className="cp-program-slide-hero-page-assist">{pageAssist}</span> : null}
                </div>
              ) : null}
            </div>
            <div
              className={
                'cp-program-slide-hero-program' + (!program && !isRegistryMode ? ' is-placeholder' : '')
              }
            >
              {program || '학습내용'}
            </div>
          </div>
          {slideLiveCaptionOn ? (
            <div className="cp-program-slide-hero-caption-dim-overlay" aria-hidden />
          ) : null}
          {slideTimerActive && !slideLiveCaptionOn ? (
            <div className="cp-program-slide-hero-timer-dim-overlay" aria-hidden />
          ) : null}
          {slideTimerActive ? (
            <div
              className="cp-program-slide-hero-timer-time"
              data-tutorial-slide="timer-running"
              aria-hidden
            >
              {slideTimerMmSsLabel}
            </div>
          ) : null}
          {slideLiveCaptionOn ? (
            <div className="cp-program-slide-hero-live-caption" aria-live="polite" aria-atomic="true">
              <div
                className={
                  'cp-program-slide-hero-live-caption-window' +
                  (slideCaptionWinMode === 'single-center' ? ' is-caption-single-line' : '') +
                  (slideCaptionWinMode === 'tail' ? ' is-caption-tail' : '')
                }
              >
                <div ref={slideCaptionInnerRef} className="cp-program-slide-hero-live-caption-inner">
                  {slideLiveCaptionText.trim() ? slideLiveCaptionText : '\u00a0'}
                </div>
              </div>
            </div>
          ) : null}
        </div>
        {slideTimerActive ? (
          <div className="cp-program-slide-hero-timer-slot" data-tutorial-slide="timer-running-bar">
            <div
              className="cp-program-slide-timer-countdown cp-program-slide-timer-countdown--in-hero"
              role="timer"
              aria-live="polite"
              aria-label={`남은 시간 ${slideTimerMmSsLabel} (${slideTimerRemainingSecLabel}초)`}
            >
              <div className="cp-program-slide-timer-countdown-track">
                <div
                  className={
                    'cp-program-slide-timer-countdown-fill' +
                    (isSlideTimerLastMinute ? ' is-last-minute' : '')
                  }
                  style={{ width: `${slideTimerFillPct}%` }}
                />
                {slideTimerPaused ? (
                  <span
                    className={
                      'cp-program-slide-timer-countdown-pause' +
                      (slideTimerFillPct < 50 ? ' is-on-track' : '')
                    }
                    aria-hidden
                  />
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="cp-program-slide-frame">
        {loadError ? (
          <p className="cp-program-slide-load-error" role="alert">
            슬라이드 불러오기 오류: {loadError}
          </p>
        ) : null}
        <div className="cp-program-slide-body-scroll-wrap">
          <div
            ref={bindSlideBodyScrollEl}
            className={
              'cp-program-slide-body-scroll' +
              (slideWebcamDesired ? ' cp-program-slide-body-scroll--with-webcam-sandwich' : '') +
              (slideCanvasFitWidth && slideCanvasOverflowsX ? ' is-fit-width' : '')
            }
            style={
              slideCanvasFitWidth && slideCanvasOverflowsX
                ? ({ ['--cp-slide-fit-scale']: String(slideCanvasFitScale) } as React.CSSProperties)
                : undefined
            }
          >
            <div className="cp-program-slide-body-stage">
              {isCommunicationView && !isRegistryMode ? (
                <div
                  className="cp-program-slide-communication-host"
                  data-tutorial-slide="question-panel-host"
                >
                  {communicationLoadError ? (
                    <p className="cp-program-slide-load-error" role="alert">
                      {communicationLoadError}
                    </p>
                  ) : null}
                  <ProgramSlideCommunicationPanel
                    mode="teacher"
                    promptText={communicationPrompt}
                    answerText={communicationAnswer}
                    question={communicationQuestion}
                    onQuestionChange={(q) => {
                      setCommunicationQuestion(q);
                      if (!editMode) setEditMode(true);
                      setCommunicationLocalDrafts((prev) => {
                        const base = prev[communicationQuestionIndex] ?? {
                          promptText: communicationPrompt,
                          answerText: communicationAnswer,
                          question: communicationQuestion,
                          multiResponse: communicationMultiResponseDraft,
                        };
                        return {
                          ...prev,
                          [communicationQuestionIndex]: { ...base, question: q },
                        };
                      });
                    }}
                    onPromptChange={(v) => {
                      setCommunicationPrompt(v);
                      if (!editMode) setEditMode(true);
                      setCommunicationLocalDrafts((prev) => {
                        const base = prev[communicationQuestionIndex] ?? {
                          promptText: communicationPrompt,
                          answerText: communicationAnswer,
                          question: communicationQuestion,
                          multiResponse: communicationMultiResponseDraft,
                        };
                        return {
                          ...prev,
                          [communicationQuestionIndex]: { ...base, promptText: v },
                        };
                      });
                    }}
                    onAnswerChange={(v) => {
                      setCommunicationAnswer(v);
                      if (!editMode) setEditMode(true);
                      setCommunicationLocalDrafts((prev) => {
                        const base = prev[communicationQuestionIndex] ?? {
                          promptText: communicationPrompt,
                          answerText: communicationAnswer,
                          question: communicationQuestion,
                          multiResponse: communicationMultiResponseDraft,
                        };
                        return {
                          ...prev,
                          [communicationQuestionIndex]: { ...base, answerText: v },
                        };
                      });
                    }}
                    savingPrompt={communicationSaving}
                    questionSaveError={communicationSaveError}
                    onClearQuestionSaveError={() => setCommunicationSaveError(null)}
                    members={communicationMembers}
                    responses={displayedCommunicationResponses}
                    groups={communicationGroups}
                    selectedGroupId={communicationSelectedGroupId}
                    onSelectGroup={handleSelectCommunicationGroup}
                    groupMemberCounts={communicationGroupMemberCounts}
                    onOpenMonitor={handleOpenCommunicationMonitor}
                    onOpenConnect={isRegistryMode ? undefined : () => void handleOpenQuestionQr()}
                    connectLoading={questionQrLoading}
                    playControl={{
                      isOpen: communicationIsOpen,
                      multiResponse: communicationMultiResponseDraft,
                      canPlay: canPlayCommunicationQuestion,
                      disabledReason: hasUnsavedCommunicationDraft
                        ? '저장하지 않은 변경사항이 있습니다. 먼저 저장한 뒤 재생해 주세요.'
                        : !communicationQuestions[communicationQuestionIndex]
                          ? '재생할 질문이 없습니다. 먼저 질문을 저장해 주세요.'
                          : undefined,
                      onToggleMultiResponse: handleToggleCommunicationMultiResponse,
                      onPlay: () => void handlePlayCommunicationQuestion(),
                      onStop: () => void handleStopCommunicationQuestion(),
                    }}
                    onExportCsv={handleExportCommunicationCsv}
                    onExportPdf={handleExportCommunicationPdf}
                    exportingCsv={exportingCommunicationCsv}
                    exportingPdf={exportingCommunicationPdf}
                    questionNav={{
                      canPrev: communicationQuestionIndex > 0,
                      canNext: Boolean(
                        (communicationPrompt || '').trim() ||
                          (communicationAnswer || '').trim() ||
                          communicationQuestion
                      ),
                      canDelete:
                        communicationQuestionIndex < communicationQuestions.length &&
                        Boolean(communicationQuestions[communicationQuestionIndex]),
                      currentIndex: communicationQuestionIndex + 1,
                      totalCount: Math.max(
                        communicationQuestions.length,
                        communicationQuestionIndex + 1,
                        1
                      ),
                      onPrev: handleCommunicationQuestionPrev,
                      onNext: handleCommunicationQuestionNext,
                      onDeleteRequest: () => setCommunicationDeleteConfirmOpen(true),
                    }}
                    deleteQuestionConfirmOpen={communicationDeleteConfirmOpen}
                    onCancelDeleteQuestion={() => setCommunicationDeleteConfirmOpen(false)}
                    onConfirmDeleteQuestion={() => void handleConfirmDeleteCommunicationQuestion()}
                    deletingQuestion={communicationDeleting}
                  />
                </div>
              ) : openedRegisteredPage && !isRegistryMode ? (
                slideWebcamDesired ? (
                  <ProgramSlideWebcamDrawRoot
                    strokes={webcamDrawStrokes}
                    onStrokesChange={setWebcamDrawStrokesForPage}
                    onDownloadSnapshot={handleWebcamDrawDownload}
                    webcamView={webcamViewControls}
                  >
                    <div className="cp-program-slide-webcam-canvas-sandwich cp-program-slide-webcam-canvas-sandwich--registered-preview">
                      <div className="cp-program-slide-webcam-sandwich-under" aria-hidden>
                        <ProgramSlideCanvas
                          key={`registered-preview-under-${openedRegisteredPage.id}`}
                          layout={openedRegisteredLayout ?? EMPTY_SLIDE_LAYOUT}
                          readOnly
                          viewMode
                          defaultTextColorHex={displayPageTextColorHex}
                          canvasBackgroundColorHex={displayPageBackgroundColorHex}
                          allLayoutsForWebpageThumbCache={layouts}
                          onLayoutChange={() => {}}
                        />
                      </div>
                      {renderSlideWebcamBackgroundLayer(
                        ' cp-program-slide-webcam-layer--in-sandwich cp-program-slide-webcam-layer--registered-preview-top',
                        { clickToFreeze: false }
                      )}
                      {showWebcamDraw ? <ProgramSlideWebcamDrawCanvas /> : null}
                      {showWebcamDraw ? <ProgramSlideWebcamDrawToolbar /> : null}
                    </div>
                  </ProgramSlideWebcamDrawRoot>
                ) : (
                  <ProgramSlideCanvas
                    key={`registered-preview-${openedRegisteredPage.id}`}
                    layout={openedRegisteredLayout ?? EMPTY_SLIDE_LAYOUT}
                    readOnly
                    viewMode
                    defaultTextColorHex={displayPageTextColorHex}
                    canvasBackgroundColorHex={displayPageBackgroundColorHex}
                    allLayoutsForWebpageThumbCache={layouts}
                    onLayoutChange={() => {}}
                  />
                )
              ) : isFirst ? (
                slideWebcamDesired ? (
                  <>
                    <ProgramSlideWebcamDrawRoot
                      strokes={webcamDrawStrokes}
                      onStrokesChange={setWebcamDrawStrokesForPage}
                      onDownloadSnapshot={handleWebcamDrawDownload}
                      webcamView={webcamViewControls}
                    >
                      <div className="cp-program-slide-webcam-canvas-sandwich">
                        <div
                          className="cp-program-slide-webcam-sandwich-under cp-program-slide-webcam-title-under"
                          aria-hidden
                        >
                          <ProgramSlideTitlePanel
                            {...titlePanelProps}
                            titleSessionEditable={false}
                            styleStorageKey={k.trim() || undefined}
                          />
                        </div>
                        {renderSlideWebcamBackgroundLayer(' cp-program-slide-webcam-layer--in-sandwich')}
                        {showWebcamDraw ? <ProgramSlideWebcamDrawCanvas /> : null}
                        <div className="cp-program-slide-webcam-sandwich-over">
                          <ProgramSlideCanvas
                            ref={slideCanvasHandleRef}
                            key="title-webcam-overlay"
                            layout={layoutWebcamOver}
                            readOnly={canvasReadOnly}
                            viewMode={!editMode}
                            webcamHitThrough
                            allLayoutsForWebpageThumbCache={layouts}
                            onBlockClipboardCopy={markSlideBlockClipboardActive}
                            defaultTextColorHex={resolvedPageTextColorHex}
                            canvasBackgroundColorHex={
                              appliedBackgroundLayoutForCanvas ? undefined : resolvedPageBackgroundColorHex
                            }
                            onLayoutChange={(next) => handleWebcamOverlayLayoutChange(pageIndexForCanvas, next)}
                          />
                        </div>
                        {showWebcamDraw ? <ProgramSlideWebcamDrawToolbar /> : null}
                      </div>
                    </ProgramSlideWebcamDrawRoot>
                  </>
                ) : (
                  <ProgramSlideTitlePanel {...titlePanelProps} styleStorageKey={k.trim() || undefined} />
                )
              ) : slideWebcamDesired ? (
                <>
                  <ProgramSlideWebcamDrawRoot
                    strokes={webcamDrawStrokes}
                    onStrokesChange={setWebcamDrawStrokesForPage}
                    onDownloadSnapshot={handleWebcamDrawDownload}
                    webcamView={webcamViewControls}
                  >
                    <div className="cp-program-slide-webcam-canvas-sandwich">
                      <div className="cp-program-slide-webcam-sandwich-under" aria-hidden>
                        {appliedBackgroundLayoutForCanvas ? (
                          <div className={layoutSandwichUnderClassName + ' cp-program-slide-layout-sandwich-under--in-webcam'}>
                            <ProgramSlideCanvas
                              key={`webcam-layout-bg-${displayAppliedLayoutIdForCanvas || 'none'}-${pageIndexForCanvas}`}
                              layout={appliedBackgroundLayoutForCanvas}
                              layoutLayerRole="background"
                              readOnly
                              viewMode
                              canvasBackgroundColorHex={appliedLayoutCanvasBackgroundColorHex}
                              onLayoutChange={() => {}}
                            />
                          </div>
                        ) : null}
                        <ProgramSlideCanvas
                          key={`webcam-under-${pageIndexForCanvas}`}
                          layout={layoutWebcamUnder}
                          readOnly
                          viewMode
                          canvasBackgroundColorHex={resolvedPageBackgroundColorHex}
                          onLayoutChange={() => {}}
                        />
                      </div>
                      {renderSlideWebcamBackgroundLayer(' cp-program-slide-webcam-layer--in-sandwich')}
                      {showWebcamDraw ? <ProgramSlideWebcamDrawCanvas /> : null}
                      <div className="cp-program-slide-webcam-sandwich-over">
                        <ProgramSlideCanvas
                          ref={slideCanvasHandleRef}
                          key={pageIndexForCanvas}
                          layout={layoutWebcamOver}
                          layoutLayerRole={appliedBackgroundLayoutForCanvas ? 'foreground' : undefined}
                          readOnly={canvasReadOnly}
                          viewMode={!editMode}
                          webcamHitThrough
                          allLayoutsForWebpageThumbCache={layouts}
                          onBlockClipboardCopy={markSlideBlockClipboardActive}
                          defaultTextColorHex={resolvedPageTextColorHex}
                          canvasBackgroundColorHex={
                            appliedBackgroundLayoutForCanvas ? undefined : resolvedPageBackgroundColorHex
                          }
                          onLayoutChange={(next) => handleWebcamOverlayLayoutChange(pageIndexForCanvas, next)}
                        />
                      </div>
                      {showWebcamDraw ? <ProgramSlideWebcamDrawToolbar /> : null}
                    </div>
                  </ProgramSlideWebcamDrawRoot>
                </>
              ) : (
                <>
                  {appliedBackgroundLayoutForCanvas ? (
                    <div className="cp-program-slide-layout-sandwich">
                      <div className={layoutSandwichUnderClassName} aria-hidden>
                        <ProgramSlideCanvas
                          key={`layout-bg-${displayAppliedLayoutIdForCanvas || 'none'}-${pageIndexForCanvas}`}
                          layout={appliedBackgroundLayoutForCanvas}
                          layoutLayerRole="background"
                          readOnly
                          viewMode
                          canvasBackgroundColorHex={appliedLayoutCanvasBackgroundColorHex}
                          onLayoutChange={() => {}}
                        />
                      </div>
                      <div className="cp-program-slide-layout-sandwich-over">
                        <ProgramSlideCanvas
                          ref={slideCanvasHandleRef}
                          key={pageIndexForCanvas}
                          layout={layoutForCanvas}
                          layoutLayerRole="foreground"
                          readOnly={canvasReadOnly}
                          viewMode={!editMode}
                          allLayoutsForWebpageThumbCache={layouts}
                          onBlockClipboardCopy={markSlideBlockClipboardActive}
                          defaultTextColorHex={resolvedPageTextColorHex}
                          onLayoutChange={(next) => handleCanvasLayoutChange(pageIndexForCanvas, next)}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <ProgramSlideCanvas
                        ref={slideCanvasHandleRef}
                        key={pageIndexForCanvas}
                        layout={layoutForCanvas}
                        readOnly={canvasReadOnly}
                        viewMode={!editMode}
                        allLayoutsForWebpageThumbCache={layouts}
                        onBlockClipboardCopy={markSlideBlockClipboardActive}
                        defaultTextColorHex={resolvedPageTextColorHex}
                        canvasBackgroundColorHex={resolvedPageBackgroundColorHex}
                        onLayoutChange={(next) => handleCanvasLayoutChange(pageIndexForCanvas, next)}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="cp-program-slide-footer-nav">
          {layoutPickerPageIndex != null && editMode ? (
            <div
              ref={layoutPickerRef}
              className={
                'cp-program-slide-layout-picker cp-program-slide-layout-picker--mini' +
                (isRegistryMode ? ' cp-program-slide-layout-picker--bg-only' : '')
              }
              role="dialog"
              aria-label={isRegistryMode ? '페이지 설정' : '슬라이드 페이지 설정'}
            >
              <div className="cp-program-slide-layout-picker-head">
                <strong>페이지 설정</strong>
                <button
                  type="button"
                  className="cp-program-slide-layout-picker-close"
                  onClick={closeLayoutPicker}
                  aria-label="닫기"
                  title="닫기"
                >
                  <svg
                    className="cp-program-slide-layout-picker-close-icon"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    aria-hidden
                  >
                    <path
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      d="M6 6l12 12M18 6L6 18"
                    />
                  </svg>
                </button>
              </div>
              <div className="cp-program-slide-layout-picker-sections">
                <section
                  className={
                    'cp-program-slide-layout-picker-section cp-program-slide-layout-picker-section--text' +
                    (layoutPickerPageHasAppliedLayout ? ' is-layout-color-locked' : '')
                  }
                >
                  <h4 className="cp-program-slide-layout-picker-section-title">기본 텍스트색</h4>
                  <div className="cp-program-slide-layout-picker-color-controls">
                    <button
                      type="button"
                      className="cp-program-slide-layout-picker-color-reset"
                      onClick={() => applyPageTextColor(layoutPickerPageIndex, null)}
                      title="기본 텍스트색 사용"
                      aria-label="기본 텍스트색 사용"
                      disabled={layoutPickerPageHasAppliedLayout}
                    >
                      <span className="cp-program-slide-layout-picker-color-reset-mark" aria-hidden />
                    </button>
                    <input
                      type="color"
                      className="cp-program-slide-layout-picker-color-input"
                      value={normalizeColorInputHex(
                        layouts[layoutPickerPageIndex]?.pageTextColorHex,
                        normalizeColorInputHex(
                          layouts[layoutPickerPageIndex]?.defaultTextColorHex,
                          slideGlobalTextColorHex
                        )
                      )}
                      onChange={(e) => applyPageTextColor(layoutPickerPageIndex, e.target.value)}
                      aria-label="기본 텍스트색"
                      disabled={layoutPickerPageHasAppliedLayout}
                    />
                  </div>
                </section>
                <section
                  className={
                    'cp-program-slide-layout-picker-section cp-program-slide-layout-picker-section--bg' +
                    (layoutPickerPageHasAppliedLayout ? ' is-layout-color-locked' : '')
                  }
                >
                  <h4 className="cp-program-slide-layout-picker-section-title">배경색</h4>
                  <div className="cp-program-slide-layout-picker-color-controls">
                    <button
                      type="button"
                      className="cp-program-slide-layout-picker-color-reset"
                      onClick={() => applyPageBackgroundColor(layoutPickerPageIndex, null)}
                      title="기본 배경색 사용"
                      aria-label="기본 배경색 사용"
                      disabled={layoutPickerPageHasAppliedLayout}
                    >
                      <span className="cp-program-slide-layout-picker-color-reset-mark" aria-hidden />
                    </button>
                    <input
                      type="color"
                      className="cp-program-slide-layout-picker-color-input"
                      value={normalizeColorInputHex(
                        layouts[layoutPickerPageIndex]?.pageBackgroundColorHex,
                        normalizeColorInputHex(
                          layouts[layoutPickerPageIndex]?.defaultBackgroundColorHex,
                          slideGlobalBackgroundColorHex
                        )
                      )}
                      onChange={(e) => applyPageBackgroundColor(layoutPickerPageIndex, e.target.value)}
                      aria-label="배경색"
                      disabled={layoutPickerPageHasAppliedLayout}
                    />
                  </div>
                </section>
                {!isRegistryMode ? (
                <section className="cp-program-slide-layout-picker-section cp-program-slide-layout-picker-section--layout">
                  <h4 className="cp-program-slide-layout-picker-section-title">레이아웃 선택</h4>
                  <div
                    className="cp-program-slide-layout-picker-list"
                    onMouseLeave={() => setLayoutPickerHoverId(undefined)}
                  >
                    <button
                      type="button"
                      className={
                        'cp-program-slide-layout-picker-color-reset' +
                        (((layouts[layoutPickerPageIndex]?.appliedLayoutTemplateId ?? '').trim() ? '' : ' is-active'))
                      }
                      onMouseEnter={() => setLayoutPickerHoverId(null)}
                      onFocus={() => setLayoutPickerHoverId(null)}
                      onClick={() => applyLayoutTemplateToPage(layoutPickerPageIndex, null)}
                      title="레이아웃 해제"
                      aria-label="레이아웃 해제"
                    >
                      <span className="cp-program-slide-layout-picker-color-reset-mark" aria-hidden />
                    </button>
                    {registeredLayouts.map((item, idx) => (
                      <button
                        key={item.id}
                        type="button"
                        className={
                          'cp-program-slide-layout-picker-item cp-program-slide-layout-picker-item--name' +
                          (((layouts[layoutPickerPageIndex]?.appliedLayoutTemplateId ?? '').trim() === item.id ? ' is-active' : ''))
                        }
                        onMouseEnter={() => setLayoutPickerHoverId(item.id)}
                        onFocus={() => setLayoutPickerHoverId(item.id)}
                        onClick={() => applyLayoutTemplateToPage(layoutPickerPageIndex, item.id)}
                        title={item.name}
                      >
                        {item.name || `레이아웃${idx + 1}`}
                      </button>
                    ))}
                    {registeredLayouts.length === 0 ? (
                      <p className="cp-program-slide-layout-picker-empty">
                        등록된 레이아웃이 없습니다.
                      </p>
                    ) : null}
                  </div>
                </section>
                ) : null}
              </div>
            </div>
          ) : null}
          <button
            type="button"
            className="cp-program-slide-nav-btn"
            onClick={goPrev}
            disabled={!isCommunicationView && slideIndex <= 0}
            aria-label="이전 슬라이드"
          >
            &lt;
          </button>
          <div className="cp-program-slide-footer-nav-center">
          {dots}
          <div className="cp-program-slide-page-actions" role="group" aria-label="슬라이드 추가 및 삭제">
            {isRegistryMode ? (
              <div ref={registryNameModalRef} className="cp-program-slide-page-name-wrap">
                {registryNameModal ? (
                  <div
                    className="cp-program-slide-layout-picker cp-program-slide-layout-picker--mini cp-program-slide-layout-picker--name-input"
                    role="dialog"
                    aria-label={`${registryNoun} 이름`}
                  >
                    <div className="cp-program-slide-layout-picker-head">
                      <strong>{registryNoun} 이름</strong>
                      <button
                        type="button"
                        className="cp-program-slide-layout-picker-close"
                        onClick={closeRegistryNameModal}
                        aria-label="닫기"
                        title="닫기"
                      >
                        <svg
                          className="cp-program-slide-layout-picker-close-icon"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                          aria-hidden
                        >
                          <path
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            d="M6 6l12 12M18 6L6 18"
                          />
                        </svg>
                      </button>
                    </div>
                    <div className="cp-program-slide-layout-picker-name-body">
                      <input
                        ref={registryNameInputRef}
                        type="text"
                        className="cp-program-slide-layout-picker-name-input"
                        value={registryNameModal.draft}
                        maxLength={PROGRAM_SLIDE_REGISTRY_NAME_MAX_LENGTH}
                        onChange={(e) =>
                          setRegistryNameModal((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  draft: limitProgramSlideRegistryNameDraft(e.target.value),
                                }
                              : prev
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            confirmRegistryNameModal();
                          }
                        }}
                        aria-label={`${registryNoun} 이름`}
                      />
                      <div className="cp-program-slide-layout-picker-name-actions">
                        <button
                          type="button"
                          className="cp-program-slide-layout-picker-name-btn"
                          onClick={closeRegistryNameModal}
                        >
                          취소
                        </button>
                        <button
                          type="button"
                          className="cp-program-slide-layout-picker-name-btn cp-program-slide-layout-picker-name-btn--primary"
                          onClick={confirmRegistryNameModal}
                        >
                          확인
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
                <button
                  type="button"
                  className="cp-program-slide-page-add"
                  onClick={() => openRegistryNameModal(selectedEditablePageIndex ?? 0)}
                  disabled={selectedEditablePageIndex == null}
                  aria-label={`현재 ${registryNoun} 이름 변경`}
                  title={`현재 ${registryNoun} 이름 변경`}
                >
                  이름
                </button>
              </div>
            ) : null}
            <button
              type="button"
              className="cp-program-slide-page-add"
              data-tutorial-slide="add-slide"
              onClick={addSlide}
              disabled={!canAddSlide}
              aria-label="슬라이드 추가"
              title={
                isRegistryMode && extraPageCount >= registryMaxCount
                  ? `${registryNoun}은(는) 최대 ${registryMaxCount}개까지 만들 수 있습니다.`
                  : '슬라이드 추가'
              }
            >
              <span
                className="cp-program-slide-page-add-icon"
                aria-hidden
                style={{ WebkitMaskImage: 'url(/Plus.svg)', maskImage: 'url(/Plus.svg)' }}
              />
            </button>
            <DeleteHoldButton
              className="cp-program-slide-page-delete"
              holdMs={1000}
              disabled={!canDeleteSelectedSlide}
              disabledTitle={
                !editMode ? '편집모드에서만 삭제할 수 있습니다.' : '삭제할 슬라이드를 먼저 선택해 주세요.'
              }
              disabledAriaLabel="슬라이드 삭제 (사용 불가)"
              title="슬라이드 삭제 (짧게: 확인 후 삭제 · 길게: 바로 삭제)"
              ariaLabel="슬라이드 삭제"
              confirmTitle="슬라이드를 삭제할까요?"
              confirmDescription="선택한 슬라이드가 삭제됩니다."
              onDelete={deleteSelectedSlide}
            >
              <span
                className="cp-program-slide-page-delete-icon"
                aria-hidden
                style={{ WebkitMaskImage: 'url(/Minus.svg)', maskImage: 'url(/Minus.svg)' }}
              />
            </DeleteHoldButton>
            <div ref={slideSettingsModalRef} className="cp-program-slide-page-settings-wrap">
              {slideSettingsModalOpen ? (
                <div
                  className="cp-program-slide-layout-picker cp-program-slide-layout-picker--mini cp-program-slide-layout-picker--settings cp-program-slide-layout-picker--bg-only"
                  role="dialog"
                  aria-label="슬라이드 기본 설정"
                >
                  <div className="cp-program-slide-layout-picker-head">
                    <strong>슬라이드 기본 설정</strong>
                    <button
                      type="button"
                      className="cp-program-slide-layout-picker-close"
                      onClick={closeSlideSettingsModal}
                      aria-label="닫기"
                      title="닫기"
                    >
                      <svg
                        className="cp-program-slide-layout-picker-close-icon"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        aria-hidden
                      >
                        <path
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          d="M6 6l12 12M18 6L6 18"
                        />
                      </svg>
                    </button>
                  </div>
                  <div className="cp-program-slide-layout-picker-sections">
                    <section className="cp-program-slide-layout-picker-section cp-program-slide-layout-picker-section--text">
                      <h4 className="cp-program-slide-layout-picker-section-title">기본 텍스트색</h4>
                      <div className="cp-program-slide-layout-picker-color-controls">
                        <input
                          type="color"
                          className="cp-program-slide-layout-picker-color-input"
                          value={effectiveGlobalTextColorHex}
                          onChange={(e) => applySlideGlobalDefaults(e.target.value, effectiveGlobalBackgroundColorHex)}
                          aria-label="기본 텍스트색"
                        />
                      </div>
                    </section>
                    <section className="cp-program-slide-layout-picker-section cp-program-slide-layout-picker-section--bg">
                      <h4 className="cp-program-slide-layout-picker-section-title">배경색</h4>
                      <div className="cp-program-slide-layout-picker-color-controls">
                        <input
                          type="color"
                          className="cp-program-slide-layout-picker-color-input"
                          value={effectiveGlobalBackgroundColorHex}
                          onChange={(e) => applySlideGlobalDefaults(effectiveGlobalTextColorHex, e.target.value)}
                          aria-label="배경색"
                        />
                      </div>
                    </section>
                  </div>
                </div>
              ) : null}
              <button
                type="button"
                className="cp-program-slide-page-settings-btn"
                onClick={() => {
                  closeLayoutPicker();
                  closeRegistryNameModal();
                  setSlideSettingsModalOpen((open) => !open);
                }}
                disabled={!editMode}
                aria-label="슬라이드 기본 설정"
                title="슬라이드 기본 설정"
              >
                <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden focusable="false">
                  <path
                    fill="currentColor"
                    d="M19.5 13.5a7.4 7.4 0 0 0 .05-1.5 7.4 7.4 0 0 0-.05-1.5l2.04-1.6a.54.54 0 0 0 .13-.7l-1.93-3.34a.56.56 0 0 0-.67-.24l-2.4.96a8.3 8.3 0 0 0-2.6-1.5l-.37-2.54a.56.56 0 0 0-.55-.46h-3.86a.56.56 0 0 0-.55.46l-.37 2.54a8.3 8.3 0 0 0-2.6 1.5l-2.4-.96a.56.56 0 0 0-.67.24L2.28 8.2a.54.54 0 0 0 .13.7l2.04 1.6a7.4 7.4 0 0 0-.05 1.5c0 .5.02 1 .05 1.5l-2.04 1.6a.54.54 0 0 0-.13.7l1.93 3.34c.14.25.44.35.67.24l2.4-.96a8.3 8.3 0 0 0 2.6 1.5l.37 2.54c.05.26.28.46.55.46h3.86c.27 0 .5-.2.55-.46l.37-2.54a8.3 8.3 0 0 0 2.6-1.5l2.4.96c.23.11.53.01.67-.24l1.93-3.34a.54.54 0 0 0-.13-.7l-2.04-1.6ZM12 15.5A3.5 3.5 0 1 1 12 8a3.5 3.5 0 0 1 0 7.5Z"
                  />
                </svg>
              </button>
            </div>
          </div>
          </div>
          <button
            type="button"
            className="cp-program-slide-nav-btn"
            onClick={goNext}
            disabled={isCommunicationView}
            aria-label="다음 슬라이드"
          >
            &gt;
          </button>
        </div>
      </div>
      {showBoardDrawOverlay ? (
        <div
          className="cp-program-slide-board-draw-overlay"
          role="region"
          aria-label="칠판 판서"
        >
          <ProgramSlideWebcamDrawRoot
            strokes={boardDrawStrokes}
            onStrokesChange={setBoardDrawStrokesForPage}
          >
            <ProgramSlideWebcamDrawCanvas />
            <ProgramSlideWebcamDrawToolbar />
          </ProgramSlideWebcamDrawRoot>
        </div>
      ) : null}
      {slideCanvasOverflowsX ? (
        <button
          type="button"
          className="cp-program-slide-fit-fab"
          onClick={() => setSlideCanvasFitWidth((v) => !v)}
          aria-label={slideCanvasFitWidth ? '캔버스 원래 크기로' : '캔버스를 화면에 맞춤'}
          title={slideCanvasFitWidth ? '확대 (가로 스크롤)' : '축소 (화면 맞춤)'}
        >
          {slideCanvasFitWidth ? '+' : '−'}
        </button>
      ) : null}
      {(editMode || hasUnsavedCommunicationDraft) && canPersistSlides ? (
        <>
          {slideSaveMessage ? (
            <div className="cp-program-slide-floating-save-message" role="status">
              {slideSaveMessage}
            </div>
          ) : null}
          <button
            type="button"
            className="cp-fab-save cp-program-slide-app-chrome"
            data-tutorial-slide="save"
            onClick={() => void handleFloatingSave()}
            disabled={savingSlides || communicationSaving || !hasUnsavedAnything}
            aria-label="저장하기"
            title={
              hasUnsavedAnything
                ? hasUnsavedCommunicationDraft
                  ? '질문·슬라이드 저장하기'
                  : '저장하기'
                : '저장할 변경사항 없음'
            }
          >
            <img src="/Save.svg" alt="" className="cp-fab-save-icon" />
          </button>
        </>
      ) : null}
      {publicQrOverlay?.mode === 'comm' && lessonComm.room && lessonComm.studentLink ? (
        <ProgramContentSlidePublicQrOverlay
          publicUrl={lessonComm.studentLink}
          expiresAt={new Date(lessonComm.room.expiresAt).toISOString()}
          title="개별 디바이스 프레젠테이션"
          simple
          hint={
            lessonComm.rosterMode
              ? 'QR을 찍고 번호 + 입장번호(6자리)로 들어와요'
              : `QR을 찍고 입장 코드 ${lessonComm.room.entryCode}(으)로 들어와요`
          }
          chips={
            <span className="cp-program-slide-public-qr-chip">
              {lessonComm.connected ? `접속 ${lessonComm.onlineCount}명` : '연결 중…'}
            </span>
          }
          onClose={() => setPublicQrOverlay(null)}
        />
      ) : null}
      {publicQrOverlay && publicQrOverlay.mode !== 'comm' ? (
        <ProgramContentSlidePublicQrOverlay
          publicUrl={publicQrOverlay.publicUrl}
          expiresAt={publicQrOverlay.expiresAt}
          title={publicQrOverlay.mode === 'question' ? '질문 QR' : '공개보기 QR'}
          hint={
            publicQrOverlay.mode === 'question'
              ? '휴대폰으로 스캔하면 질문 페이지로 바로 이동합니다.'
              : '휴대폰으로 스캔하면 보기 모드로 참여할 수 있습니다.'
          }
          onClose={() => setPublicQrOverlay(null)}
        />
      ) : null}
      {publicQrError ? (
        <div className="cp-program-slide-floating-save-message" role="alert">
          {publicQrError}
        </div>
      ) : null}
      {slideWebcamError ? (
        <div className="cp-program-slide-floating-save-message" role="alert">
          {slideWebcamError}
        </div>
      ) : null}
      <ProgramSlideTimerModal
        open={slideTimerModalOpen}
        draftSeconds={slideTimerDraftSec}
        onClose={() => setSlideTimerModalOpen(false)}
        onAddSeconds={addSlideTimerDraftSeconds}
        onReset={resetSlideTimerDraft}
        onStart={startSlideTimerFromModal}
      />
      <ProgramSlideBgmModal open={slideBgmModalOpen} onClose={() => setSlideBgmModalOpen(false)} onPick={handleSlideBgmPick} />
      <ProgramSlideDiceModal
        open={slideDiceModalOpen}
        onClose={() => setSlideDiceModalOpen(false)}
        memberLookup={slideDiceMemberLookup}
      />
      {presentationToastState ? (
        <AutoStatusToast
          state={presentationToastState}
          className="cp-toast-auto-center cp-toast-auto-over-modal"
        />
      ) : null}
    </div>
    {isLocalOnlySlideSession && !localOnlyHintDismissed && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="cp-program-slide-owner-hint cp-program-slide-owner-hint--floating"
            data-tutorial-slide="local-only-hint"
            role="status"
          >
            <span className="cp-program-slide-owner-hint-text">
              연결된 학습내용 데이터가 없어 현재 브라우저에서만 사용할 수 있어요. 편집은 가능하지만 저장되지
              않습니다.
            </span>
            <button
              type="button"
              className="cp-program-slide-owner-hint-close"
              data-tutorial-slide="local-only-hint-close"
              aria-label="안내 닫기"
              title="닫기"
              onClick={() => setLocalOnlyHintDismissed(true)}
            >
              <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden>
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  d="M6 6l12 12M18 6L6 18"
                />
              </svg>
            </button>
          </div>,
          document.body
        )
      : null}
    {remoteOverlays}
    {lessonComm.question &&
    !lessonCommPanelHidden &&
    !isRegistryMode &&
    (!lessonCommGridVisible || lessonCommUnmatchedAnswers.length > 0) ? (
      <LessonCommResponsesPanel
        questionText={lessonComm.question.text}
        rows={(lessonCommGridVisible ? lessonCommUnmatchedAnswers : lessonComm.answers).map((a) => ({
          memberId: a.memberId,
          label: a.label,
          content: a.content,
          at: a.at,
        }))}
        joinedCount={lessonComm.members.length}
        connected={lessonComm.connected}
        note={
          lessonCommGridVisible
            ? '명단 번호가 없는 학생 응답이에요 (학생 블럭에 표시되지 않아요)'
            : lessonCommLinkRef.current.has(lessonComm.question.id)
            ? '문제를 닫으면 응답이 질문 페이지에 기록돼요'
            : canSharePublicForQr
              ? undefined
              : '저장하면 응답이 기록돼요'
        }
        onCloseQuestion={() => {
          lessonComm.closeQuestion();
          setLessonCommPanelHidden(true);
        }}
      />
    ) : null}
    <TutorialOverlayHost />
    {tutorialWorkspaceId ? (
      <ClassLessonAlarmPopupRelay workspaceId={tutorialWorkspaceId} />
    ) : null}
    </TutorialProvider>
  );
};

export default ProgramContentSlidePage;
